import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate, Link, useSearchParams } from 'react-router-dom';
import { quotesAPI, pricingAPI, customersAPI, usersAPI, tenantsAPI, adminAPI } from '../../services/api';
import apiClient from '../../services/api';
import { useAuth } from '../../auth/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Separator } from '../../components/ui/separator';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { Skeleton } from '../../components/ui/skeleton';
import { Textarea } from '../../components/ui/textarea';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../../components/ui/sheet';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '../../components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import {
  ArrowLeft, ArrowRight, Save, Copy, Loader2, AlertCircle, Plus, Minus,
  RefreshCw, Trash2, CheckCircle2, FileDown, Send, Sparkles, Wand2,
  ClipboardCopy, Lightbulb, Server, Database, Package, User, FileText, Eye,
  Search, ChevronDown, Building2, Users, Check, ChevronsUpDown, X as XIcon, AlertTriangle, Info, Tag, FileSignature
} from 'lucide-react';
import { toast } from 'sonner';
import { CountrySelect } from '../../components/CountrySelect';

const API_BASE = process.env.REACT_APP_BACKEND_URL + '/api';

// Safely extract a human-readable error message from any API error response.
// Handles: plain strings, Pydantic v1 {detail: string}, Pydantic v2 {detail: [{msg, loc, ...}]}.
const extractErrorMessage = (err, fallback = 'An error occurred') => {
  const detail = err?.response?.data?.detail;
  if (!detail) return err?.message || fallback;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail) && detail.length > 0) {
    return detail.map(e => (typeof e === 'object' ? e.msg || e.type || 'Validation error' : String(e))).join('; ');
  }
  return fallback;
};

const CURRENCIES = [
  { code: 'GBP', symbol: '\u00a3' }, { code: 'EUR', symbol: '\u20ac' },
  { code: 'USD', symbol: '$' }, { code: 'CAD', symbol: 'CA$' },
  { code: 'AUD', symbol: 'A$' },
];
const TERM_OPTIONS = [1, 3, 6, 12, 24, 36];
const sym = (code) => CURRENCIES.find(c => c.code === code)?.symbol || code;
const fmtMoney = (n, code) => `${sym(code)}${Number(n || 0).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const STEPS = [
  { id: 1, label: 'Select',    icon: Search },
  { id: 2, label: 'Setup',     icon: User },
  { id: 3, label: 'Product',   icon: Package },
  { id: 4, label: 'Database',  icon: Database },
  { id: 5, label: 'Servers',   icon: Server },
  { id: 6, label: 'Extras',    icon: Plus },
  { id: 7, label: 'Review',    icon: FileText },
  { id: 8, label: 'Pricing',   icon: Tag },
];

const INITIAL_WIZARD = {
  // Step 1 - Partner & Tenant selection
  customer_id: '', contact_ids: [],
  tenant_id: '', tenant_name: '',
  billing_model: 'PARTNER',
  end_customer_name: '',
  // Step 2 - Quote Setup
  region_id: '', region_name: '', region_aws_code: '',
  psm_user_id: '', psm_user_name: '',
  expiry_date: '', required_by_date: '', use_next_available_slot: false, billing_frequency: 'Monthly', payment_method: '', quote_name: '', notes: '',
  deal_ref: '', deal_probability: 100,
  partner_margin_pupm: '',
  partner_margin_percent: '',
  // Steps 3-7
  product_code: 'STANDARD', num_users: '', num_web_client_users: 0, num_mobile_users: 0, term_months: 12, currency: 'GBP',
  db_type: 'HANA', db_server_code: '', additional_data_gb: 0, additional_backup_gb: 0, additional_db_root_gb: 0,
  app_servers: [], extras: [],
  mtpro_tenants: [],
  mtpro_quote_type: 'new',
  // Standalone (Test System / Bespoke)
  is_standalone: false,                  // true when Standalone card is selected
  standalone_subtype: 'BESPOKE',         // 'TEST_SYSTEM' | 'BESPOKE' — default is Custom (no Test System)
  standalone_ccc_required: false,        // true → product_code=MTPRO; false → product_code=ADVANCED
  service_unit_ref: '',                  // optional, e.g. SU0200
  extra_db_servers: [],                  // C4P Custom: [{id, db_type, db_server_code, additional_data_gb, additional_backup_gb, additional_db_root_gb}]
  file_storage_gb: 0,                    // total file storage GB (100 included Adv/MTPro, 10 Standard, 0 Custom)
};

// ── Helpers ─────────────────────────────────────────────────────────────
const NAVY  = 'hsl(var(--c4p-navy-900))';
const ORANGE = 'hsl(var(--c4p-orange-600))';

function SectionHeading({ children }) {
  return <h3 className="text-sm font-bold mb-4" style={{ fontFamily: 'var(--font-display)', color: NAVY }}>{children}</h3>;
}

function FieldRow({ label, children, hint }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-sm font-medium">{label}</Label>
      {children}
      {hint && <p className="text-xs" style={{ color: 'hsl(var(--muted-fg))' }}>{hint}</p>}
    </div>
  );
}

function StorageStepper({ label, value, onChange, min = 0, unit = 'GB', hint }) {
  const adj = (delta) => onChange(Math.max(min, value + delta));
  const pill = (label, delta) => {
    const disabled = value + delta < min;
    return (
      <button
        key={label}
        onClick={() => !disabled && adj(delta)}
        style={{
          height: '32px', padding: '0 9px', fontSize: '11px', fontWeight: 700,
          borderRadius: '6px', border: '1px solid hsl(var(--border))',
          background: disabled ? 'transparent' : 'white',
          color: disabled ? 'hsl(var(--muted-fg))' : NAVY,
          cursor: disabled ? 'not-allowed' : 'pointer',
          opacity: disabled ? 0.35 : 1,
          flexShrink: 0, letterSpacing: '0.01em',
        }}
      >{label}</button>
    );
  };
  return (
    <div style={{ background: 'hsl(var(--muted))', border: '1px solid hsl(var(--border))', borderRadius: '10px', padding: '10px 12px' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px' }}>
        <div style={{ minWidth: 0, flex: 1 }}>
          <p style={{ fontSize: '13px', fontWeight: 600, lineHeight: 1.3 }}>{label}</p>
          {hint && <p style={{ fontSize: '11px', marginTop: '2px', color: 'hsl(var(--muted-fg))' }}>{hint}</p>}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '5px', flexShrink: 0 }}>
          {pill('-100', -100)}
          {pill('-10', -10)}
          {/* Editable value input */}
          <div style={{ display: 'flex', alignItems: 'center', background: 'white', border: '1.5px solid hsl(var(--border))', borderRadius: '7px', height: '32px', padding: '0 8px', gap: '3px', minWidth: '92px' }}>
            <input
              type="number" min={min} value={value}
              onChange={e => onChange(Math.max(min, parseInt(e.target.value) || 0))}
              style={{ width: '52px', textAlign: 'right', fontWeight: 700, fontSize: '13px', background: 'transparent', border: 'none', outline: 'none', color: NAVY, fontFamily: 'inherit' }}
            />
            <span style={{ fontSize: '11px', fontWeight: 600, color: 'hsl(var(--muted-fg))', flexShrink: 0 }}>{unit}</span>
          </div>
          {pill('+10', 10)}
          {pill('+100', 100)}
        </div>
      </div>
    </div>
  );
}

// ── VPC helper - matches the VPC extra regardless of exact code (CLO-VPC, VPC, etc.)
const isVpcExtra     = (e) => e.code?.toUpperCase() === 'VPC' || e.code?.toUpperCase().includes('-VPC') || e.name?.toUpperCase() === 'VPC';
const isVpcExtraLine = (e) => e.extra_code?.toUpperCase() === 'VPC' || e.extra_code?.toUpperCase().includes('-VPC') || e.extra_name?.toUpperCase() === 'VPC';
// ── File Storage helper - matches CLO-Storage extras
const isFileStorageExtra = (e) => (e.code || e.extra_code || '').toUpperCase().includes('CLO-STORAGE') || (e.name || e.extra_name || '').toLowerCase().includes('file storage');
// ── Default included file storage per product
const defaultFileStorageGb = (productCode, isStandalone) => {
  if (isStandalone) return 0;
  if (productCode === 'STANDARD') return 10;
  if (productCode === 'ADVANCED' || productCode === 'MTPRO') return 100;
  return 0;
};

// ── WizardCard: defined at module scope to prevent re-mounting on re-render ─────
// IMPORTANT: Must be outside all functions/components so React sees a stable
// component identity and doesn't unmount children (which causes input focus loss).
function WizardCard({ title, children }) {
  return (
    <div className="rounded-xl border bg-white overflow-hidden"
         style={{ boxShadow: '0 1px 2px rgba(0,0,0,0.04), 0 6px 16px rgba(0,0,0,0.04)' }}>
      {title && (
        <div className="px-5 py-3.5 flex items-center gap-2.5"
             style={{ borderBottom: '1px solid hsl(var(--border))', background: 'hsl(var(--muted) / 0.5)' }}>
          <div className="w-1 h-5 rounded-full flex-shrink-0"
               style={{ background: 'hsl(var(--c4p-brand-blue))' }} />
          <h3 className="text-sm font-bold"
              style={{ fontFamily: 'var(--font-display)', color: NAVY }}>{title}</h3>
        </div>
      )}
      <div className="p-5">{children}</div>
    </div>
  );
}

// ── Main Wizard Component ──────────────────────────────────────────────────────
export default function QuoteWizardPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, isAdmin } = useAuth();
  const isEditing = !!id;

  const [step, setStep] = useState(1);
  const [data, setData] = useState(INITIAL_WIZARD);
  const [existingQuote, setExistingQuote] = useState(null);
  const [pricingConfig, setPricingConfig] = useState(null);
  const [appServerPurposes, setAppServerPurposes] = useState([]);

  const [customers, setCustomers] = useState([]);
  const [tenants, setTenants]     = useState([]);
  const [psmUsers, setPsmUsers] = useState([]);
  const [loadingData, setLoadingData] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [pricingResult, setPricingResult] = useState(null);
  const [pricingError, setPricingError] = useState(null);
  const [calculating, setCalculating] = useState(false);
  const [saving, setSaving] = useState(false);

  // Manual contract price overrides (Super Admin only, MANUAL quote_category)
  const [manualOverrides, setManualOverrides] = useState([]);
  const isManualContract = data.is_standalone && data.standalone_subtype === 'MANUAL';
  const isSuperAdmin = user?.role === 'SUPER_ADMIN';

  // AI state
  const [aiOpen, setAiOpen] = useState(false);
  const [aiText, setAiText] = useState('');
  const [aiParsing, setAiParsing] = useState(false);
  const [aiParseResult, setAiParseResult] = useState(null);
  const [aiSummarising, setAiSummarising] = useState(false);
  const [summaryDialog, setSummaryDialog] = useState(false);
  const [summaryText, setSummaryText] = useState('');
  const [aiRecommending, setAiRecommending] = useState(false);
  const [recommendations, setRecommendations] = useState([]);

  const set = useCallback((updates) => setData(prev => ({ ...prev, ...updates })), []);

  // Listen for "reset-quote-wizard" event fired by the header "+ New Quote" button
  // when the user confirms they want to discard the current in-progress quote
  useEffect(() => {
    if (isEditing) return; // only applies to new quotes, not edit mode
    const handler = () => {
      setData(INITIAL_WIZARD);
      setStep(1);
      setPricingResult(null);
      setPricingError(null);
      setAiParseResult(null);
      setAiPartnerHint('');
      setAiTenantHint('');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    };
    window.addEventListener('reset-quote-wizard', handler);
    return () => window.removeEventListener('reset-quote-wizard', handler);
  }, [isEditing]); // eslint-disable-line react-hooks/exhaustive-deps

  // Initial data load
  useEffect(() => {
    const load = async () => {
      setLoadingData(true);
      try {
        const [cfgRes, custRes, defaultsRes, tenantsRes] = await Promise.all([
          pricingAPI.getConfig(),
          customersAPI.list({ page_size: 500, status: 'active' }),
          apiClient.get('/admin/settings/defaults').catch(() => ({ data: {} })),
          tenantsAPI.list({ page_size: 500, status: 'active' }).catch(() => ({ data: { items: [] } })),
        ]);
        const cfg = cfgRes.data;
        const defaults = defaultsRes.data || {};
        const defaultTerm     = parseInt(defaults.default_term_months || '12');
        const defaultCurrency = defaults.default_currency || 'GBP';

        setPricingConfig(cfg);
        const allCustomers = custRes.data.items || [];
        const allTenants = tenantsRes.data.items || [];
        setCustomers(allCustomers);
        setTenants(allTenants);

        // ── Preset auto-selection: when returning from PartnerCreatePage or TenantCreatePage ──
        // preset_partner / preset_tenant URL params carry the newly created IDs.
        const presetPartnerId = searchParams.get('preset_partner');
        const presetTenantId  = searchParams.get('preset_tenant');
        if (!id && (presetPartnerId || presetTenantId)) {
          const updates = {};
          if (presetPartnerId) {
            const p = allCustomers.find(c => c.id === presetPartnerId);
            if (p) {
              updates.customer_id = p.id;
              if (p.default_region_id) {
                const region = (cfg.regions || []).find(r => r.id === p.default_region_id);
                if (region) {
                  updates.region_id       = region.id;
                  updates.region_name     = region.name;
                  updates.region_aws_code = region.aws_region_code || '';
                  updates.currency        = p.default_currency || region.default_currency || 'GBP';
                }
              } else if (p.default_currency) {
                updates.currency = p.default_currency;
              }
              if (p.default_psm_id) { updates.psm_user_id = p.default_psm_id; updates.psm_user_name = p.default_psm_name || ''; }
              if (p.default_payment_method) updates.payment_method = p.default_payment_method;
            }
          }
          if (presetTenantId) {
            const t = allTenants.find(t => t.id === presetTenantId);
            if (t) {
              updates.tenant_id   = t.id;
              updates.tenant_name = t.company_name || '';
            }
          }
          if (Object.keys(updates).length > 0) setData(prev => ({ ...prev, ...updates }));
        }
          usersAPI.directory('SALES').then(r => {
            const users = Array.isArray(r.data) ? r.data : [];
            setPsmUsers(users);
            // Default PSM to the logged-in user if not already set (new quote only)
            setData(prev => {
              if (prev.psm_user_id) return prev;
              const me = users.find(u => u.id === user?.id);
              if (me) return { ...prev, psm_user_id: me.id, psm_user_name: me.name };
              return prev;
            });
          }).catch(() => {});

        // Fetch app server purposes (non-blocking - defaults applied if fails)
        adminAPI.getAppServerPurposes().then(r => setAppServerPurposes(r.data.purposes || [])).catch(() => {});

        if (id) {
          const { data: q } = await quotesAPI.get(id);
          setExistingQuote(q);
          if (!q.is_legacy_model) {
            const baseHanaCode = (cfg.server_catalog || []).find(s => s.is_base_for_hana)?.code || '';
            const dbCode = q.db_server?.server_code || (['ADVANCED','MTPRO'].includes(q.product_code) ? baseHanaCode : '');
            const jumpToStep = parseInt(new URLSearchParams(window.location.search).get('step') || '1');
            setData({
              customer_id: q.customer_id || '',
              contact_ids: q.contact_ids || [],
              expiry_date: q.expiry_date || '',
              required_by_date: q.required_by_date || '',
              use_next_available_slot: q.use_next_available_slot || false,
              quote_name: q.quote_name || '',
              mtpro_tenants: q.mtpro_tenants || [],
              mtpro_quote_type: q.mtpro_quote_type || 'new',
              mtpro_system_id: q.mtpro_system_id || '',
              billing_frequency: q.billing_frequency || 'Monthly',
              payment_method: q.payment_method || '',
              notes: q.notes || '',
              product_code: q.product_code || 'STANDARD',
              num_users: String(q.num_users ?? ''),
              num_web_client_users: q.num_web_client_users || 0,
              num_mobile_users: q.num_mobile_users || 0,
              term_months: q.term_months || defaultTerm,
              currency: q.currency || defaultCurrency,
              region_id: q.region_id || '',
              region_name: q.region_name || '',
              region_aws_code: q.region_aws_code || '',
              end_customer_name: q.end_customer_name || '',
              tenant_id: q.tenant_id || '',
              tenant_name: q.tenant_name || '',
              billing_model: q.billing_model || 'PARTNER',
              psm_user_id: q.psm_user_id || '',
              psm_user_name: q.psm_user_name || '',
              partner_margin_pupm: q.partner_margin_pupm != null ? String(q.partner_margin_pupm) : '',
              partner_margin_percent: q.partner_margin_percent != null ? String(q.partner_margin_percent) : '',
              db_type: q.db_type || q.db_server?.db_type || 'HANA',
              db_server_code: dbCode,
              additional_data_gb: q.db_server?.additional_data_gb || 0,
              additional_backup_gb: q.db_server?.additional_backup_gb || 0,
              additional_db_root_gb: q.db_server?.additional_root_gb || 0,
              app_servers: (q.app_servers || []).map(s => ({
                server_code: s.server_code, quantity: s.quantity || 1,
                additional_root_gb: s.additional_root_gb || 0,
                is_included_in_pupm: s.is_included_in_pupm || false,
                notes: s.notes || null,
              })),
              extras: (q.extras || []).map(e => ({
                extra_id: e.extra_id, extra_code: e.extra_code, extra_name: e.extra_name,
                quantity: e.quantity, unit: e.unit || 'each', pricing_type: e.pricing_type || 'recurring',
              })),
              // Standalone fields
              is_standalone: (q.quote_category === 'TEST_SYSTEM' || q.quote_category === 'BESPOKE' || q.quote_category === 'MANUAL') || false,
              standalone_subtype: q.quote_category === 'TEST_SYSTEM' ? 'TEST_SYSTEM' : q.quote_category === 'MANUAL' ? 'MANUAL' : 'BESPOKE',
              standalone_ccc_required: q.product_code === 'MTPRO' && (q.quote_category === 'TEST_SYSTEM' || q.quote_category === 'BESPOKE' || q.quote_category === 'MANUAL'),
              service_unit_ref: q.service_unit_ref || '',
              // Deal grouping
              deal_ref: q.deal_ref || '',
              deal_probability: q.deal_probability ?? 100,
              // File storage — use stored value or fall back to product default
              file_storage_gb: q.file_storage_gb != null ? q.file_storage_gb : defaultFileStorageGb(q.product_code, (q.quote_category === 'TEST_SYSTEM' || q.quote_category === 'BESPOKE' || q.quote_category === 'MANUAL')),
              // For C4P Custom: rebuild extra_db_servers from BOTH the primary db_server
              // (stored as db_server on save) AND any extra_db_servers (2nd, 3rd, etc.)
              extra_db_servers: (() => {
                const isCustomQuote = q.quote_category === 'TEST_SYSTEM' || q.quote_category === 'BESPOKE' || q.quote_category === 'MANUAL';
                const primaryEntry = isCustomQuote && q.db_server ? [{
                  id: crypto.randomUUID(),
                  db_type: q.db_server.db_type || 'HANA',
                  db_server_code: q.db_server.server_code || '',
                  purpose: q.db_server.purpose || '',
                  additional_data_gb: q.db_server.additional_data_gb || 0,
                  additional_backup_gb: q.db_server.additional_backup_gb || 0,
                  additional_db_root_gb: q.db_server.additional_root_gb || 0,
                }] : [];
                const additionalEntries = (q.extra_db_servers || []).map(s => ({
                  id: crypto.randomUUID(),
                  db_type: s.db_type || 'HANA',
                  db_server_code: s.server_code || '',
                  purpose: s.purpose || '',
                  additional_data_gb: s.additional_data_gb || 0,
                  additional_backup_gb: s.additional_backup_gb || 0,
                  additional_db_root_gb: s.additional_root_gb || 0,
                }));
                return [...primaryEntry, ...additionalEntries];
              })(),
            });

            // For MANUAL quotes in edit mode (including CRs), pre-populate the price
            // overrides from the saved pricing_snapshot so the right panel and Step 8
            // show the contract's bespoke prices — NOT the DB engine recalculation.
            if (q.quote_category === 'MANUAL' && q.pricing_snapshot?.line_items) {
              const savedLines = (q.pricing_snapshot.line_items || []).filter(
                // Include ALL non-included items, even those with monthly_amount=0.
                // Zeroed items are explicit overrides (e.g. server costs absorbed into PUPM).
                // Excluding them caused the wizard to show standard engine prices on first open.
                li => !li.is_included
              );
              const qIsTenantDirect = q.billing_model === 'TENANT_DIRECT';
              const qPupmMargin = q.partner_margin_pupm || 0;
              const qNonUserPct = q.partner_margin_percent || 0;
              setManualOverrides(savedLines.map(li => {
                const unitPrice = li.unit_price ?? li.monthly_amount;
                const entry = {
                  category: li.category,
                  label: li.label,
                  sub_label: li.sub_label || '',
                  qty: li.qty ?? 1,
                  unit_price: unitPrice,
                  monthly_amount: li.monthly_amount,
                };
                if (qIsTenantDirect) {
                  // Auto-prefill from the saved bespoke tenant price, or fall back to
                  // the margin formula so the admin always sees real numbers to tweak.
                  const tenantUnit = li.tenant_unit_price != null
                    ? li.tenant_unit_price
                    : (li.category === 'base' ? unitPrice + qPupmMargin : unitPrice * (1 + qNonUserPct / 100));
                  entry.tenant_unit_price = Math.round(tenantUnit * 100) / 100;
                  entry.tenant_monthly_amount = li.tenant_monthly_amount != null
                    ? li.tenant_monthly_amount
                    : Math.round((li.qty ?? 1) * entry.tenant_unit_price * 100) / 100;
                }
                return entry;
              }));
            }

            if (jumpToStep > 1) setStep(jumpToStep);
          }
        } else {
          // New quote: apply defaults from settings + base server + current user as PSM
          const baseHana = (cfg.server_catalog || []).find(s => s.is_base_for_hana);
          const defaultExpiry = new Date();
          defaultExpiry.setDate(defaultExpiry.getDate() + 30);
          const defaultExpiryStr = defaultExpiry.toISOString().split('T')[0];

          // Check URL params - "Add to MTPro System" deep link from dashboard
          const urlParams = new URLSearchParams(window.location.search);
          const urlProduct    = urlParams.get('product');
          const urlPartnerId  = urlParams.get('partner_id');

          const urlOverrides = {};
          if (urlProduct === 'MTPRO') {
            urlOverrides.product_code       = 'MTPRO';
            urlOverrides.db_server_code     = baseHana?.code || '';
            if (urlPartnerId) {
              const partner = (custRes.data.items || []).find(c => c.id === urlPartnerId);
              if (partner) {
                urlOverrides.customer_id = urlPartnerId;
                const pDefaultRegion = (cfg.regions || []).find(r => r.id === partner.default_region_id);
                if (pDefaultRegion) {
                  urlOverrides.region_id       = pDefaultRegion.id;
                  urlOverrides.region_name     = pDefaultRegion.name;
                  urlOverrides.region_aws_code = pDefaultRegion.aws_region_code || '';
                  urlOverrides.currency        = partner.default_currency || pDefaultRegion.default_currency || defaultCurrency;
                }
              }
            }
            // Skip to Step 3 (product is already known)
            setStep(3);
          }

          setData(prev => ({
            ...prev,
            term_months: defaultTerm,
            currency: defaultCurrency,
            db_server_code: baseHana?.code || '',
            psm_user_id: user?.id || '',
            psm_user_name: user?.name || '',
            expiry_date: defaultExpiryStr,
            ...urlOverrides,
          }));
        }
      } catch (e) { setLoadError('Failed to load quote data. Please refresh.'); }
      finally { setLoadingData(false); }
    };
    load();
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps


  // Clear any stale pricing errors when switching to/from C4P Custom (standalone)
  // prevents the "minimum 20 users" Advanced error persisting after selecting Custom
  useEffect(() => {
    setPricingError(null);
    setPricingResult(null);
    // Reset file storage to product default when standalone changes
    if (!isEditing) set({ file_storage_gb: defaultFileStorageGb(data.product_code, data.is_standalone) });
  }, [data.is_standalone]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (isEditing) return; // don't overwrite existing quotes
    const customer = customers.find(c => c.id === data.customer_id);
    if (!customer) return;
    let product;
    if (data.is_standalone) {
      const cccLabel = data.standalone_ccc_required ? ' + CCC' : '';
      product = data.standalone_subtype === 'TEST_SYSTEM'
        ? `C4P Custom Test System${cccLabel}`
        : `C4P Custom${cccLabel}`;
    } else {
      product = { STANDARD: 'Standard', ADVANCED: 'Advanced', MTPRO: 'MTPro' }[data.product_code] || data.product_code;
    }
    const today = new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
    const parts = [data.is_standalone ? product : `C4P ${product}`];
    // For Tenant Direct billing, the quote goes to the tenant — omit partner name
    if (data.billing_model !== 'TENANT_DIRECT') parts.push(customer.company_name);
    // For Direct billing there is no tenant — omit tenant name
    if (data.tenant_name && data.billing_model !== 'DIRECT') parts.push(data.tenant_name);
    parts.push(today);
    set({ quote_name: parts.join(' · ') });
  }, [data.customer_id, data.product_code, data.tenant_name, data.is_standalone, data.standalone_subtype, data.standalone_ccc_required, data.billing_model]); // eslint-disable-line react-hooks/exhaustive-deps

  // Edit mode: update quote name when billing model changes
  useEffect(() => {
    if (!isEditing || !data.quote_name || !data.customer_id) return;
    const customer = customers.find(c => c.id === data.customer_id);
    if (!customer) return;
    const partnerName = customer.company_name;
    if (!partnerName) return;

    let updated = data.quote_name;
    const shouldHavePartner = data.billing_model !== 'TENANT_DIRECT';
    const shouldHaveTenant  = data.billing_model !== 'DIRECT' && !!data.tenant_name;
    const hasPartner = updated.includes(partnerName);
    const hasTenant  = data.tenant_name ? updated.includes(data.tenant_name) : false;

    // Add/remove partner name
    if (shouldHavePartner && !hasPartner) {
      const firstSep = updated.indexOf(' · ');
      if (firstSep !== -1) updated = updated.slice(0, firstSep) + ` · ${partnerName}` + updated.slice(firstSep);
    } else if (!shouldHavePartner && hasPartner) {
      updated = updated.split(` · ${partnerName}`).join('').split(`${partnerName} · `).join('').trim();
    }

    // Add/remove tenant name
    if (shouldHaveTenant && !hasTenant && data.tenant_name) {
      // Insert tenant name before the date (last segment), or append before date
      const lastSep = updated.lastIndexOf(' · ');
      if (lastSep !== -1) {
        updated = updated.slice(0, lastSep) + ` · ${data.tenant_name}` + updated.slice(lastSep);
      } else {
        updated = updated + ` · ${data.tenant_name}`;
      }
    } else if (!shouldHaveTenant && hasTenant && data.tenant_name) {
      updated = updated.split(` · ${data.tenant_name}`).join('').split(`${data.tenant_name} · `).join('').trim();
    }

    if (updated !== data.quote_name) set({ quote_name: updated });
  }, [data.billing_model]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!isEditing || !data.quote_name) return;
    const PRODUCT_LABELS = { STANDARD: 'C4P Standard', ADVANCED: 'C4P Advanced', MTPRO: 'C4P MTPro' };
    // Build the new product label
    let newProductLabel;
    if (data.is_standalone) {
      const cccLabel = data.standalone_ccc_required ? ' + CCC' : '';
      newProductLabel = data.standalone_subtype === 'TEST_SYSTEM' ? `C4P Custom Test System${cccLabel}` : `C4P Custom${cccLabel}`;
    } else {
      newProductLabel = PRODUCT_LABELS[data.product_code];
    }
    if (!newProductLabel) return;
    // Replace any other known product label with the new one
    const allLabels = [...Object.values(PRODUCT_LABELS), 'C4P Custom', 'C4P Custom Test System', 'C4P Custom + CCC', 'C4P Custom Test System + CCC'];
    let updated = data.quote_name;
    for (const old of allLabels) {
      if (old !== newProductLabel && updated.includes(old)) {
        updated = updated.split(old).join(newProductLabel);
        break;
      }
    }
    if (updated !== data.quote_name) set({ quote_name: updated });
  }, [data.product_code, data.is_standalone, data.standalone_subtype, data.standalone_ccc_required]); // eslint-disable-line react-hooks/exhaustive-deps


  // Auto-set base DB server when product/db_type changes (not for C4P Custom - DB is optional)
  useEffect(() => {
    if (!pricingConfig) return;
    if (['ADVANCED', 'MTPRO'].includes(data.product_code) && !data.is_standalone) {
      const base = (pricingConfig.server_catalog || []).find(s =>
        s.is_base_for_hana && data.db_type === 'HANA' ||
        s.is_base_for_sql && data.db_type === 'SQL'
      );
      // Only reset if current code doesn't match selected db_type
      const currentSvr = (pricingConfig.server_catalog || []).find(s => s.code === data.db_server_code);
      const wrongType = currentSvr && currentSvr.server_type === 'HANADB' && data.db_type === 'SQL';
      const wrongType2 = currentSvr && currentSvr.server_type === 'SQLDB' && data.db_type === 'HANA';
      if ((!currentSvr || wrongType || wrongType2) && base) {
        set({ db_server_code: base.code, additional_data_gb: 0, additional_backup_gb: 0 });
      }
    }
  }, [data.product_code, data.db_type, data.is_standalone, pricingConfig]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-set included app server for Advanced / MTPro (NOT for C4P Custom standalone)
  useEffect(() => {
    if (!pricingConfig) return;
    if (['ADVANCED', 'MTPRO'].includes(data.product_code) && !data.is_standalone) {
      const incl = (pricingConfig.server_catalog || []).find(s => s.is_included_app_server);
      if (incl && !data.app_servers.some(s => s.is_included_in_pupm)) {
        set({ app_servers: [{ server_code: incl.code, quantity: 1, additional_root_gb: 0, is_included_in_pupm: true, notes: '' }, ...data.app_servers.filter(s => !s.is_included_in_pupm)] });
      }
    } else {
      // Standard or C4P Custom: remove included server
      set({ app_servers: data.app_servers.filter(s => !s.is_included_in_pupm) });
    }
  }, [data.product_code, data.is_standalone, pricingConfig]); // eslint-disable-line react-hooks/exhaustive-deps

  // Live pricing (debounced)
  useEffect(() => {
    if (!pricingConfig || !data.currency) return;
    // Standalone: treat empty/NaN as 0 (no minimum); otherwise require > 0
    const n = data.is_standalone ? (parseInt(data.num_users) || 0) : parseInt(data.num_users);
    if (!data.is_standalone && (isNaN(n) || n <= 0)) { setPricingResult(null); setPricingError(null); return; }
    if (n < 0) { setPricingResult(null); setPricingError(null); return; }
    setCalculating(true);
    const timer = setTimeout(async () => {
      try {
        const appSvrsForPricing = data.app_servers.filter(s => !s.is_included_in_pupm);
        const includedSvrForPricing = data.app_servers.find(s => s.is_included_in_pupm);
        const payload = {
          product_code: data.product_code, num_users: n,
          num_web_client_users: parseInt(data.num_web_client_users) || 0,
          num_mobile_users: parseInt(data.num_mobile_users) || 0,
          term_months: data.term_months, currency: data.currency,
          region_aws_code: data.region_aws_code || null,
          db_server: ['ADVANCED', 'MTPRO'].includes(data.product_code) && (data.is_standalone
            ? (data.extra_db_servers?.length > 0 ? { server_code: data.extra_db_servers[0].db_server_code, db_type: data.extra_db_servers[0].db_type, additional_data_gb: data.extra_db_servers[0].additional_data_gb || 0, additional_backup_gb: data.extra_db_servers[0].additional_backup_gb || 0, additional_root_gb: data.extra_db_servers[0].additional_db_root_gb || 0, purpose: data.extra_db_servers[0].purpose || null } : null)
            : (data.db_server_code ? { server_code: data.db_server_code, db_type: data.db_type, additional_data_gb: data.additional_data_gb, additional_backup_gb: data.additional_backup_gb, additional_root_gb: data.additional_db_root_gb } : null)
          ) || null,
          extra_db_servers: data.is_standalone ? (data.extra_db_servers || []).slice(1).map(s => ({ server_code: s.db_server_code, db_type: s.db_type, additional_data_gb: s.additional_data_gb || 0, additional_backup_gb: s.additional_backup_gb || 0, additional_root_gb: s.additional_db_root_gb || 0, purpose: s.purpose || null })) : [],
          included_app_server: isAdvanced && includedSvrForPricing ? { server_code: includedSvrForPricing.server_code, additional_root_gb: includedSvrForPricing.additional_root_gb || 0, purpose: includedSvrForPricing.notes || null } : null,
          app_servers: appSvrsForPricing.map(s => ({ server_code: s.server_code, quantity: s.quantity, additional_root_gb: s.additional_root_gb, purpose: s.notes || null })),
          extras: (data.extras || []).filter(e => {
            if (!e || typeof e !== 'object' || !e.extra_id) return false;
            // Strip VPC extra for Advanced/MTPro (non-standalone) - it is included in the plan
            if (isAdvanced && !data.is_standalone && isVpcExtraLine(e)) return false;
            // Strip CLO-Storage File extra - handled via file_storage_gb stepper for all products
            if (isFileStorageExtra(e)) return false;
            return true;
          }),
          is_standalone: data.is_standalone === true,
          file_storage_gb: data.file_storage_gb || 0,
        };
        const { data: res } = await pricingAPI.calculate(payload);
        setPricingResult(res); setPricingError(null);
      } catch (err) { setPricingError(extractErrorMessage(err, 'Pricing error')); setPricingResult(null); }
      finally { setCalculating(false); }
    }, 500);
    return () => clearTimeout(timer);
  }, [data.product_code, data.num_users, data.num_web_client_users, data.num_mobile_users, data.term_months, data.currency, data.region_aws_code, data.db_type, data.db_server_code, data.additional_data_gb, data.additional_backup_gb, data.additional_db_root_gb, data.app_servers, data.extras, data.is_standalone, data.extra_db_servers, data.file_storage_gb, pricingConfig]);

  // Derived
  const selectedCustomer = useMemo(() => customers.find(c => c.id === data.customer_id), [customers, data.customer_id]);
  const dbServers = useMemo(() => {
    if (!pricingConfig) return [];
    const t = data.db_type === 'HANA' ? 'HANADB' : 'SQLDB';
    return (pricingConfig.server_catalog || []).filter(s =>
      s.server_type === t && s.is_active !== false && !s.custom_only
    );
  }, [pricingConfig, data.db_type]);
  const appServerCatalog = useMemo(() => {
    if (!pricingConfig) return { Windows: [], Linux: [], 'Ubuntu Pro': [], GPU: [] };
    const svrs = (pricingConfig.server_catalog || []).filter(s => s.server_type === 'Application' && s.is_active !== false);
    const grouped = {};
    for (const s of svrs) {
      const key = s.os_included?.includes('Ubuntu') ? 'Ubuntu Pro' : s.os_included?.includes('GPU') ? 'GPU' : s.os_included?.includes('Linux') ? 'Linux' : 'Windows';
      if (!grouped[key]) grouped[key] = [];
      grouped[key].push(s);
    }
    // Sort each group by GBP price (London region preferred, else any GBP), lowest first
    const getGBP = (s) => {
      const rpm = s.region_price_matrix || {};
      return rpm['eu-west-2']?.GBP                           // London GBP
          || Object.values(rpm).map(r => r.GBP || 0).find(v => v > 0)  // any region GBP
          || s.prices_by_currency?.GBP                        // flat fallback
          || 0;
    };
    for (const key of Object.keys(grouped)) {
      grouped[key].sort((a, b) => getGBP(a) - getGBP(b));
    }
    return grouped;
  }, [pricingConfig]);
  const availableExtras = useMemo(() => {
    if (!pricingConfig) return [];
    return (pricingConfig.extras_catalog || pricingConfig.extras || []).filter(e =>
      e.is_active !== false && (e.applies_to || []).includes(data.product_code));
  }, [pricingConfig, data.product_code]);

  const currentDbServer = useMemo(() => dbServers.find(s => s.code === data.db_server_code), [dbServers, data.db_server_code]);
  const isAdvanced = data.product_code === 'ADVANCED' || data.product_code === 'MTPRO';
  const isStandalone = data.is_standalone === true;
  // Super Admin reassign-only mode: ACCEPTED/EXPIRED quotes are fully locked
  // for everyone else, but a Super Admin may still open them to change the PSM
  // (which transfers ownership) without touching any other field.
  const isReassignOnlyMode = isEditing && isSuperAdmin && ['ACCEPTED', 'EXPIRED'].includes(existingQuote?.status);
  const isReadOnly = (isEditing && existingQuote && existingQuote.owner_user_id !== user?.id && !isAdmin) || isReassignOnlyMode;

  // For Step 8 (Manual Contract pricing), compute an effective pricingResult
  // that reflects the user's overrides so MiniSummary stays in sync
  const effectivePricingResult = useMemo(() => {
    if (!isManualContract || manualOverrides.length === 0 || !pricingResult) return pricingResult;
    const isTD = data.billing_model === 'TENANT_DIRECT';
    const pupmMargin = parseFloat(data.partner_margin_pupm) || 0;
    const nonUserPct = parseFloat(data.partner_margin_percent) || 0;
    const overridesMap = {};
    manualOverrides.forEach(o => { overridesMap[`${o.category}::${o.label}`] = o; });
    const newLineItems = (pricingResult.line_items || []).map(li => {
      const key = `${li.category}::${li.label}`;
      const ov = overridesMap[key];
      let newLi = li;
      if (ov && !li.is_included) {
        // Use engine qty (reflects live form inputs like num_users) × manual unit_price (bespoke rate)
        const engineQty = li.qty ?? 1;
        const overrideUnitPrice = ov.unit_price;
        const newMonthly = Math.round(engineQty * overrideUnitPrice * 100) / 100;
        newLi = { ...li, unit_price: overrideUnitPrice, monthly_amount: newMonthly };
      }
      if (isTD && !li.is_included) {
        // Live Tenant price: the bespoke value the admin just typed, or the standard
        // margin formula default — so the summary panel always matches Step 8.
        const baseUnitPrice = newLi.unit_price ?? 0;
        const engineQty = newLi.qty ?? 1;
        const tenantUnit = ov?.tenant_unit_price != null
          ? ov.tenant_unit_price
          : (li.category === 'base' ? baseUnitPrice + pupmMargin : baseUnitPrice * (1 + nonUserPct / 100));
        // Always derive tenant_monthly_amount fresh from the live qty × tenantUnit —
        // never trust a stored ov.tenant_monthly_amount, which goes stale the moment
        // either price is edited (Step8ManualPricing doesn't keep it in sync).
        const tenantMonthly = Math.round(engineQty * tenantUnit * 100) / 100;
        newLi = { ...newLi, tenant_unit_price: Math.round(tenantUnit * 100) / 100, tenant_monthly_amount: tenantMonthly };
      }
      return newLi;
    });
    const newTotal = Math.round(newLineItems.filter(li => !li.is_included).reduce((s, li) => s + (li.monthly_amount || 0), 0) * 100) / 100;
    // Recompute the 'base' PUPM subtotal too — the Tenant Direct margin preview
    // below (non-per-user % markup) relies on monthly_total minus monthly_base,
    // so it must reflect any override applied to the base line item as well.
    const newBase = Math.round(newLineItems.filter(li => !li.is_included && li.category === 'base').reduce((s, li) => s + (li.monthly_amount || 0), 0) * 100) / 100;
    const result = { ...pricingResult, monthly_total: newTotal, monthly_base: newBase, line_items: newLineItems };
    if (isTD) {
      // Live Tenant invoice total = sum of each line's live tenant amount —
      // matches the backend's per-line sum-with-fallback once saved.
      result.tenant_invoice_monthly = Math.round(
        newLineItems.filter(li => !li.is_included).reduce((s, li) => s + (li.tenant_monthly_amount || 0), 0) * 100
      ) / 100;
    }
    return result;
  }, [isManualContract, manualOverrides, pricingResult, data.billing_model, data.partner_margin_pupm, data.partner_margin_percent]);

  // Redirect away immediately if someone tries to edit an ACCEPTED quote
  // (Super Admins are exempt — they may still open it to reassign the PSM)
  useEffect(() => {
    if (isEditing && existingQuote?.status === 'ACCEPTED' && !isSuperAdmin) {
      toast.error('Accepted quotes are locked and cannot be edited. Use Save As New to create a new version.');
      navigate(`/quotes/${id}`);
    }
  }, [isEditing, existingQuote?.status, id, navigate, isSuperAdmin]);

  // Step validation
  const canProceed = useMemo(() => {
    if (step === 1) {
      if (!data.customer_id) return false;
      // A tenant is always required regardless of billing model
      if (!data.tenant_id) return false;
      return true;
    }
    if (step === 2) {
      if (!data.region_id) return false;
      // Either a specific required-by date OR "next available build slot" must be chosen
      if (!data.use_next_available_slot && !data.required_by_date) return false;
      return true;
    }
    if (step === 3) {
      if (isStandalone) {
        // C4P Custom: no minimum — 0 users is valid (e.g. App Server only quote)
        const n = parseInt(data.num_users) || 0;
        if (n < 0) return false;
      } else {
        const n = parseInt(data.num_users);
        if (isNaN(n) || n <= 0) return false;
        if (isAdvanced && n < 20) return false;
      }
      return true;
    }
    if (step === 5) {
      // All app servers must have a purpose defined
      return data.app_servers.every(s => s.notes?.trim());
    }
    return true;
  }, [step, data.customer_id, data.tenant_id, data.billing_model, data.region_id, data.num_users, data.required_by_date, data.use_next_available_slot, data.app_servers, isAdvanced, isStandalone]);

  const visibleSteps = useMemo(() => {
    let steps = STEPS.filter(s => s.id !== 4 || isAdvanced);     // DB step only for Advanced/MTPro
    // Step 8 (Price Override) only for MANUAL quote type and Super Admin
    if (!(data.is_standalone && data.standalone_subtype === 'MANUAL' && user?.role === 'SUPER_ADMIN')) {
      steps = steps.filter(s => s.id !== 8);
    }
    return steps;
  }, [isAdvanced, data.product_code, data.is_standalone, data.standalone_subtype, user?.role]);

  const nextStep = () => {
    const ids = visibleSteps.map(s => s.id);
    const idx = ids.indexOf(step);
    if (idx < ids.length - 1) {
      setStep(ids[idx + 1]);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };
  const prevStep = () => {
    const ids = visibleSteps.map(s => s.id);
    const idx = ids.indexOf(step);
    if (idx > 0) {
      setStep(ids[idx - 1]);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const buildPayload = () => ({
    customer_id: data.customer_id,
    product_code: data.product_code,
    num_users: parseInt(data.num_users) || 0,
    num_web_client_users: parseInt(data.num_web_client_users) || 0,
    num_mobile_users: parseInt(data.num_mobile_users) || 0,
    term_months: data.term_months,
    currency: data.currency,
    billing_model: data.billing_model || 'PARTNER',
    region_id: data.region_id || null,
    region_name: data.region_name || null,
    region_aws_code: data.region_aws_code || null,
    end_customer_name: data.tenant_name || data.end_customer_name || null,
    tenant_id: data.tenant_id || null,
    tenant_name: data.tenant_name || null,
    psm_user_id: data.psm_user_id || null,
    psm_user_name: data.psm_user_name || null,
    partner_margin_pupm: data.billing_model === 'TENANT_DIRECT' && data.partner_margin_pupm !== '' ? parseFloat(data.partner_margin_pupm) || 0 : null,
    partner_margin_percent: data.billing_model === 'TENANT_DIRECT' && data.partner_margin_percent !== '' ? parseFloat(data.partner_margin_percent) || 0 : null,
    db_server: (isAdvanced && (isStandalone
      ? (data.extra_db_servers?.length > 0 ? { server_code: data.extra_db_servers[0].db_server_code, db_type: data.extra_db_servers[0].db_type, additional_data_gb: data.extra_db_servers[0].additional_data_gb || 0, additional_backup_gb: data.extra_db_servers[0].additional_backup_gb || 0, additional_root_gb: data.extra_db_servers[0].additional_db_root_gb || 0, purpose: data.extra_db_servers[0].purpose || null } : null)
      : (data.db_server_code ? { server_code: data.db_server_code, db_type: data.db_type, additional_data_gb: data.additional_data_gb, additional_backup_gb: data.additional_backup_gb, additional_root_gb: data.additional_db_root_gb } : null)
    )) || null,
    extra_db_servers: isStandalone ? (data.extra_db_servers || []).slice(1).map(s => ({ server_code: s.db_server_code, db_type: s.db_type, additional_data_gb: s.additional_data_gb || 0, additional_backup_gb: s.additional_backup_gb || 0, additional_root_gb: s.additional_db_root_gb || 0, purpose: s.purpose || null })) : [],
    db_type: !isAdvanced ? (data.db_type || 'HANA') : null,  // stored at top level for Standard (no db_server record)
    app_servers: data.app_servers.map(s => ({
      server_code: s.server_code,
      quantity: s.quantity,
      additional_root_gb: s.additional_root_gb,
      is_included_in_pupm: s.is_included_in_pupm,
      notes: s.notes || null,
    })),
    extras: (data.extras || []).filter(e => {
      if (!e || typeof e !== 'object' || !e.extra_id) return false;
      // Strip VPC extra for Advanced/MTPro (non-standalone) - it is included in the plan
      if (isAdvanced && !isStandalone && isVpcExtraLine(e)) return false;
      // Strip CLO-Storage File extra - handled via file_storage_gb stepper for all products
      if (isFileStorageExtra(e)) return false;
      return true;
    }),
    expiry_date: data.expiry_date || null,
    required_by_date: data.use_next_available_slot ? null : (data.required_by_date || null),
    use_next_available_slot: data.use_next_available_slot || false,
    quote_name: data.quote_name || null,
    mtpro_tenants: data.mtpro_tenants || [],
    mtpro_quote_type: data.mtpro_quote_type || 'new',
    mtpro_system_id: data.mtpro_system_id || null,
    billing_frequency: data.billing_frequency || 'Monthly',
    payment_method: data.payment_method || null,
    contact_ids: data.contact_ids,
    // Standalone fields
    quote_category: data.is_standalone ? (data.standalone_subtype || 'TEST_SYSTEM') : 'PRODUCTION',
    service_unit_ref: data.service_unit_ref || null,
    file_storage_gb: data.file_storage_gb || 0,
    // Deal grouping
    deal_ref: data.deal_ref?.trim() || null,
    deal_probability: data.deal_ref?.trim() ? (parseInt(data.deal_probability) || 100) : null,
    // Manual contract price overrides (Super Admin only)
    // Derived from current engine output so removed items are not sent
    manual_price_overrides: (() => {
      if (!(data.is_standalone && data.standalone_subtype === 'MANUAL')) return null;
      const isTenantDirectManual = data.billing_model === 'TENANT_DIRECT';
      const overridesMap = {};
      const tenantOverridesMap = {};
      manualOverrides.forEach(o => {
        const key = `${o.category}::${o.label}`;
        overridesMap[key] = o.unit_price;
        if (o.tenant_unit_price != null) tenantOverridesMap[key] = o.tenant_unit_price;
      });
      if (!pricingResult) return manualOverrides.length > 0 ? manualOverrides : null;
      const pupmMargin = parseFloat(data.partner_margin_pupm) || 0;
      const nonUserPct = parseFloat(data.partner_margin_percent) || 0;
      const effective = (pricingResult.line_items || [])
        .filter(li => !li.is_included && (li.monthly_amount ?? 0) > 0)
        .map(li => {
          const key = `${li.category}::${li.label}`;
          const unitPrice = key in overridesMap ? overridesMap[key] : (li.unit_price ?? 0);
          const qty = li.qty ?? 1;
          const entry = { category: li.category, label: li.label, qty, unit_price: unitPrice,
                   monthly_amount: Math.round(qty * unitPrice * 100) / 100 };
          if (isTenantDirectManual) {
            // Every line gets an explicit tenant price — defaulting to the margin
            // formula when the admin hasn't tweaked it — so the saved contract
            // always has independent, bespoke Partner/Tenant pricing per line.
            const tenantUnit = key in tenantOverridesMap
              ? tenantOverridesMap[key]
              : (li.category === 'base' ? unitPrice + pupmMargin : unitPrice * (1 + nonUserPct / 100));
            entry.tenant_unit_price = Math.round(tenantUnit * 100) / 100;
            entry.tenant_monthly_amount = Math.round(qty * entry.tenant_unit_price * 100) / 100;
          }
          return entry;
        });
      return effective.length > 0 ? effective : null;
    })(),
  });

  const handleSave = async () => {
    if (isReassignOnlyMode) {
      if ((data.psm_user_id || '') === (existingQuote?.psm_user_id || '')) {
        toast.message('No PSM change to save');
        return navigate(`/quotes/${id}`);
      }
      setSaving(true);
      try {
        const { data: res } = await quotesAPI.reassignPsm(id, { new_psm_user_id: data.psm_user_id });
        const n = res.reassigned_quote_numbers?.length || 1;
        toast.success(`${n > 1 ? `${n} quotes` : 'Quote'} now belong${n > 1 ? '' : 's'} to ${res.new_psm_user_name}`);
        navigate(`/quotes/${id}`);
      } catch (err) { toast.error(extractErrorMessage(err, 'Failed to reassign PSM')); }
      finally { setSaving(false); }
      return;
    }
    if (!data.customer_id) return toast.error('Select a customer first');
    if (!data.is_standalone && !data.num_users) return toast.error('Enter number of users');
    setSaving(true);
    try {
      if (isEditing) {
        await quotesAPI.update(id, buildPayload());
        toast.success('Quote updated');
        navigate(`/quotes/${id}`);
      } else {
        const { data: q } = await quotesAPI.create(buildPayload());
        toast.success(`Quote ${q.quote_number} saved`);
        navigate(`/quotes/${q.id}`, { replace: true });
      }
    } catch (err) { toast.error(extractErrorMessage(err, 'Failed to save quote')); }
    finally { setSaving(false); }
  };

  const handleSaveAsNew = async () => {
    setSaving(true);
    try {
      const { data: q } = await quotesAPI.create(buildPayload());
      toast.success(`New quote ${q.quote_number} created`);
      navigate(`/quotes/${q.id}`, { replace: true });
    } catch (err) { toast.error(extractErrorMessage(err, 'Failed to save quote')); }
    finally { setSaving(false); }
  };

  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [aiPartnerHint, setAiPartnerHint] = useState('');  // pre-fills partner search after AI Quick Fill
  const [aiTenantHint,  setAiTenantHint]  = useState('');  // pre-fills tenant search after AI Quick Fill

  const handleDelete = async () => {
    setDeleting(true);
    try { await quotesAPI.delete(id); toast.success('Quote deleted'); navigate('/quotes'); }
    catch { toast.error('Delete failed'); setDeleting(false); }
  };

  const handleDownloadPDF = async () => {
    if (!existingQuote?.pricing_snapshot) return toast.error('Save first');
    const tid = toast.loading('Generating PDF…');
    try {
      const token = localStorage.getItem('c4p_access_token');
      const res = await fetch(`${API_BASE}/quotes/${id}/pdf`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || `Error ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const rawName = existingQuote.quote_name || existingQuote.quote_number;
      const safeName = rawName.replace(/[\\/:*?"<>|·•]/g, '-').replace(/\s+-\s+/g, ' - ').trim();
      const a = document.createElement('a'); a.href = url; a.download = `${safeName}.pdf`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a); URL.revokeObjectURL(url);
      toast.dismiss(tid); toast.success('PDF downloaded');
    } catch (err) { toast.dismiss(tid); toast.error(err.message || 'Failed'); }
  };

  const handleAIParse = async () => {
    if (!aiText.trim()) return toast.error('Enter a description');
    setAiParsing(true); setAiParseResult(null);
    try {
      const { data: r } = await apiClient.post('/ai/parse-quote', { text: aiText });
      setAiParseResult(r);
      // Auto-apply immediately - "Parse & Fill" means parse AND fill in one click
      applyAIParseResult(r);
    }
    catch (err) { toast.error(extractErrorMessage(err, 'AI parsing failed')); }
    finally { setAiParsing(false); }
  };

  // Accepts the parse result directly so it works both on auto-apply and manual re-apply
  const applyAIParseResult = (r) => {
    if (!r || !pricingConfig) return;
    const updates = { product_code: r.product_code || data.product_code };
    if (r.num_users) updates.num_users = String(r.num_users);
    if (r.term_months) updates.term_months = r.term_months;
    // Region: match by aws_region_code - also auto-sets currency to region default
    if (r.region_code) {
      const matched = (pricingConfig.regions || []).find(reg => reg.aws_region_code === r.region_code);
      if (matched) {
        updates.region_id   = matched.id;
        updates.region_name = matched.name; updates.region_aws_code = matched.aws_region_code || '';
        // Explicit currency in the prompt overrides region default; otherwise use region default
        updates.currency = r.currency || matched.default_currency;
      }
    }
    // If no region was matched but AI gave an explicit currency, apply it
    if (!updates.region_id && r.currency) updates.currency = r.currency;
    if (r.db_type) updates.db_type = r.db_type;
    if (r.db_upgrade_gb && r.db_type) {
      const t = r.db_type === 'HANA' ? 'HANADB' : 'SQLDB';
      const match = (pricingConfig.server_catalog || []).find(s => s.server_type === t && s.memory_gb === r.db_upgrade_gb);
      if (match) updates.db_server_code = match.code;
    }
    if (r.extras?.length) {
      const catalog = pricingConfig.extras_catalog || pricingConfig.extras || [];
      const newExtras = r.extras
        .filter(ex => !isVpcExtra({ code: ex.code, name: ex.code })) // VPC is auto-managed - never apply via AI
        .map(ex => {
          const cat = catalog.find(e => e.code === ex.code);
          return cat ? { extra_id: cat.id, extra_code: cat.code, extra_name: cat.name, quantity: ex.quantity || 1, unit: cat.unit, pricing_type: cat.pricing_type } : null;
        }).filter(Boolean);
      if (newExtras.length) updates.extras = newExtras;
    }
    setData(prev => ({ ...prev, ...updates }));
    setAiOpen(false);
    // Pre-fill partner/tenant search if AI detected names
    if (r.suggested_partner_name) setAiPartnerHint(r.suggested_partner_name);
    if (r.suggested_tenant_name)  setAiTenantHint(r.suggested_tenant_name);
    // Navigate: jump to first step that still needs attention
    const _FIELD_STEPS = { partner_customer: 1, region: 2, num_users: 2, db_type: 2, db_server: 4, app_servers: 5, extras: 6 };
    const _missingSteps = Object.keys(r.missing_fields || {})
      .filter(f => !(f === 'partner_customer' && data.customer_id))  // skip partner if already selected
      .map(f => _FIELD_STEPS[f]).filter(Boolean);
    setStep(_missingSteps.length ? Math.min(..._missingSteps) : (data.customer_id ? 7 : 1));
    const pct = Math.round((r.confidence || 0) * 100);
    const missingCount = Object.keys(r.missing_fields || {}).length;
    toast.success(`Form updated · ${pct}% confidence · ${missingCount} step${missingCount !== 1 ? 's' : ''} to complete`);
  };

  // Keep legacy applyAIParse for any other call-sites (uses current state)
  const applyAIParse = () => applyAIParseResult(aiParseResult);

  const handleAISummarise = async () => {
    if (!existingQuote?.pricing_snapshot) return toast.error('Save first');
    setAiSummarising(true);
    try { const { data: r } = await apiClient.post('/ai/summarise-quote', { quote_id: id }); setSummaryText(r.summary); setSummaryDialog(true); }
    catch (err) { toast.error(extractErrorMessage(err, 'AI summary failed')); }
    finally { setAiSummarising(false); }
  };

  const handleAIRecommend = async () => {
    if (!data.num_users || !data.product_code) return;
    setAiRecommending(true);
    try {
      const { data: r } = await apiClient.post('/ai/recommend-extras', {
        product_code: data.product_code, num_users: parseInt(data.num_users),
        currency: data.currency, current_extra_codes: data.extras.map(e => e.extra_code),
      });
      setRecommendations(r.recommendations || []);
    } catch { }
    finally { setAiRecommending(false); }
  };

  if (loadingData) return (
    <div className="max-w-6xl mx-auto space-y-4">
      <Skeleton className="h-12 w-full" />
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-4"><Skeleton className="h-64" /><Skeleton className="h-48" /></div>
        <Skeleton className="h-96" />
      </div>
    </div>
  );
  if (loadError) return <Alert variant="destructive"><AlertCircle className="h-4 w-4" /><AlertDescription>{loadError}</AlertDescription></Alert>;

  return (
    <div className="flex flex-col min-h-full" style={{ background: '#f8fafc' }} data-testid="quote-wizard-page">

      {/* ── Sticky Header ── */}
      <div className="bg-white flex-shrink-0" style={{ borderBottom: '1px solid #e2e8f0' }}>

        {/* Title row */}
        <div className="px-8 pt-5 pb-3 flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            <Link to={isEditing ? `/quotes/${id}` : '/quotes'}>
              <Button variant="ghost" size="sm"><ArrowLeft size={15} /></Button>
            </Link>
            <div>
              <h2 className="text-xl font-bold" style={{ fontFamily: 'var(--font-display)', color: NAVY }}>
                {isEditing && existingQuote?.contract_id
                  ? `Change Request — ${existingQuote.contract_number_ref || existingQuote.order_number_ref || existingQuote.quote_number}`
                  : isEditing ? `Edit ${existingQuote?.quote_number || ''}`
                  : 'New Quote'}
              </h2>
              {isEditing && existingQuote && (
                <p className="text-xs" style={{ color: 'hsl(var(--muted-fg))' }}>
                  {existingQuote.contract_id ? `${existingQuote.quote_number} · ` : ''}v{existingQuote.version} · {existingQuote.status}
                </p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {(!isReadOnly || isReassignOnlyMode) && isEditing && (
              <>
                <Button variant="outline" size="sm" onClick={() => navigate(`/quotes/${id}`)} className="gap-1.5">
                  Cancel
                </Button>
                <Button onClick={handleSave} disabled={saving} className="gap-2" style={{ background: NAVY, color: 'white' }} data-testid="quote-save-button">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save size={14} />}
                  {isReassignOnlyMode ? 'Save Reassignment' : 'Save & Return'}
                </Button>
              </>
            )}
            {!isReadOnly && !isEditing && step === 7 && !isManualContract && (
              <Button onClick={handleSave} disabled={saving} className="gap-2" style={{ background: NAVY, color: 'white' }} data-testid="quote-save-button">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save size={14} />}
                Save Quote
              </Button>
            )}
            {!isReadOnly && !isEditing && step === 8 && isManualContract && (
              <Button onClick={handleSave} disabled={saving} className="gap-2" style={{ background: 'hsl(var(--c4p-orange-600))', color: 'white' }} data-testid="quote-save-manual-button">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save size={14} />}
                Save Manual Quote
              </Button>
            )}
          </div>
        </div>

        {/* Step progress bar — part of the header */}
        <div className="px-8 pb-4 flex items-center overflow-x-auto gap-0">
          {visibleSteps.map((s, idx, arr) => {
            const isCurrent = s.id === step;
            const isDone    = s.id < step;
            return (
              <React.Fragment key={s.id}>
                <button
                  onClick={() => setStep(s.id)}
                  className="flex items-center gap-1.5 flex-shrink-0 py-1 px-0.5"
                  title={s.label}
                >
                  <div
                    className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 transition-all duration-200"
                    style={
                      isDone    ? { background: 'hsl(var(--c4p-brand-blue))', color: 'white' } :
                      isCurrent ? { background: NAVY, color: 'white', boxShadow: '0 0 0 3px hsl(var(--c4p-brand-blue) / 0.3)' } :
                                  { background: '#e2e8f0', color: '#94a3b8' }
                    }
                  >
                    {isDone ? <CheckCircle2 size={11} /> : idx + 1}
                  </div>
                  {(isCurrent || isDone) && (
                    <span
                      className="hidden md:block text-xs font-semibold whitespace-nowrap"
                      style={{ color: isCurrent ? NAVY : 'hsl(var(--c4p-brand-blue))' }}
                    >
                      {s.label}
                    </span>
                  )}
                </button>
                {idx < arr.length - 1 && (
                  <div
                    className="flex-1 h-0.5 mx-1 min-w-[12px] rounded-full transition-all duration-300"
                    style={{ background: isDone ? 'hsl(var(--c4p-brand-blue))' : '#e2e8f0' }}
                  />
                )}
              </React.Fragment>
            );
          })}
        </div>
      </div>

      {/* ── Content area ── */}
      <div className="flex-1 overflow-auto px-8 py-6">
        <div className="max-w-[1400px] mx-auto grid grid-cols-1 lg:grid-cols-6 gap-5 items-start">
          {/* Form area */}
          <div className="lg:col-span-4 space-y-3">

            {/* Change Request context banner */}
            {isEditing && existingQuote?.contract_id && (
              <div className="rounded-xl px-4 py-3 flex items-start gap-3" data-testid="wizard-cr-banner"
                style={{ background: '#eff6ff', border: '1.5px solid #bfdbfe' }}>
                <FileSignature size={15} style={{ color: '#1d4ed8', flexShrink: 0, marginTop: 1 }} />
                <div>
                  <p className="text-sm font-semibold" style={{ color: '#1e40af' }}>
                    Change Request —{' '}
                    <span style={{ fontFamily: 'var(--font-mono, monospace)', fontSize: '13px' }}>
                      {existingQuote.contract_number_ref || existingQuote.order_number_ref || 'Contract'}
                    </span>
                  </p>
                  <p className="text-xs mt-0.5" style={{ color: '#1e3a8a' }}>
                    You are modifying a live contract. Make your changes, then{isManualContract ? ' review pricing on Step 8 and' : ''} send for signing. The contract will update automatically once signed.
                  </p>
                </div>
              </div>
            )}

            {/* Reassign-only mode banner (Super Admin, Accepted/Expired quote) */}
            {isReassignOnlyMode && (
              <div className="rounded-xl px-4 py-3 flex items-start gap-3" data-testid="wizard-reassign-only-banner"
                style={{ background: '#fffbeb', border: '1.5px solid #fde68a' }}>
                <AlertCircle size={15} style={{ color: '#b45309', flexShrink: 0, marginTop: 1 }} />
                <div>
                  <p className="text-sm font-semibold" style={{ color: '#92400e' }}>
                    {existingQuote?.status} quote — PSM reassignment only
                  </p>
                  <p className="text-xs mt-0.5" style={{ color: '#78350f' }}>
                    This quote is {existingQuote?.status === 'ACCEPTED' ? 'a live contract' : 'expired'} and locked for editing.
                    As a Super Admin, you can still change the Partner Success Manager on Step 2 — doing so transfers ownership
                    {existingQuote?.variant_group_id ? ' of this quote and every variant in its group' : ''} to the new PSM.
                  </p>
                </div>
              </div>
            )}

            {/* AI Quick Fill banner - Step 1 only */}
            {step === 1 && !isReadOnly && (
              <button
                onClick={() => setAiOpen(true)}
                className="w-full flex items-center gap-3 px-4 py-2.5 rounded-xl text-left transition-colors"
                style={{
                  background: aiParseResult ? 'rgba(22,163,74,0.05)' : 'rgba(251,191,36,0.07)',
                  border: '1px solid',
                  borderColor: aiParseResult ? '#bbf7d0' : 'rgba(251,191,36,0.35)',
                  borderLeft: `3px solid ${aiParseResult ? '#16a34a' : ORANGE}`,
                }}
                data-testid="ai-quickfill-banner"
              >
                {aiParseResult
                  ? <CheckCircle2 size={15} style={{ color: '#16a34a', flexShrink: 0 }} />
                  : <Sparkles    size={15} style={{ color: ORANGE,    flexShrink: 0 }} />}
                <div className="flex-1 min-w-0">
                  {aiParseResult ? (
                    <>
                      <p className="text-xs font-semibold" style={{ color: '#15803d' }}>AI Quick Fill applied</p>
                      <p className="text-xs truncate" style={{ color: 'hsl(var(--muted-fg))' }}>
                        {aiParseResult.interpretation?.slice(0, 80)}{aiParseResult.interpretation?.length > 80 ? '…' : ''}
                        {Object.keys(aiParseResult.missing_fields || {}).length > 0 &&
                          ` · ${Object.keys(aiParseResult.missing_fields).length} step${Object.keys(aiParseResult.missing_fields).length > 1 ? 's' : ''} still needed`}
                      </p>
                    </>
                  ) : (
                    <>
                      <p className="text-xs font-semibold" style={{ color: NAVY }}>AI Quick Fill</p>
                      <p className="text-xs" style={{ color: 'hsl(var(--muted-fg))' }}>
                        Describe the quote in plain English to fill all steps automatically
                      </p>
                    </>
                  )}
                </div>
                <span className="text-xs font-semibold flex-shrink-0"
                  style={{ color: aiParseResult ? '#16a34a' : ORANGE }}>
                  {aiParseResult ? 'Re-run →' : 'Try it →'}
                </span>
              </button>
            )}

            <StepContent
              step={step} data={data} set={set} customers={customers}
              tenants={tenants}
              psmUsers={psmUsers}
              pricingConfig={pricingConfig} dbServers={dbServers} currentDbServer={currentDbServer}
              appServerCatalog={appServerCatalog} availableExtras={availableExtras}
              pricingResult={pricingResult} pricingError={pricingError}
              effectivePricingResult={effectivePricingResult}
              isAdvanced={isAdvanced} isStandalone={isStandalone} isReadOnly={isReadOnly} existingQuote={existingQuote}
              isSuperAdmin={isSuperAdmin} isReassignOnlyMode={isReassignOnlyMode}
              onSave={handleSave} saving={saving} onAIRecommend={handleAIRecommend}
              aiRecommending={aiRecommending} recommendations={recommendations}
              aiPartnerHint={aiPartnerHint} aiTenantHint={aiTenantHint}
              appServerPurposes={appServerPurposes}
              manualOverrides={manualOverrides} setManualOverrides={setManualOverrides}
              onCustomersRefresh={async () => { const r = await customersAPI.list({ page_size: 500, status: 'active' }); setCustomers(r.data.items || []); }}
              onTenantsRefresh={async () => { const { tenantsAPI } = await import('../../services/api'); const r = await tenantsAPI.list({ page_size: 500, status: 'active' }); setTenants(r.data.items || []); }}
            />
            {/* Step navigation */}
            <div className="flex items-center justify-between mt-5">
              <Button variant="outline" onClick={prevStep} disabled={step === 1} className="gap-2 font-semibold">
                <ArrowLeft size={14} /> Back
              </Button>
              {step < visibleSteps[visibleSteps.length - 1]?.id ? (
                <Button onClick={nextStep} disabled={!canProceed} className="gap-2 font-bold"
                  style={{ background: 'hsl(var(--c4p-brand-gold))', color: '#1a1a1a', paddingLeft: '20px', paddingRight: '20px' }}
                  data-testid="wizard-next-button">
                  Next <ArrowRight size={14} />
                </Button>
              ) : (!isReadOnly || isReassignOnlyMode) ? (
                <Button onClick={handleSave} disabled={saving || (!isReassignOnlyMode && (!data.customer_id || (!data.is_standalone && !data.num_users)))}
                  className="gap-2 font-bold"
                  style={{ background: 'hsl(var(--c4p-brand-gold))', color: '#1a1a1a', paddingLeft: '20px', paddingRight: '20px' }}
                  data-testid="quote-save-button">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save size={14} />}
                  {isReassignOnlyMode ? 'Save Reassignment' : isEditing ? 'Save & Return' : (isManualContract ? 'Save Manual Quote' : 'Save Quote')}
                </Button>
              ) : null}
            </div>
          </div>

          {/* Right panel: Quote Summary - sticky */}
          <div className="lg:col-span-2 lg:sticky lg:top-0 space-y-4">
            <MiniSummary r={effectivePricingResult} err={pricingError} calculating={calculating}
              data={data} customer={selectedCustomer} existingQuote={existingQuote} />
          </div>
        </div>
      </div>

      {/* AI Drawer */}
      <Sheet open={aiOpen} onOpenChange={setAiOpen}>
        <SheetContent side="right" className="w-[460px] overflow-y-auto">
          <SheetHeader className="mb-4">
            <SheetTitle className="flex items-center gap-2" style={{ fontFamily: 'var(--font-display)', color: NAVY }}>
              <Sparkles size={18} style={{ color: ORANGE }} /> AI Quick Fill
            </SheetTitle>
            <p className="text-sm" style={{ color: 'hsl(var(--muted-fg))' }}>
              Describe the quote in plain English - product, users, region, database, term, extras - and the AI fills all the steps for you.
            </p>
          </SheetHeader>

          <div className="space-y-4">
            {/* Input */}
            <div className="space-y-2">
              <Label>Describe the quote</Label>
              <Textarea value={aiText} onChange={e => setAiText(e.target.value)}
                placeholder={`e.g. "45 users C4P Advanced HANA 256GB in Ireland EUR 36 months, 2 VPNs for Ascarii"`}
                className="h-24 resize-none text-sm" data-testid="ai-text-input" />
            </div>

            {/* Clickable examples */}
            <div className="space-y-2">
              <p className="text-xs font-semibold uppercase tracking-wide" style={{ color: 'hsl(var(--muted-fg))' }}>Examples - click to pre-fill, then edit</p>
              <div className="space-y-1.5">
                {[
                  { label: 'Standard · HANA · UK',    text: '12 users C4P Standard HANA, UK London, GBP, 12 months' },
                  { label: 'Standard · SQL · Canada',  text: '8 users C4P Standard SQL, Canada, CAD, 24 months' },
                  { label: 'Advanced · HANA · Ireland',text: '45 users C4P Advanced HANA 256GB, Ireland, EUR, 36 months, 1 VPN' },
                  { label: 'Advanced · SQL · Germany', text: '30 users C4P Advanced SQL 64GB, Frankfurt, EUR, 24 months' },
                  { label: 'Advanced · HANA · Sydney', text: '60 users C4P Advanced HANA, Sydney, AUD, 36 months, 2 VPNs' },
                  { label: 'MTPro · HANA · UAE',       text: '80 users C4P MTPro HANA 256GB, UAE, USD, 36 months, 1 VPN' },
                ].map(ex => (
                  <button key={ex.label} onClick={() => setAiText(ex.text)}
                    className="w-full text-left rounded-lg px-3 py-2 transition-colors text-xs"
                    style={{ background: 'hsl(var(--muted))', color: 'hsl(var(--foreground))', border: '1px solid hsl(var(--border))' }}
                    onMouseEnter={e => e.currentTarget.style.background = 'hsl(var(--muted) / 0.7)'}
                    onMouseLeave={e => e.currentTarget.style.background = 'hsl(var(--muted))'}
                  >
                    <span className="font-semibold" style={{ color: NAVY }}>{ex.label}</span>
                    <span style={{ color: 'hsl(var(--muted-fg))' }}> · {ex.text}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Parse button */}
            <Button onClick={handleAIParse} disabled={aiParsing || !aiText.trim()} className="w-full gap-2"
              style={{ background: NAVY, color: 'white' }} data-testid="ai-parse-button">
              {aiParsing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wand2 size={14} />}
              {aiParsing ? 'Parsing & Filling…' : 'Parse & Fill Quote'}
            </Button>

            {/* Result */}
            {aiParseResult && (
              <div className="rounded-xl border space-y-0 overflow-hidden">
                {/* Header */}
                <div className="flex items-center gap-2 px-4 py-3" style={{ background: '#f0fdf4', borderBottom: '1px solid #bbf7d0' }}>
                  <CheckCircle2 size={14} style={{ color: '#16a34a' }} />
                  <p className="text-sm font-semibold" style={{ color: '#15803d' }}>Applied to form</p>
                  <span className="ml-auto text-xs px-2 py-0.5 rounded-full font-medium"
                    style={{ background: '#dcfce7', color: '#166534' }}>
                    {Math.round((aiParseResult.confidence || 0) * 100)}% confidence
                  </span>
                </div>

                {/* Interpretation */}
                <div className="px-4 py-3" style={{ borderBottom: '1px solid hsl(var(--border))' }}>
                  <p className="text-xs italic" style={{ color: 'hsl(var(--muted-fg))' }}>{aiParseResult.interpretation}</p>
                </div>

                {/* What was filled */}
                <div className="px-4 py-3 space-y-1.5" style={{ borderBottom: '1px solid hsl(var(--border))' }}>
                  <p className="text-xs font-semibold uppercase tracking-wide mb-2" style={{ color: '#15803d' }}>Filled in</p>
                  <div className="grid grid-cols-2 gap-x-4 gap-y-1">
                    {[
                      ['Product',  aiParseResult.product_code ? ({'STANDARD':'C4P Standard','ADVANCED':'C4P Advanced','MTPRO':'C4P MTPro'}[aiParseResult.product_code] || aiParseResult.product_code) : null],
                      ['Users',    aiParseResult.num_users],
                      ['Term',     aiParseResult.term_months ? `${aiParseResult.term_months} months` : null],
                      ['Currency', aiParseResult.currency],
                      ['Region',   aiParseResult.region_code ? ((pricingConfig.regions || []).find(r => r.aws_region_code === aiParseResult.region_code)?.name || aiParseResult.region_code) : null],
                      ['Database', aiParseResult.db_type],
                      ['DB size',  aiParseResult.db_upgrade_gb ? `${aiParseResult.db_upgrade_gb}GB upgrade` : null],
                    ].filter(([, v]) => v).map(([l, v]) => (
                      <div key={l} className="flex items-center gap-1.5 text-xs">
                        <CheckCircle2 size={10} style={{ color: '#16a34a', flexShrink: 0 }} />
                        <span style={{ color: 'hsl(var(--muted-fg))' }}>{l}:</span>
                        <span className="font-semibold whitespace-nowrap">{v}</span>
                      </div>
                    ))}
                    {aiParseResult.extras?.length > 0 && (
                      <div className="col-span-2 flex items-center gap-1.5 text-xs">
                        <CheckCircle2 size={10} style={{ color: '#16a34a', flexShrink: 0 }} />
                        <span style={{ color: 'hsl(var(--muted-fg))' }}>Extras:</span>
                        <span className="font-semibold">{aiParseResult.extras.map(e => `${e.quantity}× ${e.code}`).join(', ')}</span>
                      </div>
                    )}
                  </div>
                  {/* Suggested names */}
                  {(aiParseResult.suggested_partner_name || aiParseResult.suggested_tenant_name) && (
                    <div className="mt-2 rounded-lg px-3 py-2 text-xs" style={{ background: '#eff6ff', border: '1px solid #bfdbfe' }}>
                      {aiParseResult.suggested_partner_name && (
                        <p><span style={{ color: '#1d4ed8' }}>Partner name mentioned:</span> <strong>{aiParseResult.suggested_partner_name}</strong> - search for this on step 1</p>
                      )}
                      {aiParseResult.suggested_tenant_name && (
                        <p><span style={{ color: '#1d4ed8' }}>Tenant mentioned:</span> <strong>{aiParseResult.suggested_tenant_name}</strong> - select on step 1</p>
                      )}
                    </div>
                  )}
                </div>

                {/* What still needs to be done */}
                {Object.keys(aiParseResult.missing_fields || {}).filter(f => !(f === 'partner_customer' && data.customer_id)).length > 0 && (
                  <div className="px-4 py-3 space-y-1.5">
                    {(() => {
                      const FIELD_STEPS = { partner_customer: 1, region: 2, num_users: 2, db_type: 2, db_server: 4, app_servers: 5, extras: 6 };
                      return (
                        <>
                          <p className="text-xs font-semibold uppercase tracking-wide mb-2" style={{ color: '#b45309' }}>Still needed</p>
                          {Object.entries(aiParseResult.missing_fields || {})
                            .filter(([f]) => !(f === 'partner_customer' && data.customer_id))
                            .map(([field, msg]) => {
                              const stepNum = FIELD_STEPS[field];
                              return (
                                <div key={field} className="flex items-start justify-between gap-2 text-xs">
                                  <div className="flex items-start gap-1.5 flex-1 min-w-0">
                                    <AlertTriangle size={10} style={{ color: '#d97706', flexShrink: 0, marginTop: 2 }} />
                                    <span style={{ color: 'hsl(var(--foreground))' }}>{msg}</span>
                                  </div>
                                  {stepNum && (
                                    <button
                                      onClick={() => { setAiOpen(false); setStep(stepNum); }}
                                      className="shrink-0 font-semibold hover:underline"
                                      style={{ color: '#d97706' }}
                                    >
                                      Step {stepNum} →
                                    </button>
                                  )}
                                </div>
                              );
                            })}
                        </>
                      );
                    })()}
                  </div>
                )}

                {/* Warnings / assumptions */}
                {aiParseResult.warnings?.length > 0 && (
                  <div className="px-4 py-2 space-y-1" style={{ background: '#fefce8', borderTop: '1px solid hsl(var(--border))' }}>
                    {aiParseResult.warnings.map((w, i) => (
                      <p key={i} className="flex items-start gap-1.5 text-xs" style={{ color: '#92400e' }}>
                        <Info size={10} style={{ flexShrink: 0, marginTop: 2 }} />
                        {w}
                      </p>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </SheetContent>
      </Sheet>

      {/* Delete Quote Confirmation */}
      <Dialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Trash2 size={16} style={{ color: 'hsl(var(--danger))' }} /> Delete Quote
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm" style={{ color: 'hsl(var(--muted-fg))' }}>
            Are you sure you want to delete <strong>{existingQuote?.quote_number}</strong>? This action cannot be undone.
          </p>
          <DialogFooter className="mt-2">
            <Button variant="outline" onClick={() => setShowDeleteConfirm(false)}>Cancel</Button>
            <Button onClick={handleDelete} disabled={deleting} style={{ background: 'hsl(var(--danger))', color: 'white' }}>
              {deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Delete Quote'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Summary Dialog */}
      <Dialog open={summaryDialog} onOpenChange={setSummaryDialog}>
        <DialogContent className="max-w-xl">
          <DialogHeader><DialogTitle style={{ fontFamily: 'var(--font-display)' }}>Quote Summary</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="rounded-lg p-4 text-sm leading-relaxed whitespace-pre-wrap" style={{ background: 'hsl(var(--muted))' }}>{summaryText}</div>
            <Button variant="outline" className="w-full gap-2" onClick={() => { navigator.clipboard.writeText(summaryText); toast.success('Copied'); }} data-testid="copy-summary-button">
              <ClipboardCopy size={13} /> Copy to Clipboard
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ── Step content dispatcher ────────────────────────────────────────────────────
function StepContent(props) {
  const { step } = props;
  if (step === 1) return <Step1SelectPartner {...props} Card={WizardCard} />;
  if (step === 2) return <Step2Setup         {...props} Card={WizardCard} />;
  if (step === 3) return <Step3Product       {...props} Card={WizardCard} />;
  if (step === 4) return <Step4Database      {...props} Card={WizardCard} />;
  if (step === 5) return <Step5Servers       {...props} Card={WizardCard} />;
  if (step === 6) return <Step6Extras        {...props} Card={WizardCard} />;
  if (step === 7) return <Step7Review {...props} pricingResult={props.effectivePricingResult || props.pricingResult} Card={WizardCard} />;
  if (step === 8) return (
    <Step8ManualPricing
      data={props.data}
      pricingResult={props.pricingResult}
      manualOverrides={props.manualOverrides}
      setManualOverrides={props.setManualOverrides}
      Card={WizardCard}
    />
  );
  return null;
}

// ── FilterableSelect - Select with injected search, reliable Radix portal ────
// Uses Shadcn Select + SelectContent which handles all z-index/portal correctly.
// A search Input is injected at the top of SelectContent using stopPropagation.
function FilterableSelect({ items, value, onChange, placeholder, getLabel, getSubLabel, disabled, testId }) {
  const [search, setSearch] = React.useState('');
  const filtered = search
    ? items.filter(i => getLabel(i).toLowerCase().includes(search.toLowerCase()))
    : items;
  const selected = items.find(i => i.id === value);

  return (
    <Select
      value={value || '__none__'}
      onValueChange={v => { onChange(v === '__none__' ? '' : v); setSearch(''); }}
      disabled={disabled}
    >
      <SelectTrigger data-testid={testId} className="h-10">
        {selected ? (
          <div className="flex flex-col items-start min-w-0 overflow-hidden text-left">
            <span className="text-sm font-semibold truncate w-full leading-tight">{getLabel(selected)}</span>
          </div>
        ) : (
          <SelectValue placeholder={placeholder} />
        )}
      </SelectTrigger>
      <SelectContent>
        {/* Search input at the top - stopPropagation prevents Select from handling keystrokes */}
        <div className="px-2 py-1.5 sticky top-0 bg-white z-10" style={{ borderBottom: '1px solid hsl(var(--border))' }}>
          <div className="relative">
            <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none" style={{ color: 'hsl(var(--muted-fg))' }} />
            <input
              className="w-full rounded-md border text-sm pl-7 pr-2 py-1.5 outline-none"
              style={{ borderColor: 'hsl(var(--border))' }}
              placeholder="Type to filter..."
              value={search}
              autoComplete="off"
              onChange={e => setSearch(e.target.value)}
              onKeyDown={e => e.stopPropagation()}
              onClick={e => e.stopPropagation()}
            />
          </div>
        </div>
        {/* Clear option */}
        <SelectItem value="__none__">
          <span style={{ color: 'hsl(var(--muted-fg))' }}>{placeholder}</span>
        </SelectItem>
        {filtered.length === 0 && (
          <div className="px-3 py-4 text-sm text-center" style={{ color: 'hsl(var(--muted-fg))' }}>
            No results for &ldquo;{search}&rdquo;
          </div>
        )}
        {filtered.slice(0, 80).map(item => (
          <SelectItem key={item.id} value={item.id}>
            <div>
              <div className="font-medium text-sm">{getLabel(item)}</div>
              {getSubLabel && <div className="text-xs" style={{ color: 'hsl(var(--muted-fg))' }}>{getSubLabel(item)}</div>}
            </div>
          </SelectItem>
        ))}
        {search === '' && items.length > 80 && (
          <div className="px-3 py-1.5 text-xs text-center" style={{ color: 'hsl(var(--muted-fg))', borderTop: '1px solid hsl(var(--border))' }}>
            {items.length - 80} more - type to filter
          </div>
        )}
      </SelectContent>
    </Select>
  );
}

// ── Quick Create Sheets - full fields, all mandatory ─────────────────────────
const COUNTRIES   = ['United Kingdom', 'Germany', 'France', 'Netherlands', 'Spain', 'Italy', 'United States', 'Canada', 'Australia', 'UAE', 'South Africa', 'Other'];
const PARTNER_TYPES = ['SAP Partner', 'VAR', 'ISV', 'MSP', 'System Integrator', 'Reseller', 'Distributor', 'Other'];

function RequiredLabel({ children }) {
  return (
    <Label className="text-sm font-medium">
      {children} <span style={{ color: 'hsl(var(--danger))' }}>*</span>
    </Label>
  );
}

function QuickCreatePartnerSheet({ open, onClose, regions, psmUsers, onCreated }) {
  const emptyForm = { company_name: '', partner_type: '', website: '', billing_address: '', country: '', default_currency: '', default_region_id: '', default_region_name: '', default_psm_id: '', default_psm_name: '', notes: '' };
  const [form, setForm]   = React.useState(emptyForm);
  const [errors, setErrors] = React.useState({});
  const [saving, setSaving] = React.useState(false);

  const required = ['company_name', 'partner_type', 'country', 'default_currency', 'default_region_id', 'billing_address'];

  const validate = () => {
    const e = {};
    required.forEach(k => { if (!form[k]?.trim()) e[k] = 'Required'; });
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleRegionChange = (id) => {
    const r = regions.find(r => r.id === id);
    setForm(f => ({ ...f, default_region_id: id, default_region_name: r?.name || '', default_currency: r?.default_currency || f.default_currency }));
  };

  const handlePsmChange = (id) => {
    const u = psmUsers.find(u => u.id === id);
    setForm(f => ({ ...f, default_psm_id: id, default_psm_name: u?.name || '' }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!validate()) { toast.error('Please fill in all required fields'); return; }
    setSaving(true);
    try {
      const { data: newPartner } = await customersAPI.create(form);
      toast.success(`Partner "${form.company_name}" created`);
      onCreated(newPartner);
      onClose();
      setForm(emptyForm);
      setErrors({});
    } catch (ex) { toast.error(ex.response?.data?.detail || 'Failed to create partner'); }
    finally { setSaving(false); }
  };

  const Err = ({ field }) => errors[field] ? <p className="text-xs mt-0.5" style={{ color: 'hsl(var(--danger))' }}>{errors[field]}</p> : null;

  return (
    <Sheet open={open} onOpenChange={v => { if (!v) { onClose(); setErrors({}); } }}>
      <SheetContent side="right" className="w-[480px] max-w-full overflow-y-auto">
        <SheetHeader className="mb-5">
          <SheetTitle style={{ fontFamily: 'var(--font-display)', color: NAVY }}>New Partner</SheetTitle>
          <p className="text-sm" style={{ color: 'hsl(var(--muted-fg))' }}>All fields marked * are required.</p>
        </SheetHeader>
        <form onSubmit={handleSave} className="space-y-4">

          <div className="space-y-1.5">
            <RequiredLabel>Company Name</RequiredLabel>
            <Input value={form.company_name} onChange={e => setForm(f => ({ ...f, company_name: e.target.value }))}
              placeholder="e.g. Acme Solutions Ltd" />
            <Err field="company_name" />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <RequiredLabel>Partner Type</RequiredLabel>
              <Select value={form.partner_type} onValueChange={v => setForm(f => ({ ...f, partner_type: v }))}>
                <SelectTrigger><SelectValue placeholder="Select type..." /></SelectTrigger>
                <SelectContent>{PARTNER_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
              <Err field="partner_type" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-sm font-medium">Website</Label>
              <Input value={form.website} onChange={e => setForm(f => ({ ...f, website: e.target.value }))} placeholder="https://" />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <RequiredLabel>Country</RequiredLabel>
              <CountrySelect value={form.country} onChange={v => setForm(f => ({ ...f, country: v }))} testId="create-partner-country" />
              <Err field="country" />
            </div>
            <div className="space-y-1.5">
              <RequiredLabel>Default Currency</RequiredLabel>
              <Select value={form.default_currency} onValueChange={v => setForm(f => ({ ...f, default_currency: v }))}>
                <SelectTrigger><SelectValue placeholder="Select..." /></SelectTrigger>
                <SelectContent>{CURRENCIES.map(c => <SelectItem key={c.code} value={c.code}>{c.code}</SelectItem>)}</SelectContent>
              </Select>
              <Err field="default_currency" />
            </div>
          </div>

          <div className="space-y-1.5">
            <RequiredLabel>Default AWS Region</RequiredLabel>
            <Select value={form.default_region_id} onValueChange={handleRegionChange}>
              <SelectTrigger><SelectValue placeholder="Select hosting region..." /></SelectTrigger>
              <SelectContent>
                {regions.map(r => <SelectItem key={r.id} value={r.id}>{r.name} · {r.aws_region_code} · {r.default_currency}</SelectItem>)}
              </SelectContent>
            </Select>
            <Err field="default_region_id" />
          </div>

          <div className="space-y-1.5">
            <Label className="text-sm font-medium">Default PSM</Label>
            <Select value={form.default_psm_id || 'none'} onValueChange={v => handlePsmChange(v === 'none' ? '' : v)}>
              <SelectTrigger><SelectValue placeholder="Assign a PSM..." /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">- No default PSM -</SelectItem>
                {psmUsers.map(u => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <RequiredLabel>Billing Address</RequiredLabel>
            <Textarea value={form.billing_address} onChange={e => setForm(f => ({ ...f, billing_address: e.target.value }))}
              className="h-20 resize-none" placeholder="Full billing address for invoices..." />
            <Err field="billing_address" />
          </div>

          <div className="space-y-1.5">
            <Label className="text-sm font-medium">Notes</Label>
            <Textarea value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
              className="h-16 resize-none" placeholder="Internal notes about this partner..." />
          </div>

          <Separator />
          <Button type="submit" disabled={saving} className="w-full gap-2" style={{ background: NAVY, color: 'white' }}>
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><CheckCircle2 size={14} /> Create Partner</>}
          </Button>
        </form>
      </SheetContent>
    </Sheet>
  );
}



function QuickCreateTenantSheet({ open, onClose, partnerId, partnerName, onCreated }) {
  const emptyForm = { company_name: '', contact_name: '', email: '', phone_mobile: '', phone_landline: '', billing_address: '', country: '', default_currency: '', notes: '' };
  const [form, setForm]     = React.useState(emptyForm);
  const [errors, setErrors] = React.useState({});
  const [saving, setSaving] = React.useState(false);

  const required = ['company_name'];

  const validate = () => {
    const e = {};
    if (!form.company_name?.trim()) e.company_name = 'Required';
    if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = 'Invalid email address';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!validate()) { toast.error('Please fill in all required fields'); return; }
    setSaving(true);
    try {
      const { tenantsAPI } = await import('../../services/api');
      const { data: newTenant } = await tenantsAPI.create({ ...form, partner_id: partnerId || null, partner_name: partnerName || null });
      toast.success(`Tenant "${form.company_name}" created`);
      onCreated(newTenant);
      onClose();
      setForm(emptyForm);
      setErrors({});
    } catch (ex) { toast.error(ex.response?.data?.detail || 'Failed to create tenant'); }
    finally { setSaving(false); }
  };

  const Err = ({ field }) => errors[field] ? <p className="text-xs mt-0.5" style={{ color: 'hsl(var(--danger))' }}>{errors[field]}</p> : null;

  return (
    <Sheet open={open} onOpenChange={v => { if (!v) { onClose(); setErrors({}); } }}>
      <SheetContent side="right" className="w-[480px] max-w-full overflow-y-auto">
        <SheetHeader className="mb-5">
          <SheetTitle style={{ fontFamily: 'var(--font-display)', color: NAVY }}>New Tenant</SheetTitle>
          <p className="text-sm" style={{ color: 'hsl(var(--muted-fg))' }}>
            Linked to partner: <strong>{partnerName}</strong>
          </p>
        </SheetHeader>
        <form onSubmit={handleSave} className="space-y-4">
          <div className="space-y-1.5">
            <RequiredLabel>Company / Organisation Name</RequiredLabel>
            <Input value={form.company_name} onChange={e => setForm(f => ({ ...f, company_name: e.target.value }))} placeholder="e.g. Acme Corporation Ltd" />
            <Err field="company_name" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-sm font-medium">Primary Contact Name</Label>
            <Input value={form.contact_name} onChange={e => setForm(f => ({ ...f, contact_name: e.target.value }))} placeholder="e.g. John Smith" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-sm font-medium">Email Address</Label>
            <Input type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} placeholder="contact@company.com" />
            <Err field="email" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-sm font-medium">Mobile</Label>
              <Input value={form.phone_mobile} onChange={e => setForm(f => ({ ...f, phone_mobile: e.target.value }))} placeholder="+44 7700 000000" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-sm font-medium">Landline</Label>
              <Input value={form.phone_landline} onChange={e => setForm(f => ({ ...f, phone_landline: e.target.value }))} placeholder="+44 20 0000 0000" />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-sm font-medium">Country</Label>
              <CountrySelect value={form.country} onChange={v => setForm(f => ({ ...f, country: v }))} testId="create-tenant-country" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-sm font-medium">Currency</Label>
              <Select value={form.default_currency} onValueChange={v => setForm(f => ({ ...f, default_currency: v }))}>
                <SelectTrigger><SelectValue placeholder="Select..." /></SelectTrigger>
                <SelectContent>{CURRENCIES.map(c => <SelectItem key={c.code} value={c.code}>{c.code}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-sm font-medium">Billing Address</Label>
            <Textarea value={form.billing_address} onChange={e => setForm(f => ({ ...f, billing_address: e.target.value }))} className="h-20 resize-none" placeholder="Full billing address" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-sm font-medium">Notes</Label>
            <Textarea value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} className="h-16 resize-none" placeholder="Internal notes about this tenant..." />
          </div>
          <Separator />
          <Button type="submit" disabled={saving} className="w-full gap-2" style={{ background: 'hsl(265 89% 38%)', color: 'white' }}>
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><CheckCircle2 size={14} /> Create Tenant</>}
          </Button>
        </form>
      </SheetContent>
    </Sheet>
  );
}

// ── Step 1: Partner & Tenant Table Picker ───────────────────────────────────
function Step1SelectPartner({ data, set, customers, tenants, psmUsers, pricingConfig, isReadOnly, existingQuote, Card, onCustomersRefresh, onTenantsRefresh, aiPartnerHint, aiTenantHint }) {
  const navigate = useNavigate();
  const [partnerFilter, setPartnerFilter] = React.useState('');
  const [tenantFilter,  setTenantFilter]  = React.useState('');

  // When AI Quick Fill detects a partner/tenant name, pre-populate the search filters
  React.useEffect(() => {
    if (aiPartnerHint) setPartnerFilter(aiPartnerHint);
  }, [aiPartnerHint]);
  React.useEffect(() => {
    if (aiTenantHint) setTenantFilter(aiTenantHint);
  }, [aiTenantHint]);
  const [partnerPage,   setPartnerPage]   = React.useState(0);
  const [tenantPage,    setTenantPage]    = React.useState(0);
  // showQCPartner/Tenant kept for backward compat with Sheet usage, but now unused
  const [showQCPartner, setShowQCPartner] = React.useState(false); // eslint-disable-line no-unused-vars
  const [showQCTenant,  setShowQCTenant]  = React.useState(false); // eslint-disable-line no-unused-vars

  const ROWS = 3; // max rows per page
  const ROW_H = 56; // fixed px height per row - keeps table stable

  const selectedPartner = customers.find(c => c.id === data.customer_id);
  const selectedTenant  = tenants.find(t => t.id === data.tenant_id);

  // Partners - filter by text
  const filteredPartners = (partnerFilter
    ? customers.filter(c => c.company_name.toLowerCase().includes(partnerFilter.toLowerCase()) ||
        (c.country || '').toLowerCase().includes(partnerFilter.toLowerCase()))
    : customers
  ).slice().sort((a, b) => a.company_name.localeCompare(b.company_name));
  const partnerPageCount = Math.ceil(filteredPartners.length / ROWS);
  const partnerRows      = filteredPartners.slice(partnerPage * ROWS, (partnerPage + 1) * ROWS);

  // Tenants - when a partner is selected show ONLY their tenants; otherwise show all
  const scopedTenants = React.useMemo(() => {
    if (!data.customer_id) return tenants;
    return tenants.filter(t => t.partner_id === data.customer_id);
  }, [tenants, data.customer_id]);

  const filteredTenants = (tenantFilter
    ? scopedTenants.filter(t => t.company_name.toLowerCase().includes(tenantFilter.toLowerCase()) ||
        (t.email || '').toLowerCase().includes(tenantFilter.toLowerCase()))
    : scopedTenants
  ).slice().sort((a, b) => a.company_name.localeCompare(b.company_name));
  const tenantPageCount = Math.ceil(filteredTenants.length / ROWS);
  const tenantRows      = filteredTenants.slice(tenantPage * ROWS, (tenantPage + 1) * ROWS);

  // Reset pages when filter changes
  React.useEffect(() => { setPartnerPage(0); }, [partnerFilter]);
  React.useEffect(() => { setTenantPage(0);  }, [tenantFilter, data.customer_id]);

  const handlePartnerClick = (customerId) => {
    if (isReadOnly) return;
    if (data.customer_id === customerId) { set({ customer_id: '', tenant_id: '', tenant_name: '', end_customer_name: '', contact_ids: [] }); return; }
    const c = customers.find(x => x.id === customerId);
    const contacts = c?.contacts || [];
    const primary  = contacts.find(ct => ct.is_primary) || contacts[0];
    const updates  = { customer_id: customerId, tenant_id: '', tenant_name: '', end_customer_name: '',
                       contact_ids: primary ? [primary.id] : [] };
    if (c?.default_region_id && !data.region_id) {
      const region = (pricingConfig?.regions || []).find(r => r.id === c.default_region_id);
      if (region) { updates.region_id = region.id; updates.region_name = region.name; updates.region_aws_code = region.aws_region_code || ''; updates.currency = c.default_currency || region.default_currency; }
    }
    if (c?.default_psm_id) { updates.psm_user_id = c.default_psm_id; updates.psm_user_name = c.default_psm_name || ''; }
    if (c?.default_billing_frequency) updates.billing_frequency = c.default_billing_frequency;
    if (c?.default_payment_method)    updates.payment_method    = c.default_payment_method;
    set(updates);
    setTenantFilter('');
  };

  const handleTenantClick = (tenantId) => {
    if (isReadOnly) return;
    if (data.tenant_id === tenantId) { set({ tenant_id: '', tenant_name: '', end_customer_name: '' }); return; }
    const t = tenants.find(x => x.id === tenantId);
    const newTenantName = t?.company_name || '';
    const updates = { tenant_id: tenantId, tenant_name: newTenantName, end_customer_name: newTenantName };
    // When editing an existing quote: replace old tenant name in quote_name string
    if (existingQuote && data.tenant_name && newTenantName && data.quote_name && data.quote_name.includes(data.tenant_name)) {
      updates.quote_name = data.quote_name.split(data.tenant_name).join(newTenantName);
    }
    if (t?.partner_id && !data.customer_id) {
      const c = customers.find(x => x.id === t.partner_id);
      if (c) {
        const contacts = c?.contacts || [];
        const primary  = contacts.find(ct => ct.is_primary) || contacts[0];
        updates.customer_id  = c.id;
        updates.contact_ids  = primary ? [primary.id] : [];
        if (c.default_region_id && !data.region_id) {
          const region = (pricingConfig?.regions || []).find(r => r.id === c.default_region_id);
          if (region) { updates.region_id = region.id; updates.region_name = region.name; updates.region_aws_code = region.aws_region_code || ''; updates.currency = c.default_currency || region.default_currency; }
        }
        if (c.default_psm_id) { updates.psm_user_id = c.default_psm_id; updates.psm_user_name = c.default_psm_name || ''; }
      }
    }
    set(updates);
  };

  const isTenantDirect = data.billing_model === 'TENANT_DIRECT';
  const showTenant     = data.billing_model !== 'DIRECT';

  const BILLING_OPTS = [
    { value: 'PARTNER',       label: 'Partner Billing',  sub: 'C4P bills the Partner' },
    { value: 'DIRECT',        label: 'Direct',           sub: 'No Tenant involved' },
    { value: 'TENANT_DIRECT', label: 'Tenant Direct',    sub: 'C4P bills the Tenant' },
  ];

  const rowBase     = { borderBottom: '1px solid hsl(var(--border))', cursor: isReadOnly ? 'default' : 'pointer', height: `${ROW_H}px` };
  const rowSelected = { ...rowBase, background: NAVY };
  const rowEmpty    = { ...rowBase, cursor: 'default', height: `${ROW_H}px`, background: 'white' };

  // Pagination controls
  const Pager = ({ page, count, onPrev, onNext }) => count <= 1 ? null : (
    <div className="flex items-center justify-between px-3 py-1.5" style={{ borderTop: '1px solid hsl(var(--border))', background: 'hsl(var(--muted) / 0.3)' }}>
      <span className="text-xs" style={{ color: 'hsl(var(--muted-fg))' }}>Page {page + 1} of {count}</span>
      <div className="flex gap-1">
        <Button type="button" variant="ghost" size="sm" className="h-6 px-2 text-xs" disabled={page === 0} onClick={onPrev}>‹ Prev</Button>
        <Button type="button" variant="ghost" size="sm" className="h-6 px-2 text-xs" disabled={page >= count - 1} onClick={onNext}>Next ›</Button>
      </div>
    </div>
  );

  return (
    <Card title="Who is this quote for?">
      <div className="space-y-5">

        {/* Billing Arrangement - compact segmented */}
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide mb-2" style={{ color: 'hsl(var(--muted-fg))' }}>Billing Arrangement</p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            {BILLING_OPTS.map(opt => {
              const active = data.billing_model === opt.value;
              return (
                <button key={opt.value} type="button" disabled={isReadOnly}
                  onClick={() => set({ billing_model: opt.value })}
                  data-testid={`wizard-billing-${opt.value.toLowerCase()}`}
                  className="flex flex-col p-3 rounded-lg border-2 text-left transition-all"
                  style={active
                    ? { borderColor: NAVY, background: NAVY }
                    : { borderColor: 'hsl(var(--border))', background: 'white' }
                  }>
                  <span className="text-xs font-bold" style={{ color: active ? 'white' : NAVY }}>{opt.label}</span>
                  <span className="text-xs mt-0.5" style={{ color: active ? 'rgba(255,255,255,0.6)' : 'hsl(var(--muted-fg))' }}>{opt.sub}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Partner table */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs font-semibold uppercase tracking-wide" style={{ color: 'hsl(var(--muted-fg))' }}>
              Partner <span style={{ color: 'hsl(var(--danger))' }}>*</span>
              {selectedPartner && <span className="ml-2 normal-case font-normal" style={{ color: 'hsl(var(--c4p-brand-blue))' }}>- {selectedPartner.company_name}</span>}
            </p>
            {!isReadOnly && (
              <Button type="button" variant="ghost" size="sm" className="h-6 gap-1 text-xs px-2"
                style={{ color: 'hsl(var(--c4p-brand-blue))' }}
                onClick={() => navigate('/partners/new?return_to=/quotes/new')}>
                <Plus size={11} /> New Partner
              </Button>
            )}
          </div>
          <div className="rounded-xl border overflow-hidden bg-white">
            <div className="px-3 py-2" style={{ borderBottom: '1px solid hsl(var(--border))', background: 'hsl(var(--muted) / 0.4)' }}>
              <div className="relative">
                <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none" style={{ color: 'hsl(var(--muted-fg))' }} />
                <input type="text" className="w-full text-sm bg-white rounded-md border pl-7 pr-3 py-1.5 outline-none"
                  style={{ borderColor: aiPartnerHint && partnerFilter === aiPartnerHint ? '#0891b2' : 'hsl(var(--border))' }}
                  placeholder="Filter partners..." value={partnerFilter}
                  onChange={e => setPartnerFilter(e.target.value)} autoComplete="off" />
              </div>
              {aiPartnerHint && partnerFilter === aiPartnerHint && (
                <p className="text-xs mt-1" style={{ color: '#0891b2' }}>
                  ✧ AI detected "{aiPartnerHint}" - click to select or clear to search manually
                </p>
              )}
            </div>
            {/* Fixed-height rows area - always ROWS × ROW_H px so it never jumps */}
            <div style={{ height: `${ROWS * ROW_H}px` }}>
              {filteredPartners.length === 0 ? (
                <div className="flex items-center justify-center h-full text-sm" style={{ color: 'hsl(var(--muted-fg))' }}>
                  {partnerFilter ? `No partners match "${partnerFilter}"` : 'No partners available'}
                </div>
              ) : (
                <>
                  {partnerRows.map((c, idx) => {
                    const isSelected = c.id === data.customer_id;
                    return (
                      <div key={c.id} style={isSelected ? rowSelected : rowBase}
                        className="flex items-center justify-between px-3"
                        onClick={() => handlePartnerClick(c.id)}
                        onMouseEnter={e => { if (!isSelected) e.currentTarget.style.background = 'hsl(var(--muted))'; }}
                        onMouseLeave={e => { if (!isSelected) e.currentTarget.style.background = 'white'; }}
                        data-testid={`partner-row-${c.id}`}>
                        <div>
                          <p className="text-sm font-semibold" style={{ color: isSelected ? 'white' : NAVY }}>{c.company_name}</p>
                          <p className="text-xs" style={{ color: isSelected ? 'rgba(255,255,255,0.65)' : 'hsl(var(--muted-fg))' }}>
                            {[c.country, c.default_currency, c.default_region_name].filter(Boolean).join(' · ')}
                          </p>
                        </div>
                        {isSelected && <CheckCircle2 size={15} style={{ color: 'white', flexShrink: 0 }} />}
                      </div>
                    );
                  })}
                  {/* Pad remaining rows so height stays fixed */}
                  {Array.from({ length: ROWS - partnerRows.length }).map((_, i) => (
                    <div key={`pad-${i}`} style={rowEmpty} />
                  ))}
                </>
              )}
            </div>
            <Pager page={partnerPage} count={partnerPageCount}
              onPrev={() => setPartnerPage(p => p - 1)} onNext={() => setPartnerPage(p => p + 1)} />
          </div>
        </div>

        {/* Tenant table */}
        {showTenant && (
          <div>
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-semibold uppercase tracking-wide" style={{ color: 'hsl(var(--muted-fg))' }}>
                Tenant {isTenantDirect && <span style={{ color: 'hsl(var(--danger))' }}>*</span>}
                {!isTenantDirect && <span className="ml-1 normal-case font-normal">(optional)</span>}
                {selectedPartner && <span className="ml-2 normal-case font-normal" style={{ color: 'hsl(var(--c4p-brand-blue))' }}>- {selectedPartner.company_name}'s tenants</span>}
                {selectedTenant && <span className="ml-2 normal-case font-normal" style={{ color: 'hsl(var(--c4p-brand-blue))' }}>- {selectedTenant.company_name}</span>}
              </p>
              {!isReadOnly && (
                <Button type="button" variant="ghost" size="sm" className="h-6 gap-1 text-xs px-2"
                  disabled={!data.customer_id}
                  title={!data.customer_id ? 'Select a partner first to create a tenant' : undefined}
                  style={{ color: data.customer_id ? 'hsl(265 89% 45%)' : 'hsl(var(--muted-fg))' }}
                  onClick={() => {
                    if (!data.customer_id) return;
                    let url = `/tenants/new?return_to=/quotes/new`;
                    url += `&preset_partner=${data.customer_id}`;
                    navigate(url);
                  }}>
                  <Plus size={11} /> New Tenant
                </Button>
              )}
            </div>
            <div className="rounded-xl border overflow-hidden bg-white">
              <div className="px-3 py-2" style={{ borderBottom: '1px solid hsl(var(--border))', background: 'hsl(var(--muted) / 0.4)' }}>
                <div className="relative">
                  <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none" style={{ color: 'hsl(var(--muted-fg))' }} />
                  <input type="text" className="w-full text-sm bg-white rounded-md border pl-7 pr-3 py-1.5 outline-none"
                    style={{ borderColor: aiTenantHint && tenantFilter === aiTenantHint ? '#0891b2' : 'hsl(var(--border))' }}
                    placeholder={selectedPartner ? `Filter ${selectedPartner.company_name}'s tenants...` : 'Select a partner to filter tenants...'}
                    value={tenantFilter} onChange={e => setTenantFilter(e.target.value)} autoComplete="off" />
                </div>
                {aiTenantHint && tenantFilter === aiTenantHint && (
                  <p className="text-xs mt-1" style={{ color: '#0891b2' }}>
                    ✧ AI detected "{aiTenantHint}" - click to select or clear to search manually
                  </p>
                )}
              </div>
              <div style={{ height: `${ROWS * ROW_H}px` }}>
                {filteredTenants.length === 0 ? (
                  <div className="flex items-center justify-center h-full text-sm" style={{ color: 'hsl(var(--muted-fg))' }}>
                    {tenantFilter ? `No tenants match "${tenantFilter}"`
                      : selectedPartner ? `No tenants for ${selectedPartner.company_name} yet - use + New Tenant`
                      : 'Select a partner first to see their tenants'}
                  </div>
                ) : (
                  <>
                    {tenantRows.map(t => {
                      const isSelected = t.id === data.tenant_id;
                      return (
                        <div key={t.id} style={isSelected ? { ...rowSelected, background: 'hsl(265 89% 38%)' } : rowBase}
                          className="flex items-center justify-between px-3"
                          onClick={() => handleTenantClick(t.id)}
                          onMouseEnter={e => { if (!isSelected) e.currentTarget.style.background = 'hsl(var(--muted))'; }}
                          onMouseLeave={e => { if (!isSelected) e.currentTarget.style.background = 'white'; }}
                          data-testid={`tenant-row-${t.id}`}>
                          <div>
                            <p className="text-sm font-semibold" style={{ color: isSelected ? 'white' : NAVY }}>{t.company_name}</p>
                            <p className="text-xs" style={{ color: isSelected ? 'rgba(255,255,255,0.65)' : 'hsl(var(--muted-fg))' }}>
                              {[t.country, t.email].filter(Boolean).join(' · ')}
                            </p>
                          </div>
                          {isSelected && <CheckCircle2 size={15} style={{ color: 'white', flexShrink: 0 }} />}
                        </div>
                      );
                    })}
                    {Array.from({ length: ROWS - tenantRows.length }).map((_, i) => (
                      <div key={`pad-${i}`} style={rowEmpty} />
                    ))}
                  </>
                )}
              </div>
              <Pager page={tenantPage} count={tenantPageCount}
                onPrev={() => setTenantPage(p => p - 1)} onNext={() => setTenantPage(p => p + 1)} />
            </div>
            {isTenantDirect && selectedTenant?.billing_address && (
              <div className="mt-1.5 rounded-lg px-3 py-2 text-xs" style={{ background: 'hsl(265 89% 97%)', color: 'hsl(265 89% 35%)', border: '1px solid hsl(265 89% 80%)' }}>
                <span className="font-semibold">Invoice address: </span>{selectedTenant.billing_address}
              </div>
            )}
            {isTenantDirect && !data.tenant_id && (
              <p className="mt-1 text-xs" style={{ color: 'hsl(var(--danger))' }}>A Tenant is required for Tenant Direct billing.</p>
            )}
          </div>
        )}

        {/* Contact selection - shown when a partner is selected */}
        {selectedPartner && (() => {
          const partnerContacts = selectedPartner.contacts || [];
          // For Tenant Direct use tenant contact; otherwise use partner contacts
          const tenantContact = isTenantDirect && selectedTenant
            ? { id: `tenant-${selectedTenant.id}`, name: selectedTenant.contact_name, email: selectedTenant.email, phone: selectedTenant.phone_mobile, title: 'Tenant Contact', _isTenant: true }
            : null;
          const allContacts = tenantContact
            ? [tenantContact, ...partnerContacts]
            : partnerContacts;
          const selectedContactId = data.contact_ids?.[0] || '';

          return (
            <div>
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs font-semibold uppercase tracking-wide" style={{ color: 'hsl(var(--muted-fg))' }}>
                  Quote Contact
                  <span className="ml-1 normal-case font-normal">(PDF recipient)</span>
                </p>
              </div>
              <div className="rounded-xl border overflow-hidden bg-white">
                {allContacts.length === 0 ? (
                  <div className="px-3 py-4 text-sm text-center" style={{ color: 'hsl(var(--muted-fg))' }}>
                    No contacts for {selectedPartner.company_name} - add them in the Partners section.
                  </div>
                ) : allContacts.map((ct, idx) => {
                  const isSelected = selectedContactId === ct.id;
                  return (
                    <div key={ct.id}
                      className="flex items-center gap-3 px-3 py-2.5"
                      style={{
                        borderBottom: idx < allContacts.length - 1 ? '1px solid hsl(var(--border))' : 'none',
                        background: isSelected ? 'hsl(196 75% 56% / 0.07)' : 'white',
                        cursor: isReadOnly ? 'default' : 'pointer',
                      }}
                      onClick={() => !isReadOnly && set({ contact_ids: isSelected ? [] : [ct.id] })}
                      onMouseEnter={e => { if (!isSelected) e.currentTarget.style.background = 'hsl(var(--muted))'; }}
                      onMouseLeave={e => { if (!isSelected) e.currentTarget.style.background = 'white'; }}
                    >
                      {/* Radio dot */}
                      <div className="w-4 h-4 rounded-full border-2 flex-shrink-0 flex items-center justify-center"
                        style={{ borderColor: isSelected ? 'hsl(var(--c4p-brand-blue))' : 'hsl(var(--border))' }}>
                        {isSelected && <div className="w-2 h-2 rounded-full" style={{ background: 'hsl(var(--c4p-brand-blue))' }} />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="text-sm font-semibold" style={{ color: NAVY }}>{ct.name || '-'}</p>
                          {ct.title && <p className="text-xs" style={{ color: 'hsl(var(--muted-fg))' }}>{ct.title}</p>}
                          {ct._isTenant && (
                            <span className="text-xs px-1.5 py-0.5 rounded-full" style={{ background: 'hsl(265 89% 95%)', color: 'hsl(265 89% 40%)' }}>
                              Tenant Contact
                            </span>
                          )}
                          {ct.is_primary && (
                            <span className="text-xs px-1.5 py-0.5 rounded-full" style={{ background: 'hsl(var(--accent))', color: 'hsl(var(--c4p-brand-blue))' }}>
                              Primary
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-3 mt-0.5 flex-wrap">
                          {ct.email && <p className="text-xs" style={{ color: 'hsl(var(--c4p-brand-blue))' }}>{ct.email}</p>}
                          {ct.phone && <p className="text-xs" style={{ color: 'hsl(var(--muted-fg))' }}>{ct.phone}</p>}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
              {allContacts.length > 0 && !selectedContactId && (
                <p className="mt-1 text-xs" style={{ color: 'hsl(var(--muted-fg))' }}>
                  Select a contact to receive the PDF quote by email.
                </p>
              )}
              {selectedContactId && (() => {
                const ct = allContacts.find(c => c.id === selectedContactId);
                return ct?.email ? (
                  <p className="mt-1 text-xs" style={{ color: '#15803d' }}>
                    PDF will be addressed to <strong>{ct.name}</strong> - {ct.email}
                  </p>
                ) : null;
              })()}
            </div>
          );
        })()}

        {/* Margin fields - Tenant Direct only */}
        {isTenantDirect && (
          <div className="rounded-xl p-4 space-y-3" style={{ background: 'hsl(265 89% 97%)', border: '1.5px solid hsl(265 89% 75%)' }}>
            <p className="text-xs font-bold uppercase tracking-wide" style={{ color: 'hsl(265 89% 45%)' }}>Partner Margin Agreement</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs">PUPM Margin ({sym(data.currency)}/user/month)</Label>
                <Input type="number" min="0" step="0.01" value={data.partner_margin_pupm}
                  onChange={e => set({ partner_margin_pupm: e.target.value })}
                  disabled={isReadOnly} placeholder="e.g. 5.00" data-testid="wizard-partner-margin-pupm" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Non-User Items Margin %</Label>
                <Input type="number" min="0" max="100" step="0.1" value={data.partner_margin_percent}
                  onChange={e => set({ partner_margin_percent: e.target.value })}
                  disabled={isReadOnly} placeholder="e.g. 15" data-testid="wizard-partner-margin-percent" />
              </div>
            </div>
          </div>
        )}

      </div>

      <QuickCreatePartnerSheet
        open={showQCPartner} onClose={() => setShowQCPartner(false)}
        regions={pricingConfig?.regions || []} psmUsers={psmUsers || []}
        onCreated={(p) => { onCustomersRefresh?.(); handlePartnerClick(p.id); }}
      />
      <QuickCreateTenantSheet
        open={showQCTenant} onClose={() => setShowQCTenant(false)}
        partnerId={data.customer_id} partnerName={selectedPartner?.company_name || ''}
        onCreated={(t) => {
          onTenantsRefresh?.(); // refresh list in background for future use
          // Set state directly from the new tenant object — no race with the tenant list
          const newTenantName = t.company_name || '';
          const tenantUpdates = { tenant_id: t.id, tenant_name: newTenantName, end_customer_name: newTenantName };
          // When editing: also replace old tenant name in the quote_name
          if (existingQuote && data.tenant_name && newTenantName && data.quote_name && data.quote_name.includes(data.tenant_name)) {
            tenantUpdates.quote_name = data.quote_name.split(data.tenant_name).join(newTenantName);
          }
          set(tenantUpdates);
        }}
      />
    </Card>
  );
}

// ── Step 2: Quote Setup (Region, PSM, Dates, Notes) ────────────────────────
function Step2Setup({ data, set, customers, psmUsers, pricingConfig, isReadOnly, isStandalone, isReassignOnlyMode, Card }) {
  const regions    = (pricingConfig?.regions || []);
  const paymentMethods    = pricingConfig?.payment_methods    || ['Direct Debit', 'Wise', 'Bank Transfer', 'Credit Card', 'BACS'];
  const billingFrequencies = pricingConfig?.billing_frequencies || ['Monthly', 'Quarterly', 'Annually'];

  const handleRegionChange = (regionId) => {
    const region = regions.find(r => r.id === regionId);
    if (region) {
      // Currency is intentionally NOT changed when region changes - user must change it explicitly via the currency field
      set({ region_id: regionId, region_name: region.name, region_aws_code: region.aws_region_code || '' });
    }
  };

  return (
    <Card title="Quote Setup">
      <div className="space-y-4">
        <FieldRow label="Quote Name" hint="A friendly name for this quote - shown in search results and on the PDF">
          <Input
            value={data.quote_name || ''}
            onChange={e => set({ quote_name: e.target.value })}
            disabled={isReadOnly}
            placeholder="e.g. C4P Advanced · Acme Corp · May 2026"
            className="w-full"
            data-testid="wizard-quote-name"
          />
        </FieldRow>

        <FieldRow label="AWS Region *" hint="Sets the hosting region - currency can be set independently below">
          <Select value={data.region_id} onValueChange={handleRegionChange} disabled={isReadOnly}>
            <SelectTrigger data-testid="wizard-region-select"><SelectValue placeholder="Select hosting region..." /></SelectTrigger>
            <SelectContent>
              {regions.map(r => (
                <SelectItem key={r.id} value={r.id}>
                  {r.name}<span style={{ color: 'hsl(var(--muted-fg))' }}> · {r.aws_region_code} · {r.default_currency}</span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {data.region_id && customers.find(c => c.id === data.customer_id)?.default_region_id === data.region_id && (
            <p className="text-xs mt-1" style={{ color: 'hsl(var(--muted-fg))' }}>(from partner default)</p>
          )}
          {regions.length === 0 && <p className="text-xs" style={{ color: 'hsl(var(--danger))' }}>No regions configured. Ask an admin to add them.</p>}
        </FieldRow>

        <FieldRow label="Partner Success Manager (PSM) *" hint={isReassignOnlyMode ? 'Select a different PSM to reassign/transfer this quote' : "Defaults to partner's PSM or the creating user - can be reassigned for cover"}>
          <Select value={data.psm_user_id || 'none'} onValueChange={v => { const u = psmUsers.find(u => u.id === v); set({ psm_user_id: v === 'none' ? '' : v, psm_user_name: u?.name || '' }); }} disabled={isReadOnly && !isReassignOnlyMode}>
            <SelectTrigger data-testid="wizard-psm-select"><SelectValue placeholder="Select PSM..." /></SelectTrigger>
            <SelectContent>
              <SelectItem value="none">- Unassigned -</SelectItem>
              {psmUsers.map(u => (
                <SelectItem key={u.id} value={u.id}>{u.name} <span style={{ color: 'hsl(var(--muted-fg))' }}>· {u.role === 'SUPER_ADMIN' ? 'Admin' : u.role === 'MANAGER' ? 'Manager' : 'PSM'}</span></SelectItem>
              ))}
            </SelectContent>
          </Select>
        </FieldRow>

        <FieldRow label="Billing Frequency">
          <Select value={data.billing_frequency || 'Monthly'} onValueChange={v => set({ billing_frequency: v })} disabled={isReadOnly}>
            <SelectTrigger data-testid="wizard-billing-frequency"><SelectValue /></SelectTrigger>
            <SelectContent>
              {billingFrequencies.map(f => <SelectItem key={f} value={f}>{f}</SelectItem>)}
            </SelectContent>
          </Select>
        </FieldRow>

        <FieldRow label="Payment Method">
          <Select value={data.payment_method || 'none'} onValueChange={v => set({ payment_method: v === 'none' ? '' : v })} disabled={isReadOnly}>
            <SelectTrigger data-testid="wizard-payment-method"><SelectValue placeholder="Select payment method..." /></SelectTrigger>
            <SelectContent>
              <SelectItem value="none">- Not specified -</SelectItem>
              {paymentMethods.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}
            </SelectContent>
          </Select>
        </FieldRow>

        <div className="flex gap-4 flex-wrap">
          <FieldRow label="Expiry Date" hint="Quote validity - defaults to 30 days">
            <Input type="date" value={data.expiry_date} onChange={e => set({ expiry_date: e.target.value })} disabled={isReadOnly} min={new Date().toISOString().split('T')[0]} className="w-40" data-testid="wizard-expiry-date" />
          </FieldRow>
          <FieldRow label={<span>Required By Date <span style={{ color: '#ef4444' }}>*</span></span>} hint="Required - enter a specific date or use next available build slot">
            <div className="flex items-center gap-3 flex-wrap">
              {!data.use_next_available_slot ? (
                <Input
                  type="date"
                  value={data.required_by_date}
                  onChange={e => set({ required_by_date: e.target.value })}
                  disabled={isReadOnly}
                  min={new Date().toISOString().split('T')[0]}
                  className="w-40"
                  style={!data.required_by_date ? { borderColor: '#fca5a5' } : {}}
                  data-testid="wizard-required-by-date"
                />
              ) : (
                <div
                  className="flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium"
                  style={{ background: 'hsl(var(--muted))', color: 'hsl(var(--muted-fg))' }}
                  data-testid="wizard-next-available-slot-badge"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                  Next available slot
                </div>
              )}
              {!isReadOnly && (
                <button
                  type="button"
                  onClick={() => set({
                    use_next_available_slot: !data.use_next_available_slot,
                    required_by_date: '',
                  })}
                  className="flex items-center gap-1.5 text-xs transition-colors"
                  style={{ color: data.use_next_available_slot ? 'hsl(var(--primary))' : 'hsl(var(--muted-fg))' }}
                  data-testid="wizard-next-available-slot-toggle"
                >
                  <span
                    className="inline-flex items-center justify-center w-3.5 h-3.5 rounded border transition-colors flex-shrink-0"
                    style={{
                      background: data.use_next_available_slot ? 'hsl(var(--primary))' : 'transparent',
                      borderColor: data.use_next_available_slot ? 'hsl(var(--primary))' : 'hsl(var(--border))',
                    }}
                  >
                    {data.use_next_available_slot && (
                      <svg xmlns="http://www.w3.org/2000/svg" width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                    )}
                  </span>
                  Use next available build slot
                </button>
              )}
            </div>
          </FieldRow>
        </div>

        <FieldRow label="Notes / Scope">
          <Textarea value={data.notes} onChange={e => set({ notes: e.target.value })} disabled={isReadOnly} className="h-24 resize-none" placeholder="Internal notes, scope details, special requirements..." />
        </FieldRow>

        {/* ── Deal Reference + Probability ── */}
        <DealRefFields data={data} set={set} isReadOnly={isReadOnly} />
      </div>
    </Card>
  );
}

// ── Deal Reference fields (used inside Step2) ─────────────────────────────────
function DealRefFields({ data, set, isReadOnly }) {
  const [suggestions, setSuggestions] = React.useState([]);
  const [showSuggestions, setShowSuggestions] = React.useState(false);
  const debounceRef = React.useRef(null);
  const { quotesAPI: qApi } = React.useMemo(() => require('../../services/api'), []);
  const NAVY = 'hsl(var(--c4p-navy-900))';

  const handleDealRefChange = (val) => {
    set({ deal_ref: val });
    if (!data.customer_id) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try {
        const res = await qApi.getDealRefs(data.customer_id, val || undefined);
        setSuggestions((res.data || []).filter(s => s.ref && s.ref !== val));
      } catch { /* ignore */ }
    }, 300);
  };

  return (
    <div className="rounded-xl border p-4 space-y-3" style={{ background: 'hsl(var(--muted) / 0.3)', borderColor: 'hsl(var(--border))' }}>
      <p className="text-xs font-bold uppercase tracking-widest" style={{ color: NAVY }}>Deal Grouping <span className="font-normal text-xs" style={{ textTransform: 'none', color: 'hsl(var(--muted-fg))' }}>(optional)</span></p>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {/* Deal Reference with autocomplete */}
        <div className="md:col-span-2 space-y-1.5 relative">
          <Label className="text-xs">Deal Reference</Label>
          <Input
            value={data.deal_ref || ''}
            onChange={e => handleDealRefChange(e.target.value)}
            onFocus={() => { if (data.customer_id) handleDealRefChange(data.deal_ref || ''); setShowSuggestions(true); }}
            onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
            placeholder="e.g. Acme ERP Migration 2026"
            disabled={isReadOnly}
            data-testid="wizard-deal-ref"
          />
          {suggestions.length > 0 && showSuggestions && (
            <div className="absolute z-50 left-0 right-0 bg-white rounded-xl border shadow-lg overflow-hidden"
              style={{ top: '100%', marginTop: '2px', borderColor: '#e2e8f0' }}>
              {suggestions.map(s => (
                <button key={s.ref} type="button"
                  className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50 transition-colors flex items-center justify-between"
                  onMouseDown={() => { set({ deal_ref: s.ref }); setSuggestions([]); }}>
                  <span style={{ color: NAVY }}>{s.ref}</span>
                  <span className="text-xs" style={{ color: 'hsl(var(--muted-fg))' }}>{s.count} quote{s.count !== 1 ? 's' : ''}</span>
                </button>
              ))}
            </div>
          )}
          <p className="text-xs" style={{ color: 'hsl(var(--muted-fg))' }}>
            Groups quotes for the same deal — only the primary counts in pipeline reporting.
          </p>
        </div>
        {/* Probability */}
        <div className="space-y-1.5">
          <Label className="text-xs">Probability %</Label>
          <div className="relative">
            <Input
              type="number" min="0" max="100"
              value={data.deal_probability ?? 100}
              onChange={e => set({ deal_probability: Math.min(100, Math.max(0, parseInt(e.target.value) || 0)) })}
              disabled={isReadOnly || !data.deal_ref?.trim()}
              className="pr-8"
              data-testid="wizard-deal-probability"
            />
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs" style={{ color: 'hsl(var(--muted-fg))' }}>%</span>
          </div>
          <p className="text-xs" style={{ color: 'hsl(var(--muted-fg))' }}>Used for weighted pipeline.</p>
        </div>
      </div>
    </div>
  );
}


// ── Step 3: Product & Scale ────────────────────────────────────────────────────
function Step3Product({ data, set, pricingResult, isAdvanced, isStandalone, isReadOnly, customers, isSuperAdmin, Card }) {
  const tierLabel = pricingResult?.user_tier_label;

  return (
    <Card title="What are they buying?">
      <div className="space-y-5">
        {/* Product selector */}
        <div className="grid grid-cols-2 xl:grid-cols-4 gap-3">
          {[
            ['STANDARD', 'C4P Standard',  'Flat rate per user · No included App Server · No included DB · VPC required if App Servers added'],
            ['ADVANCED', 'C4P Advanced',  'Tiered pricing · 1 App Server included · HANA DB included · VPC included · Min 20 users'],
            ['MTPRO',    'C4P MTPro',     'Everything in Advanced · Cloud Control Centre (CCC) for multi-tenant management · Min 20 users'],
          ].map(([code, name, desc]) => (
            <button key={code} onClick={() => !isReadOnly && set({ product_code: code, is_standalone: false, mtpro_quote_type: 'new', mtpro_system_id: '', file_storage_gb: defaultFileStorageGb(code, false) })}
              className="p-4 rounded-xl border-2 text-left transition-all duration-150 flex flex-col items-start"
              style={!isStandalone && data.product_code === code
                ? { borderColor: 'hsl(var(--c4p-brand-blue))', background: 'hsl(196 75% 56% / 0.06)' }
                : { borderColor: 'hsl(var(--border))', background: 'white' }}
              data-testid={`wizard-product-${code.toLowerCase()}`}>
              <div className="flex items-center gap-2 mb-1.5">
                <div className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0"
                     style={{ background: !isStandalone && data.product_code === code ? 'hsl(var(--c4p-brand-blue))' : 'hsl(var(--border))' }}>
                  {!isStandalone && data.product_code === code && <CheckCircle2 size={12} color="white" />}
                </div>
                <p className="font-bold text-sm" style={{ fontFamily: 'var(--font-display)', color: NAVY }}>{name}</p>
              </div>
              <p className="text-xs ml-7" style={{ color: 'hsl(var(--muted-fg))', lineHeight: '1.5' }}>{desc}</p>
            </button>
          ))}
          {/* Standalone card */}
          <button
            onClick={() => !isReadOnly && set({ is_standalone: true, product_code: 'ADVANCED', mtpro_quote_type: 'new', mtpro_system_id: '', standalone_subtype: 'BESPOKE', standalone_ccc_required: false, num_users: '', file_storage_gb: 0 })}
            className="p-4 rounded-xl border-2 text-left transition-all duration-150 flex flex-col items-start"
            style={isStandalone
              ? { borderColor: 'hsl(var(--c4p-brand-blue))', background: 'hsl(196 75% 56% / 0.06)' }
              : { borderColor: 'hsl(var(--border))', background: 'white' }}
            data-testid="wizard-product-standalone">
            <div className="flex items-center gap-2 mb-1.5">
              <div className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0"
                   style={{ background: isStandalone ? 'hsl(var(--c4p-brand-blue))' : 'hsl(var(--border))' }}>
                {isStandalone && <CheckCircle2 size={12} color="white" />}
              </div>
              <p className="font-bold text-sm" style={{ fontFamily: 'var(--font-display)', color: NAVY }}>C4P Custom</p>
            </div>
            <p className="text-xs ml-7" style={{ color: 'hsl(var(--muted-fg))', lineHeight: '1.5' }}>
              Custom or Test System · Full DB price · No user minimum · VPC optional
            </p>
          </button>
        </div>

        {/* C4P Custom sub-options */}
        {isStandalone && (
          <div className="rounded-xl p-4 space-y-4" style={{ background: 'hsl(196 75% 56% / 0.05)', border: '1.5px solid hsl(var(--c4p-brand-blue) / 0.3)' }}>
            <p className="text-xs font-bold uppercase tracking-wide" style={{ color: 'hsl(var(--c4p-brand-blue))' }}>C4P Custom Options</p>

            {/* Test System toggle */}
            <div className="space-y-3">
              <label className="flex items-start gap-3 cursor-pointer group" htmlFor="is-test-system">
                <input
                  type="checkbox"
                  id="is-test-system"
                  checked={data.standalone_subtype === 'TEST_SYSTEM'}
                  onChange={e => !isReadOnly && set({
                    standalone_subtype: e.target.checked ? 'TEST_SYSTEM' : 'BESPOKE',
                    // Only default to '0' when checking Test System if no users entered yet; never wipe an existing value
                    num_users: e.target.checked ? (data.num_users || '0') : data.num_users,
                    service_unit_ref: e.target.checked ? data.service_unit_ref : '',
                  })}
                  className="mt-0.5 h-4 w-4 rounded flex-shrink-0"
                  style={{ accentColor: 'hsl(var(--c4p-brand-blue))' }}
                  data-testid="wizard-standalone-test-system-toggle"
                  disabled={isReadOnly || data.standalone_subtype === 'MANUAL'}
                />
                <div>
                  <p className="text-sm font-semibold" style={{ color: NAVY }}>Test System</p>
                  <p className="text-xs mt-0.5" style={{ color: 'hsl(var(--muted-fg))' }}>
                    This is a test environment linked to an existing Production system, sharing the same VPC. Users default to 0 (no PUPM charge).
                  </p>
                </div>
              </label>

              {data.standalone_subtype === 'TEST_SYSTEM' && (
                <div className="ml-7 space-y-1.5">
                  <label className="text-xs font-semibold" style={{ color: NAVY }}>Production Service Unit *</label>
                  <Input
                    value={data.service_unit_ref || ''}
                    onChange={e => {
                      // Auto-uppercase and strip non-alphanumeric
                      const raw = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '');
                      // Enforce SU prefix
                      let val = raw.startsWith('SU') ? raw : 'SU' + raw.replace(/^S?/, '');
                      // Limit to SU + 4 digits
                      const digits = val.replace(/^SU/, '').replace(/\D/g, '').slice(0, 4);
                      set({ service_unit_ref: 'SU' + digits });
                    }}
                    disabled={isReadOnly}
                    placeholder="SU0200"
                    className="w-full font-mono"
                    maxLength={6}
                    data-testid="wizard-production-system-name"
                  />
                  {data.service_unit_ref && !/^SU\d{4}$/.test(data.service_unit_ref) && (
                    <p className="text-xs" style={{ color: 'hsl(var(--danger))' }}>Format must be SU followed by 4 digits - e.g. SU0200</p>
                  )}
                  {(!data.service_unit_ref || /^SU\d{4}$/.test(data.service_unit_ref)) && (
                    <p className="text-xs" style={{ color: 'hsl(var(--muted-fg))' }}>Service Unit identifier of the existing Production system - e.g. SU0200</p>
                  )}
                </div>
              )}
            </div>

            {/* Manual Contract toggle - Super Admin only */}
            {isSuperAdmin && (
              <div className="space-y-1" style={{ paddingTop: '4px', borderTop: '1px solid hsl(var(--border))' }}>
                <label className="flex items-start gap-3 cursor-pointer group pt-3" htmlFor="is-manual-contract">
                  <input
                    type="checkbox"
                    id="is-manual-contract"
                    checked={data.standalone_subtype === 'MANUAL'}
                    onChange={e => !isReadOnly && set({
                      standalone_subtype: e.target.checked ? 'MANUAL' : 'BESPOKE',
                      num_users: data.num_users || '0',
                      service_unit_ref: '',
                    })}
                    className="mt-0.5 h-4 w-4 rounded flex-shrink-0"
                    style={{ accentColor: 'hsl(var(--c4p-orange-600))' }}
                    data-testid="wizard-standalone-manual-toggle"
                    disabled={isReadOnly || data.standalone_subtype === 'TEST_SYSTEM'}
                  />
                  <div>
                    <p className="text-sm font-semibold" style={{ color: NAVY }}>
                      Manual Contract
                      <span className="ml-2 text-xs font-medium px-1.5 py-0.5 rounded" style={{ background: '#fff7ed', color: '#ea580c', border: '1px solid #fdba74' }}>Super Admin</span>
                    </p>
                    <p className="text-xs mt-0.5" style={{ color: 'hsl(var(--muted-fg))' }}>
                      Legacy or existing customer. Prices pulled from the database and can be overridden on the final step before saving.
                    </p>
                  </div>
                </label>
              </div>
            )}

            {/* CCC toggle */}
            <div className="space-y-1.5" style={{ paddingTop: '4px', borderTop: '1px solid hsl(var(--border))' }}>
              <p className="text-xs font-semibold pt-3" style={{ color: NAVY }}>Cloud Control Centre (CCC) required?</p>
              <p className="text-xs" style={{ color: 'hsl(var(--muted-fg))' }}>CCC enables multi-tenant management within the standalone system. Select Yes if required for this deployment.</p>
              <div className="grid grid-cols-2 gap-2 mt-1">
                {[['no', 'No', false, 'Standard infrastructure - no multi-tenant management'], ['yes', 'Yes', true, 'Includes Cloud Control Centre for multi-tenant management']].map(([testId, label, val, desc]) => (
                  <button key={testId} type="button" onClick={() => !isReadOnly && set({ standalone_ccc_required: val, product_code: val ? 'MTPRO' : 'ADVANCED' })}
                    className="p-3 rounded-lg border-2 text-left transition-all"
                    style={data.standalone_ccc_required === val
                      ? { borderColor: 'hsl(var(--c4p-brand-blue))', background: 'hsl(196 75% 56% / 0.08)' }
                      : { borderColor: 'hsl(var(--border))', background: 'white' }}
                    data-testid={`wizard-standalone-ccc-${testId}`}>
                    <p className="font-semibold text-sm" style={{ color: NAVY }}>{label}</p>
                    <p className="text-xs mt-0.5" style={{ color: 'hsl(var(--muted-fg))' }}>{desc}</p>
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* DB type selector for Standard - HANA or SQL, no server config required */}
        {data.product_code === 'STANDARD' && (
          <div className="rounded-xl p-4 space-y-2" style={{ background: 'hsl(var(--muted))', border: '1px solid hsl(var(--border))' }}>
            <p className="text-xs font-semibold uppercase tracking-wide mb-2" style={{ color: 'hsl(var(--muted-fg))' }}>Database type</p>
            <div className="flex gap-2">
              {[['HANA', 'SAP HANA', 'In-memory · real-time analytics built in'], ['SQL', 'Microsoft SQL Server', 'Relational · broad add-on compatibility']].map(([t, label, desc]) => (
                <button key={t} type="button" onClick={() => !isReadOnly && set({ db_type: t })}
                  className="flex-1 p-3 rounded-xl border-2 text-left transition-all"
                  style={data.db_type === t
                    ? { borderColor: 'hsl(var(--c4p-brand-blue))', background: 'hsl(196 75% 56% / 0.06)' }
                    : { borderColor: 'hsl(var(--border))', background: 'white' }}
                  data-testid={`wizard-standard-db-${t.toLowerCase()}`}>
                  <p className="font-semibold text-sm" style={{ color: NAVY }}>{label}</p>
                  <p className="text-xs mt-0.5" style={{ color: 'hsl(var(--muted-fg))' }}>{desc}</p>
                </button>
              ))}
            </div>
            <p className="text-xs" style={{ color: 'hsl(var(--muted-fg))' }}>
              Pricing is the same for both. This records which database the customer uses and appears in the service description on the PDF.
            </p>
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <FieldRow label="Currency">
            <Select value={data.currency} onValueChange={v => set({ currency: v })} disabled={isReadOnly}>
              <SelectTrigger data-testid="wizard-currency-select"><SelectValue /></SelectTrigger>
              <SelectContent>{[{c:'GBP',s:'\u00a3'},{c:'EUR',s:'\u20ac'},{c:'USD',s:'$'},{c:'CAD',s:'CA$'},{c:'AUD',s:'A$'}].map(({c,s})=><SelectItem key={c} value={c}>{s} {c}</SelectItem>)}</SelectContent>
            </Select>
          </FieldRow>
          <FieldRow label="Term">
            <Select value={String(data.term_months)} onValueChange={v => set({ term_months: parseInt(v) })} disabled={isReadOnly}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{[1,3,6,12,24,36].map(t=><SelectItem key={t} value={String(t)}>{t} months</SelectItem>)}</SelectContent>
            </Select>
          </FieldRow>
        </div>
        <FieldRow label={
          isStandalone
            ? (data.standalone_subtype === 'TEST_SYSTEM' ? 'Number of Users (0 = borrowing from Production system)' : 'Number of Users')
            : `Number of Users${isAdvanced ? ' (minimum 20)' : ''}`
        }>
          <Input type="number" min={isStandalone ? 0 : (isAdvanced ? 20 : 1)} placeholder={isStandalone ? 'e.g. 0' : (isAdvanced ? 'e.g. 60' : 'e.g. 10')}
            value={data.num_users} onChange={e => set({ num_users: e.target.value })}
            disabled={isReadOnly} className="text-lg font-semibold" data-testid="wizard-users-input" />
          {tierLabel && !isStandalone && <p className="text-sm font-semibold mt-1" style={{ color: 'hsl(var(--c4p-brand-blue))' }}>→ {tierLabel}</p>}
          {isStandalone && data.standalone_subtype === 'TEST_SYSTEM' && data.num_users === '0' && (
            <p className="text-xs mt-1" style={{ color: 'hsl(var(--muted-fg))' }}>Zero users - this system will borrow users from the Production system at no PUPM charge.</p>
          )}
          {!isStandalone && isAdvanced && data.num_users && parseInt(data.num_users) < 20 && <p className="text-xs" style={{ color: 'hsl(var(--danger))' }}>Advanced / MTPro requires minimum 20 users</p>}
        </FieldRow>

        {/* Add-on user types - flat rate, no tier contribution */}
        <div className="rounded-xl border bg-white p-4 space-y-3">
          <p className="text-xs font-semibold uppercase tracking-wide" style={{ color: 'hsl(var(--muted-fg))' }}>
            Add-on User Types <span className="normal-case font-normal">(flat rate per user, no tier contribution)</span>
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <FieldRow label="Web Client Users">
              <Input type="number" min={0} placeholder="0"
                value={data.num_web_client_users || ''}
                onChange={e => set({ num_web_client_users: parseInt(e.target.value) || 0 })}
                disabled={isReadOnly} data-testid="wizard-web-client-users" />
            </FieldRow>
            <FieldRow label="Mobile Users">
              <Input type="number" min={0} placeholder="0"
                value={data.num_mobile_users || ''}
                onChange={e => set({ num_mobile_users: parseInt(e.target.value) || 0 })}
                disabled={isReadOnly} data-testid="wizard-mobile-users" />
            </FieldRow>
          </div>
        </div>
      </div>
    </Card>
  );
}

// ── Step 4: Database ────────────────────────────────────────────────────────────
function Step4Database({ data, set, dbServers, currentDbServer, pricingResult, isReadOnly, isStandalone, pricingConfig, Card }) {
  const s   = sym(data.currency);

  // ── C4P Custom: fully optional unified list ─────────────────────────────────
  if (isStandalone) {
    const allServers = data.extra_db_servers || [];
    const addDb = () => {
      const catalog = pricingConfig?.server_catalog || [];
      // C4P Custom: default to the custom-only 64GB HANA server; fall back to base if not found
      const baseHana = catalog.find(sv => sv.custom_only && sv.server_type === 'HANADB' && sv.is_active !== false)
                    || catalog.find(sv => sv.is_base_for_hana);
      // Default db_type is HANA, so we start with the smallest HANA server
      set({ extra_db_servers: [...allServers, {
        id: crypto.randomUUID(), db_type: 'HANA',
        db_server_code: baseHana?.code || '',
        purpose: '',
        additional_data_gb: 0, additional_backup_gb: 0, additional_db_root_gb: 0,
      }]});
    };
    const removeDb = (id) => set({ extra_db_servers: allServers.filter(sv => sv.id !== id) });
    const updateDb = (id, patch) => set({ extra_db_servers: allServers.map(sv => sv.id === id ? { ...sv, ...patch } : sv) });

    return (
      <Card title="Database Server">
        <div className="space-y-4">
          {allServers.length === 0 && (
            <div className="rounded-xl border border-dashed p-6 text-center" style={{ borderColor: 'hsl(var(--border))' }}>
              <p className="text-sm font-medium mb-1" style={{ color: NAVY }}>No DB Server added</p>
              <p className="text-xs mb-4" style={{ color: 'hsl(var(--muted-fg))' }}>
                C4P Custom does not require a DB server. Add one below if needed, or click Next to continue without one.
              </p>
              {!isReadOnly && (
                <Button type="button" size="sm" variant="outline" className="gap-1.5"
                  onClick={addDb} data-testid="add-extra-db-server">
                  <Plus size={13} /> Add DB Server
                </Button>
              )}
            </div>
          )}

          {allServers.map((es, idx) => {
            const eType = es.db_type === 'HANA' ? 'HANADB' : es.db_type === 'PRO4GL' ? 'PRO4GLDB' : 'SQLDB';
            const eCatalog = (pricingConfig?.server_catalog || []).filter(sv => sv.server_type === eType && sv.is_active !== false).sort((a,b) => a.sort_order - b.sort_order);
            const eSvr = eCatalog.find(sv => sv.code === es.db_server_code);
            const ePriceAmt = (() => {
              // Regional price takes priority (same logic as pricing engine)
              if (data.region_aws_code && eSvr?.standalone_region_price_matrix?.[data.region_aws_code]?.[data.currency]) {
                return eSvr.standalone_region_price_matrix[data.region_aws_code][data.currency];
              }
              return eSvr?.standalone_prices_by_currency?.[data.currency] || 0;
            })();
            const eSortedIdx = eCatalog.findIndex(sv => sv.code === es.db_server_code);
            const eNextSvr = eSortedIdx >= 0 && eSortedIdx < eCatalog.length - 1 ? eCatalog[eSortedIdx + 1] : null;
            return (
              <div key={es.id} className="rounded-xl border overflow-hidden">
                {/* Header */}
                <div className="flex items-center justify-between px-4 py-3" style={{ borderBottom: '1px solid hsl(var(--border))', background: 'hsl(var(--muted) / 0.5)' }}>
                  <p className="text-sm font-semibold" style={{ color: NAVY }}>DB Server {idx + 1}</p>
                  {!isReadOnly && (
                    <Button type="button" size="sm" variant="ghost" className="h-7 w-7 p-0"
                      style={{ color: 'hsl(var(--danger))' }} onClick={() => removeDb(es.id)}
                      data-testid={`remove-extra-db-${idx}`}>
                      <Trash2 size={13} />
                    </Button>
                  )}
                </div>
                <div className="p-4 space-y-4">
                  {/* Purpose */}
                  <div className="space-y-1.5">
                    <label className="text-xs font-semibold" style={{ color: NAVY }}>Purpose *</label>
                    <Input
                      value={es.purpose || ''}
                      onChange={e => updateDb(es.id, { purpose: e.target.value })}
                      disabled={isReadOnly}
                      placeholder="e.g. SAP HANA Production, SQL CRM Database, Progress 4GL ERP"
                      className="w-full"
                      data-testid={`db-server-purpose-${idx}`}
                    />
                    {!es.purpose?.trim() && (
                      <p className="text-xs" style={{ color: 'hsl(var(--muted-fg))' }}>Required - helps identify this server on the quote and build sheet.</p>
                    )}
                  </div>
                  {/* DB type */}
                  <div className="flex gap-2">
                    {['HANA', 'SQL', 'PRO4GL'].map(t => (
                      <button key={t} type="button" onClick={() => {
                        if (isReadOnly) return;
                        const catalog = pricingConfig?.server_catalog || [];
                        // C4P Custom: default to smallest server by sort_order (not the base/included server)
                        const typeKey = t === 'HANA' ? 'HANADB' : t === 'PRO4GL' ? 'PRO4GLDB' : 'SQLDB';
                        const base = t === 'HANA'
                          ? (catalog.find(sv => sv.custom_only && sv.server_type === 'HANADB' && sv.is_active !== false) || catalog.find(sv => sv.is_base_for_hana))
                          : t === 'PRO4GL'
                            ? (catalog.filter(sv => sv.server_type === 'PRO4GLDB' && sv.is_active !== false).sort((a,b) => (a.sort_order||99) - (b.sort_order||99))[0] || catalog.find(sv => sv.is_base_for_pro4gl))
                            : (catalog.filter(sv => sv.server_type === 'SQLDB' && sv.is_active !== false).sort((a,b) => (a.sort_order||99) - (b.sort_order||99))[0] || catalog.find(sv => sv.is_base_for_sql));
                        updateDb(es.id, { db_type: t, db_server_code: base?.code || '', additional_data_gb: 0, additional_backup_gb: 0 });
                      }}
                        className="px-5 py-2.5 rounded-xl font-semibold text-sm border-2"
                        data-testid={`custom-db-type-${t.toLowerCase()}-${idx}`}
                        style={es.db_type === t ? { background: NAVY, color: 'white', border: 'none' } : { background: 'white', color: 'hsl(var(--muted-fg))', borderColor: 'hsl(var(--border))' }}>
                        {t === 'PRO4GL' ? 'Progress 4GL' : t}
                      </button>
                    ))}
                  </div>
                  {/* Server info card */}
                  {eSvr && (
                    <div className="rounded-xl border-2 p-4" style={{ borderColor: NAVY, background: 'white' }}>
                      <div className="flex items-start justify-between">
                        <div>
                          <p className="font-bold text-base" style={{ fontFamily: 'var(--font-display)', color: NAVY }}>{eSvr.description}</p>
                          <p className="text-sm mt-0.5" style={{ color: 'hsl(var(--muted-fg))' }}>{eSvr.os_included}</p>
                        </div>
                        <p className="font-bold tabular-nums" style={{ color: 'hsl(var(--c4p-orange-600))' }}>
                          {ePriceAmt != null && ePriceAmt > 0 ? `${s}${ePriceAmt.toLocaleString('en-GB', { minimumFractionDigits: 2 })}/mo` : '-'}
                        </p>
                      </div>
                      <div className="grid grid-cols-3 gap-3 mt-3 pt-3" style={{ borderTop: '1px solid hsl(var(--border))' }}>
                        <div className="text-center">
                          <p className="text-xs" style={{ color: 'hsl(var(--muted-fg))' }}>Root Storage</p>
                          <p className="font-semibold text-sm">100 GB <span className="text-xs font-normal" style={{ color: 'hsl(var(--muted-fg))' }}>included</span></p>
                        </div>
                        <div className="text-center">
                          <p className="text-xs" style={{ color: 'hsl(var(--muted-fg))' }}>Data Storage</p>
                          <p className="font-semibold text-sm text-xs" style={{ color: 'hsl(var(--muted-fg))' }}>not included</p>
                        </div>
                        <div className="text-center">
                          <p className="text-xs" style={{ color: 'hsl(var(--muted-fg))' }}>Backup Storage</p>
                          <p className="font-semibold text-sm text-xs" style={{ color: 'hsl(var(--muted-fg))' }}>not included</p>
                        </div>
                      </div>
                    </div>
                  )}
                  {/* Size selector */}
                  <div className="space-y-2">
                    <Label>Select DB Server</Label>
                    <div className="flex gap-2 flex-wrap">
                      {eCatalog.map(sv => (
                        <button key={sv.code} type="button" onClick={() => !isReadOnly && updateDb(es.id, { db_server_code: sv.code })}
                          className="px-3 py-1.5 rounded-lg text-xs font-medium border"
                          data-testid={`custom-db-size-${sv.code}-${idx}`}
                          style={sv.code === es.db_server_code ? { background: NAVY, color: 'white', border: 'none' } : { background: 'white', color: 'hsl(var(--muted-fg))', borderColor: 'hsl(var(--border))' }}>
                          {sv.cpu}vCPU/{sv.memory_gb}GB
                        </button>
                      ))}
                    </div>
                    {eNextSvr && (
                      <p className="text-xs" style={{ color: 'hsl(var(--muted-fg))' }}>
                        Next: {eNextSvr.description} (+{eNextSvr.included_data_gb}GB data / +{eNextSvr.included_backup_gb}GB backup)
                      </p>
                    )}
                  </div>
                  <Separator />
                  <div className="space-y-3">
                    <p className="text-sm font-semibold" style={{ color: 'hsl(var(--muted-fg))' }}>Root &amp; Data Storage</p>
                    <StorageStepper label="Additional Root Drive Volume" hint="100 GB included · extra charged per 100 GB block"
                      value={es.additional_db_root_gb || 0} onChange={v => !isReadOnly && updateDb(es.id, { additional_db_root_gb: v })} />
                    <Separator />
                    <p className="text-sm font-semibold" style={{ color: 'hsl(var(--muted-fg))' }}>Data &amp; Backup Storage (not included - priced per 100 GB block)</p>
                    <StorageStepper label="Data Volume" hint="Not included - priced per 100 GB block"
                      value={es.additional_data_gb || 0} onChange={v => !isReadOnly && updateDb(es.id, { additional_data_gb: v })} />
                    <StorageStepper label="Backup Volume" hint="Not included - priced per 100 GB block"
                      value={es.additional_backup_gb || 0} onChange={v => !isReadOnly && updateDb(es.id, { additional_backup_gb: v })} />
                  </div>
                </div>
              </div>
            );
          })}

          {/* Add button - always at the bottom when at least one server exists */}
          {!isReadOnly && allServers.length > 0 && (
            <Button type="button" variant="outline" className="w-full gap-2"
              onClick={addDb} data-testid="add-extra-db-server">
              <Plus size={13} /> Add DB Server
            </Button>
          )}
        </div>
      </Card>
    );
  }

  // ── Standard / Advanced / MTPro: existing single-server layout ──────────────
  const dbLineItem = pricingResult?.line_items?.find(l => l.category === 'db_server' && !l.is_included);
  const dbMonthly  = dbLineItem?.monthly_amount ?? null;
  const tierKey    = pricingResult?.user_tier_number != null ? String(pricingResult.user_tier_number) : null;
  const getMatrixPrice = (svr) => {
    if (!svr || !tierKey) return null;
    const p = svr.price_matrix?.[tierKey]?.[data.currency];
    return (p != null && p > 0) ? p : null;
  };
  const sortedServers = [...dbServers].sort((a, b) => a.sort_order - b.sort_order);
  const currentIdx = sortedServers.findIndex(sv => sv.code === data.db_server_code);
  const nextSvr = currentIdx < sortedServers.length - 1 ? sortedServers[currentIdx + 1] : null;

  return (
    <Card title="Database Server">
      <div className="space-y-5">
        {/* DB Type toggle */}
        <div className="flex gap-2">
          {['HANA', 'SQL'].map(t => (
            <button key={t} onClick={() => !isReadOnly && set({ db_type: t, db_server_code: '', additional_data_gb: 0, additional_backup_gb: 0, additional_db_root_gb: 0 })}
              className="px-5 py-2.5 rounded-xl font-semibold text-sm border-2"
              style={data.db_type === t ? { background: 'hsl(var(--c4p-navy-900))', color: 'white', border: 'none' } : { background: 'white', color: 'hsl(var(--muted-fg))', borderColor: 'hsl(var(--border))' }}
              data-testid={`wizard-db-type-${t.toLowerCase()}`}>{t}</button>
          ))}
        </div>
        {/* Current server card */}
        {currentDbServer && (
          <div className="rounded-xl border-2 p-4" style={{ borderColor: 'hsl(var(--c4p-navy-900))' }}>
            <div className="flex items-start justify-between">
              <div>
                <p className="font-bold text-base" style={{ fontFamily: 'var(--font-display)', color: 'hsl(var(--c4p-navy-900))' }}>{currentDbServer.description}</p>
                <p className="text-sm mt-0.5" style={{ color: 'hsl(var(--muted-fg))' }}>{currentDbServer.os_included}</p>
                {(currentDbServer.is_base_for_hana || currentDbServer.is_base_for_sql) && (
                  <span className="text-xs font-semibold px-2 py-0.5 rounded-full mt-1 inline-block" style={{ background: 'hsl(var(--accent))', color: 'hsl(var(--c4p-navy-900))' }}>Included in plan</span>
                )}
              </div>
              <div className="text-right">
                <p className="font-bold tabular-nums" style={{ color: 'hsl(var(--c4p-orange-600))' }}>
                  {(currentDbServer.is_base_for_hana || currentDbServer.is_base_for_sql)
                    ? 'Included'
                    : dbMonthly != null ? `${s}${dbMonthly.toLocaleString('en-GB', { minimumFractionDigits: 2 })}/mo` : '-'
                  }
                </p>
                {dbMonthly != null && !(currentDbServer.is_base_for_hana || currentDbServer.is_base_for_sql) && pricingResult?.user_tier_label && (
                  <p className="text-xs mt-0.5" style={{ color: 'hsl(var(--muted-fg))' }}>{pricingResult.user_tier_label}</p>
                )}
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3 mt-3 pt-3" style={{ borderTop: '1px solid hsl(var(--border))' }}>
              {[['Root Storage', '100 GB'], ['Data Storage', `${currentDbServer.included_data_gb} GB`], ['Backup Storage', `${currentDbServer.included_backup_gb} GB`]].map(([label, val]) => (
                <div key={label} className="text-center">
                  <p className="text-xs" style={{ color: 'hsl(var(--muted-fg))' }}>{label}</p>
                  <p className="font-semibold text-sm">{val} <span className="text-xs font-normal" style={{ color: 'hsl(var(--muted-fg))' }}>included</span></p>
                </div>
              ))}
            </div>
          </div>
        )}
        {/* Server selector */}
        <div className="space-y-2">
          <Label>Upgrade Server</Label>
          <div className="flex gap-2 flex-wrap">
            {sortedServers.map(sv => (
              <button key={sv.code} onClick={() => !isReadOnly && set({ db_server_code: sv.code })}
                className="px-3 py-1.5 rounded-lg text-xs font-medium border"
                style={sv.code === data.db_server_code ? { background: 'hsl(var(--c4p-navy-900))', color: 'white', border: 'none' } : { background: 'white', color: 'hsl(var(--muted-fg))', borderColor: 'hsl(var(--border))' }}
                data-testid={`wizard-db-server-${sv.memory_gb}gb`}>
                {sv.memory_gb}GB{(sv.is_base_for_hana || sv.is_base_for_sql) ? ' ★' : ''}
              </button>
            ))}
          </div>
          {nextSvr && (
            <p className="text-xs" style={{ color: 'hsl(var(--muted-fg))' }}>
              Next: {nextSvr.description} (+{nextSvr.included_data_gb}GB data / +{nextSvr.included_backup_gb}GB backup)
              {(() => { const p = getMatrixPrice(nextSvr); return p != null ? ` · ${s}${p.toLocaleString('en-GB', { minimumFractionDigits: 2 })}/mo` : ''; })()}
            </p>
          )}
        </div>
        <Separator />
        <div className="space-y-3">
          <p className="text-sm font-semibold" style={{ color: 'hsl(var(--muted-fg))' }}>Root &amp; Data Storage</p>
          <StorageStepper label="Additional Root Drive Volume" hint="100 GB included · extra charged per 100 GB block"
            value={data.additional_db_root_gb} onChange={v => !isReadOnly && set({ additional_db_root_gb: v })} />
          <Separator />
          <p className="text-sm font-semibold" style={{ color: 'hsl(var(--muted-fg))' }}>Additional Data Storage (beyond included)</p>
          <StorageStepper label="Additional Data Volume" hint={`${currentDbServer?.included_data_gb || 0}GB included · priced per 100GB block`}
            value={data.additional_data_gb} onChange={v => !isReadOnly && set({ additional_data_gb: v })} />
          <StorageStepper label="Additional Backup Volume" hint={`${currentDbServer?.included_backup_gb || 0}GB included · priced per 100GB block`}
            value={data.additional_backup_gb} onChange={v => !isReadOnly && set({ additional_backup_gb: v })} />
        </div>
      </div>
    </Card>
  );
}

// ── Step 5: Application Servers ───────────────────────────────────────────────
// ── Purpose Select: dropdown of predefined purposes + "Other" free text ───────
function PurposeSelect({ value, onChange, purposes, disabled, testId }) {
  const OTHER = '__other__';
  const isCustomValue = !!(value && !purposes.includes(value));
  const [showCustom, setShowCustom] = React.useState(isCustomValue);
  const selectVal = showCustom ? OTHER : (value || '');

  const handleSelectChange = (v) => {
    if (v === OTHER) {
      setShowCustom(true);
      onChange('');
    } else {
      setShowCustom(false);
      onChange(v);
    }
  };

  return (
    <div className="space-y-1.5">
      <Select value={selectVal} onValueChange={handleSelectChange} disabled={disabled}>
        <SelectTrigger className="h-8 text-xs" data-testid={testId}
          style={!value ? { borderColor: '#fca5a5' } : {}}>
          <SelectValue placeholder="Select purpose *" />
        </SelectTrigger>
        <SelectContent>
          {purposes.map(p => <SelectItem key={p} value={p} className="text-xs">{p}</SelectItem>)}
          <SelectItem value={OTHER} className="text-xs italic" style={{ color: 'hsl(var(--muted-fg))' }}>Other - type your own</SelectItem>
        </SelectContent>
      </Select>
      {(showCustom || isCustomValue) && (
        <Input
          className="h-8 text-xs"
          placeholder="Describe the server purpose..."
          value={value || ''}
          onChange={e => onChange(e.target.value)}
          disabled={disabled}
          autoFocus={!disabled}
          data-testid="app-server-custom-purpose-input"
        />
      )}
      {!value && (
        <p className="text-xs" style={{ color: '#ef4444' }}>Purpose is required for all app servers</p>
      )}
    </div>
  );
}

function Step5Servers({ data, set, appServerCatalog, pricingConfig, isAdvanced, isStandalone, isReadOnly, availableExtras, Card, appServerPurposes }) {
  const [osFilter, setOsFilter] = useState('Windows');
  const [addCode, setAddCode] = useState('');

  const includedSvr = data.app_servers.find(s => s.is_included_in_pupm);
  // All additional servers - each is an individual instance at index i
  const additionalSvrs = data.app_servers
    .map((s, i) => ({ ...s, _idx: i }))
    .filter(s => !s.is_included_in_pupm);
  const filteredCatalog = appServerCatalog[osFilter] || [];

  // Compute the included server upgrade delta for UI preview
  const includedUpgradeInfo = useMemo(() => {
    if (!includedSvr || !pricingConfig) return null;
    const catalog = pricingConfig.server_catalog || [];
    const baseSvr = catalog.find(s => s.is_included_app_server);
    if (!baseSvr || includedSvr.server_code === baseSvr.code) return null;
    const chosenSvr = catalog.find(s => s.code === includedSvr.server_code);
    if (!chosenSvr) return null;
    const getPrice = (svr) => {
      if (svr.region_price_matrix && data.region_aws_code) {
        const p = svr.region_price_matrix[data.region_aws_code]?.[data.currency];
        if (p > 0) return p;
      }
      return svr.prices_by_currency?.[data.currency] || 0;
    };
    const delta = Math.max(0, getPrice(chosenSvr) - getPrice(baseSvr));
    return { baseSvr, chosenSvr, delta };
  }, [includedSvr, pricingConfig, data.region_aws_code, data.currency]);

  // Always add as a NEW individual server entry (never consolidate by code)
  const addServer = () => {
    if (!addCode) return;
    const newServer = { server_code: addCode, quantity: 1, additional_root_gb: 0, is_included_in_pupm: false, notes: '' };
    const newAppServers = [...data.app_servers, newServer];
    const updates = { app_servers: newAppServers };

    // Auto-add VPC for Standard if App Server added and no VPC yet
    // Only 1 VPC is needed regardless of how many App Servers are added
    if (!isAdvanced && availableExtras) {
      const vpcExtra = (availableExtras || []).find(e => isVpcExtra(e));
      const hasVpc = data.extras.some(e => isVpcExtraLine(e));
      if (vpcExtra && !hasVpc) {
        updates.extras = [...data.extras, {
          extra_id: vpcExtra.id, extra_code: vpcExtra.code, extra_name: vpcExtra.name,
          quantity: 1, unit: vpcExtra.unit, pricing_type: vpcExtra.pricing_type,
        }];
      }
    }
    set(updates);
    setAddCode('');
  };

  // Update by array index (supports multiple instances of same server code)
  const updateByIdx = (idx, field, value) => {
    const updated = data.app_servers.map((s, i) => i === idx ? { ...s, [field]: value } : s);
    set({ app_servers: updated });
  };

  const removeByIdx = (idx) => {
    set({ app_servers: data.app_servers.filter((_, i) => i !== idx) });
  };

  const hasAdditionalServers = additionalSvrs.length > 0;
  const hasVpcInExtras = data.extras.some(e => isVpcExtraLine(e));
  const vpcExtra = (availableExtras || []).find(e => isVpcExtra(e));

  const quickAddVpc = () => {
    if (vpcExtra && !hasVpcInExtras) {
      set({ extras: [...data.extras, { extra_id: vpcExtra.id, extra_code: vpcExtra.code, extra_name: vpcExtra.name, quantity: 1, unit: vpcExtra.unit, pricing_type: vpcExtra.pricing_type }] });
    }
  };

  return (
    <Card title="Application Servers">
      <div className="space-y-5">
        {/* Advanced: VPC included notice (not for Standalone) */}
        {isAdvanced && !isStandalone && (
          <div className="flex items-center gap-3 p-3 rounded-lg" style={{ background: 'hsl(196 75% 56% / 0.08)', border: '1px solid hsl(var(--c4p-brand-blue) / 0.25)' }}>
            <div className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: 'hsl(var(--c4p-brand-blue))', color: 'white' }}>
              <CheckCircle2 size={12} />
            </div>
            <p className="text-sm"><span className="font-semibold">VPC included</span> with C4P Advanced - no extra charge required.</p>
          </div>
        )}

        {/* Standard: VPC warning when App Servers added but no VPC */}
        {!isAdvanced && hasAdditionalServers && !hasVpcInExtras && (
          <div className="flex items-start justify-between gap-3 p-3 rounded-lg" style={{ background: '#fffbeb', border: '1px solid #fde68a' }}>
            <div className="flex items-start gap-2">
              <AlertCircle size={15} className="flex-shrink-0 mt-0.5" style={{ color: '#d97706' }} />
              <p className="text-sm" style={{ color: '#92400e' }}>
                <span className="font-semibold">VPC required</span> - C4P Standard requires a VPC when Application Servers are added.
                Only 1 VPC is needed regardless of the number of servers.
              </p>
            </div>
            {vpcExtra && (
              <Button size="sm" onClick={quickAddVpc}
                className="flex-shrink-0 gap-1 text-xs"
                style={{ background: '#d97706', color: 'white' }}>
                <Plus size={11} /> Add VPC
              </Button>
            )}
          </div>
        )}

        {/* Standard: VPC confirmed */}
        {!isAdvanced && hasAdditionalServers && hasVpcInExtras && (
          <div className="flex items-center gap-2 p-2.5 rounded-lg text-sm" style={{ background: '#f0fdf4', border: '1px solid #bbf7d0' }}>
            <CheckCircle2 size={14} style={{ color: '#16a34a' }} />
            <span style={{ color: '#14532d' }}>VPC added to Extras - requirement met.</span>
          </div>
        )}

        {/* Included server (Advanced/MTPro) - upgradeable */}
        {includedSvr && (() => {
          const includedIdx = data.app_servers.findIndex(s => s.is_included_in_pupm);
          const sym_ = sym(data.currency);
          // All available Windows app servers for upgrade selection
          const upgradeOptions = (pricingConfig?.server_catalog || []).filter(
            s => s.server_type === 'Application' && s.is_active !== false &&
                 !s.os_included?.includes('Linux') && !s.os_included?.includes('Ubuntu') && !s.os_included?.includes('GPU')
          ).sort((a, b) => {
            const getP = s => s.region_price_matrix?.[data.region_aws_code]?.[data.currency] || s.prices_by_currency?.[data.currency] || 0;
            return getP(a) - getP(b);
          });
          return (
            <div className="rounded-xl p-4 space-y-3" style={{ background: 'hsl(196 75% 56% / 0.08)', border: '1px solid hsl(var(--c4p-brand-blue) / 0.25)' }}>
              <div className="flex items-center justify-between">
                <p className="text-xs font-bold uppercase tracking-wide" style={{ color: 'hsl(var(--c4p-brand-blue))' }}>Included App Server</p>
                <span className="text-xs font-bold px-2 py-0.5 rounded-full" style={{ background: 'hsl(var(--c4p-brand-blue))', color: 'white' }}>Included in plan</span>
              </div>

              {/* Server selector */}
              {!isReadOnly ? (
                <div className="space-y-1.5">
                  <Label className="text-xs text-muted-foreground">Server type (upgrade optional)</Label>
                  <Select
                    value={includedSvr.server_code}
                    onValueChange={code => updateByIdx(includedIdx, 'server_code', code)}
                  >
                    <SelectTrigger className="w-full" data-testid="wizard-included-server-select">
                      <SelectValue placeholder="Select server..." />
                    </SelectTrigger>
                    <SelectContent>
                      {upgradeOptions.map(s => (
                        <SelectItem key={s.code} value={s.code}>{s.description}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {/* Upgrade cost indicator */}
                  {includedUpgradeInfo && includedUpgradeInfo.delta > 0 && (
                    <p className="text-xs font-medium" style={{ color: 'hsl(var(--c4p-orange-600))' }}
                      data-testid="wizard-included-server-upgrade-cost">
                      Upgrade cost: +{sym_}{includedUpgradeInfo.delta.toFixed(2)}/month
                      <span className="font-normal ml-1" style={{ color: 'hsl(var(--muted-fg))' }}>
                        (delta above included {includedUpgradeInfo.baseSvr.description})
                      </span>
                    </p>
                  )}
                  {includedUpgradeInfo && includedUpgradeInfo.delta === 0 && (
                    <p className="text-xs" style={{ color: 'hsl(var(--muted-fg))' }}>
                      No upgrade charge - this server costs the same or less than the included base
                    </p>
                  )}
                  {!includedUpgradeInfo && (
                    <p className="text-xs" style={{ color: 'hsl(var(--muted-fg))' }}>100 GB Root Volume included · £0/month</p>
                  )}
                </div>
              ) : (
                <div>
                  <p className="font-semibold text-sm">{includedSvr.server_code}</p>
                  {includedUpgradeInfo && includedUpgradeInfo.delta > 0 && (
                    <p className="text-xs mt-0.5" style={{ color: 'hsl(var(--c4p-orange-600))' }}>
                      Upgrade cost: +{sym_}{includedUpgradeInfo.delta.toFixed(2)}/month
                    </p>
                  )}
                </div>
              )}

              {/* Additional root storage */}
              {!isReadOnly && (
                <StorageStepper
                  label="Additional Root Volume"
                  hint="100 GB included · extra per 10 or 100 GB"
                  value={includedSvr.additional_root_gb || 0}
                  onChange={v => updateByIdx(includedIdx, 'additional_root_gb', v)}
                />
              )}
              {isReadOnly && (includedSvr.additional_root_gb || 0) > 0 && (
                <p className="text-xs" style={{ color: 'hsl(var(--muted-fg))' }}>
                  Root Volume: {100 + (includedSvr.additional_root_gb || 0)} GB total
                </p>
              )}

              {/* Server Purpose */}
              {!isReadOnly && (
                <div className="space-y-1">
                  <Label className="text-xs font-semibold">Server Purpose <span style={{ color: '#ef4444' }}>*</span>
                    <span className="ml-1.5 font-normal" style={{ color: 'hsl(var(--muted-fg))' }}>
                      - identifies this server on the build sheet and quote document
                    </span>
                  </Label>
                  <PurposeSelect
                    value={includedSvr.notes || ''}
                    onChange={v => updateByIdx(includedIdx, 'notes', v)}
                    purposes={appServerPurposes}
                    disabled={false}
                    testId="wizard-included-server-purpose"
                  />
                </div>
              )}
              {isReadOnly && includedSvr.notes && (
                <div>
                  <p className="text-xs font-semibold" style={{ color: 'hsl(var(--muted-fg))' }}>Server Purpose</p>
                  <p className="text-xs font-medium" style={{ color: 'hsl(var(--c4p-navy-900))' }}>{includedSvr.notes}</p>
                </div>
              )}
            </div>
          );
        })()}
        {!isAdvanced && !hasAdditionalServers && (
          <p className="text-sm" style={{ color: 'hsl(var(--muted-fg))' }}>Standard plan has no included App Server. Add servers below.</p>
        )}

        {/* Individual additional server cards */}
        {additionalSvrs.length > 0 && (
          <div className="space-y-3">
            <p className="text-sm font-semibold">Additional Servers ({additionalSvrs.length})</p>
            {additionalSvrs.map((s, listIdx) => (
              <div key={s._idx} className="rounded-xl border p-4 space-y-3">
                <div className="flex items-start justify-between">
                  <div className="min-w-0">
                    <div className="flex items-center gap-1.5">
                      <p className="font-medium text-sm truncate">{s.server_code}</p>
                      <span className="text-xs px-1.5 py-0.5 rounded flex-shrink-0"
                        style={{ background: 'hsl(var(--muted))', color: 'hsl(var(--muted-fg))' }}>
                        #{listIdx + 1}
                      </span>
                    </div>
                    <p className="text-xs" style={{ color: 'hsl(var(--muted-fg))' }}>100 GB Root Volume included</p>
                  </div>
                  {!isReadOnly && (
                    <button onClick={() => removeByIdx(s._idx)} style={{ color: 'hsl(var(--danger))' }} className="ml-3 flex-shrink-0">
                      <Trash2 size={14} />
                    </button>
                  )}
                </div>
                {/* Server Purpose - identifies this server on build sheet and quote */}
                <div className="space-y-1">
                  <Label className="text-xs font-semibold">Server Purpose <span style={{ color: '#ef4444' }}>*</span>
                    <span className="ml-1.5 font-normal" style={{ color: 'hsl(var(--muted-fg))' }}>
                      - identifies this server on the build sheet and quote document
                    </span>
                  </Label>
                  <PurposeSelect
                    value={s.notes || ''}
                    onChange={v => updateByIdx(s._idx, 'notes', v)}
                    purposes={appServerPurposes}
                    disabled={isReadOnly}
                    testId={`wizard-server-purpose-${listIdx}`}
                  />
                </div>
                <StorageStepper label="Additional Root Volume" hint="100GB included · extra charged per 100GB block"
                  value={s.additional_root_gb} onChange={v => !isReadOnly && updateByIdx(s._idx, 'additional_root_gb', v)} />
              </div>
            ))}
          </div>
        )}

        {/* Add server */}
        {!isReadOnly && (
          <div className="space-y-3 pt-2" style={{ borderTop: '1px solid hsl(var(--border))' }}>
            <p className="text-sm font-semibold">Add Application Server
              <span className="ml-2 text-xs font-normal" style={{ color: 'hsl(var(--muted-fg))' }}>
                Each server is added individually with its own root volume and purpose note
              </span>
            </p>
            <div className="flex gap-2 flex-wrap mb-2">
              {Object.keys(appServerCatalog).map(os => (
                <button key={os} onClick={() => setOsFilter(os)}
                  className="px-3 py-1 rounded-md text-xs font-medium"
                  style={osFilter === os ? { background: 'hsl(var(--c4p-navy-900))', color: 'white' } : { background: 'hsl(var(--muted))', color: 'hsl(var(--muted-fg))' }}>
                  {os}
                </button>
              ))}
            </div>
            <div className="flex gap-2">
              <Select value={addCode} onValueChange={setAddCode}>
                <SelectTrigger className="flex-1"><SelectValue placeholder="Select server type..." /></SelectTrigger>
                <SelectContent>
                  {filteredCatalog.map(s => (
                    <SelectItem key={s.code} value={s.code}>{s.description}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button onClick={addServer} disabled={!addCode} style={{ background: 'hsl(var(--c4p-navy-900))', color: 'white' }} className="gap-1">
                <Plus size={14} /> Add
              </Button>
            </div>
          </div>
        )}
      </div>
    </Card>
  );
}

// ── Step 6: Extras ─────────────────────────────────────────────────────────────
function Step6Extras({ data, set, availableExtras, isStandalone, isReadOnly, onAIRecommend, aiRecommending, recommendations, Card }) {
  const sym_ = sym(data.currency);
  const getLine = id => data.extras.find(e => e.extra_id === id);
  const toggle = (extra) => {
    if (getLine(extra.id)) set({ extras: data.extras.filter(e => e.extra_id !== extra.id) });
    else set({ extras: [...data.extras, { extra_id: extra.id, extra_code: extra.code, extra_name: extra.name, quantity: 1, unit: extra.unit, pricing_type: extra.pricing_type }] });
  };
  const updateQty = (id, qty) => {
    if (qty <= 0) set({ extras: data.extras.filter(e => e.extra_id !== id) });
    else set({ extras: data.extras.map(e => e.extra_id === id ? { ...e, quantity: qty } : e) });
  };
  const addRec = (rec) => {
    if (!availableExtras) return;
    const cat = availableExtras.find(e => e.code === rec.code);
    if (cat && !getLine(cat.id)) set({ extras: [...data.extras, { extra_id: cat.id, extra_code: cat.code, extra_name: cat.name, quantity: 1, unit: cat.unit, pricing_type: cat.pricing_type }] });
  };
  return (
    <Card title={`Extras${data.extras.length > 0 ? ` (${data.extras.length} selected)` : ''}`}>
      {!isReadOnly && (
        <div className="mb-4">
          <Button variant="ghost" size="sm" onClick={onAIRecommend} disabled={aiRecommending || !data.num_users}
            className="gap-1.5 text-xs" style={{ color: 'hsl(var(--c4p-brand-blue))' }} data-testid="ai-recommend-button">
            {aiRecommending ? <Loader2 size={11} className="animate-spin" /> : <Lightbulb size={11} />}
            AI Recommendations
          </Button>
          {recommendations.length > 0 && (
            <div className="mt-2 space-y-1.5 p-3 rounded-lg" style={{ background: 'hsl(24 95% 96%)', border: '1px solid hsl(var(--c4p-orange-600) / 0.2)' }}>
              <p className="text-xs font-semibold" style={{ color: 'hsl(var(--c4p-orange-600))' }}>AI suggests:</p>
              {recommendations.map(r => (
                <div key={r.code} className="flex items-start justify-between gap-2">
                  <div><p className="text-xs font-medium">{r.name}</p><p className="text-xs" style={{ color: 'hsl(var(--muted-fg))' }}>{r.reason}</p></div>
                  <Button size="sm" variant="outline" className="h-6 text-xs flex-shrink-0" onClick={() => addRec(r)}><Plus size={10} /> Add</Button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
      {availableExtras.length === 0 ? <p className="text-sm" style={{ color: 'hsl(var(--muted-fg))' }}>No extras for this product.</p> : (
        <div className="space-y-2">
          {/* VPC: show as Included for Advanced/MTPro (but NOT for Standalone), or as a regular extra for Standard */}
          {['ADVANCED', 'MTPRO'].includes(data.product_code) && !isStandalone && (() => {
            const vpc = availableExtras.find(e => isVpcExtra(e));
            if (!vpc) return null;
            return (
              <div key={vpc.id} className="flex items-center justify-between p-3 rounded-lg"
                style={{ background: 'hsl(196 75% 56% / 0.08)', border: '1px solid hsl(var(--c4p-brand-blue) / 0.3)' }}>
                <div>
                  <p className="text-sm font-medium">{vpc.name}</p>
                  <p className="text-xs" style={{ color: 'hsl(var(--muted-fg))' }}>Included with {data.product_code === 'MTPRO' ? 'C4P MTPro' : 'C4P Advanced'} - no extra charge</p>
                </div>
                <span className="text-xs font-bold px-2 py-0.5 rounded-full"
                  style={{ background: 'hsl(var(--c4p-brand-blue))', color: 'white' }}>Included</span>
              </div>
            );
          })()}

          {/* 2 Databases Included: informational card for C4P Standard */}
          {!isStandalone && data.product_code === 'STANDARD' && (
            <div className="flex items-center justify-between p-3 rounded-lg"
              style={{ background: 'hsl(196 75% 56% / 0.08)', border: '1px solid hsl(var(--c4p-brand-blue) / 0.3)' }}>
              <div>
                <p className="text-sm font-medium">2 Databases</p>
                <p className="text-xs" style={{ color: 'hsl(var(--muted-fg))' }}>Production + UAT/test database instances included in C4P Standard — add more below if required</p>
              </div>
              <span className="text-xs font-bold px-2 py-0.5 rounded-full"
                style={{ background: 'hsl(var(--c4p-brand-blue))', color: 'white' }}>Included</span>
            </div>
          )}

          {/* File Storage: interactive stepper for Adv/MTPro/Standard (replaces CLO-Storage extra) */}
          {!isStandalone && ['ADVANCED', 'MTPRO'].includes(data.product_code) && (
            <div className="rounded-xl p-4 space-y-2.5" style={{ background: 'hsl(196 75% 56% / 0.06)', border: '1px solid hsl(var(--c4p-brand-blue) / 0.25)' }}>
              <div className="flex items-center justify-between">
                <p className="text-xs font-bold uppercase tracking-wide" style={{ color: 'hsl(var(--c4p-brand-blue))' }}>File Storage</p>
                <span className="text-xs font-bold px-2 py-0.5 rounded-full" style={{ background: 'hsl(var(--c4p-brand-blue))', color: 'white' }}>100GB included</span>
              </div>
              <StorageStepper
                label="File Storage"
                hint={`100GB included in plan · ${(data.file_storage_gb || 100) > 100 ? `${(data.file_storage_gb || 100) - 100}GB additional (charged)` : 'no extra charge'}`}
                value={data.file_storage_gb || 100}
                min={100}
                onChange={v => set({ file_storage_gb: v })}
              />
            </div>
          )}
          {!isStandalone && data.product_code === 'STANDARD' && (
            <div className="rounded-xl p-4 space-y-2.5" style={{ background: 'hsl(196 75% 56% / 0.06)', border: '1px solid hsl(var(--c4p-brand-blue) / 0.25)' }}>
              <div className="flex items-center justify-between">
                <p className="text-xs font-bold uppercase tracking-wide" style={{ color: 'hsl(var(--c4p-brand-blue))' }}>File Storage</p>
                <span className="text-xs font-bold px-2 py-0.5 rounded-full" style={{ background: 'hsl(var(--c4p-brand-blue))', color: 'white' }}>10GB included</span>
              </div>
              <StorageStepper
                label="File Storage"
                hint={`10GB included in plan · ${(data.file_storage_gb || 10) > 10 ? `${(data.file_storage_gb || 10) - 10}GB additional (charged)` : 'no extra charge'}`}
                value={data.file_storage_gb || 10}
                min={10}
                onChange={v => set({ file_storage_gb: v })}
              />
            </div>
          )}
          {isStandalone && (
            <div className="rounded-xl p-4 space-y-2.5" style={{ background: 'hsl(196 75% 56% / 0.06)', border: '1px solid hsl(var(--c4p-brand-blue) / 0.25)' }}>
              <p className="text-xs font-bold uppercase tracking-wide" style={{ color: 'hsl(var(--c4p-brand-blue))' }}>File Storage</p>
              <StorageStepper
                label="File Storage"
                hint={`${data.file_storage_gb > 0 ? `${data.file_storage_gb}GB selected (charged)` : 'None selected - add in 10 or 100GB blocks'}`}
                value={data.file_storage_gb || 0}
                min={0}
                onChange={v => set({ file_storage_gb: v })}
              />
            </div>
          )}
          {availableExtras.filter(e => !((['ADVANCED', 'MTPRO'].includes(data.product_code) && !isStandalone) && isVpcExtra(e))
            && !isFileStorageExtra(e)  // File Storage handled via stepper for all products
          ).map(extra => {
            const line = getLine(extra.id);
            const price = extra.prices_by_currency?.[data.currency] || 0;
            return (
              <div key={extra.id} className="flex items-center justify-between p-3 rounded-lg"
                style={{ background: line ? 'hsl(var(--accent))' : 'hsl(var(--muted))', border: line ? '1px solid hsl(var(--c4p-orange-600) / 0.3)' : '1px solid transparent' }}>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium truncate">{extra.name}</p>
                  <p className="text-xs" style={{ color: 'hsl(var(--muted-fg))' }}>{sym_}{price.toFixed(2)}/{extra.unit}{extra.pricing_type === 'recurring' ? ' · monthly' : ' · one-off'}</p>
                </div>
                {!isReadOnly && (
                  <div className="flex items-center gap-2 ml-3">
                    {line ? (
                      <>
                        <button onClick={() => updateQty(extra.id, line.quantity - 1)} className="w-7 h-7 rounded-full flex items-center justify-center" style={{ background: 'hsl(var(--border))' }}><Minus size={12} /></button>
                        <Input type="number" min="0.5" step="0.5" value={line.quantity} onChange={e => updateQty(extra.id, parseFloat(e.target.value))} className="w-14 h-7 text-center tabular-nums" />
                        <button onClick={() => updateQty(extra.id, line.quantity + 1)} className="w-7 h-7 rounded-full flex items-center justify-center" style={{ background: 'hsl(var(--border))' }}><Plus size={12} /></button>
                        <button onClick={() => toggle(extra)} style={{ color: 'hsl(var(--danger))' }}><Trash2 size={13} /></button>
                      </>
                    ) : (
                      <Button size="sm" variant="outline" className="h-7 gap-1" onClick={() => toggle(extra)}
                        data-testid={`add-extra-${extra.code}`}><Plus size={12} /> Add</Button>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
}

// ── Step 7: Review ────────────────────────────────────────────────────────────
function Step7Review({ data, customers, tenants, pricingConfig, psmUsers, pricingResult: r, pricingError, calculating, isAdvanced, Card }) {
  const customer = customers.find(c => c.id === data.customer_id);
  const tenant   = tenants?.find(t => t.id === data.tenant_id);
  const s = sym(data.currency);
  const additionalServers = data.app_servers.filter(sv => !sv.is_included_in_pupm);
  const includedServer    = data.app_servers.find(sv => sv.is_included_in_pupm);
  const region = (pricingConfig?.regions || []).find(r => r.id === data.region_id);
  const psm = psmUsers?.find(u => u.id === data.psm_user_id);
  const BILLING_LABELS = { PARTNER: 'Partner Billing', DIRECT: 'Direct (No Tenant)', TENANT_DIRECT: 'Tenant Direct' };
  const tenantDisplay = tenant?.company_name || data.tenant_name || data.end_customer_name;
  const dbTypeLabel = (t) => ({ HANA: 'SAP HANA', SQL: 'SQL Server', PRO4GL: 'Progress 4GL' }[t] || t);
  // C4P Custom: the per-server db_type lives on each extra_db_servers entry, not the
  // top-level data.db_type field - show every distinct type in use, not just HANA.
  const customDbTypesDisplay = data.is_standalone
    ? [...new Set((data.extra_db_servers || []).map(sv => sv.db_type))].map(dbTypeLabel).join(' and ')
    : '';
  const hideUserLines = data.is_standalone && (parseInt(data.num_users) || 0) === 0;
  const reviewLineItems = (r?.line_items || []).filter(l =>
    !hideUserLines || (l.category !== 'base' && l.category !== 'inclusion')
  );
  return (
    <Card title="Review Your Quote">
      <div className="space-y-4">
        {data.quote_name && (
          <div className="rounded-lg px-3 py-2.5" style={{ background: 'hsl(var(--muted))', border: '1px solid hsl(var(--border))' }}>
            <p className="text-xs uppercase tracking-wide mb-0.5" style={{ color: 'hsl(var(--muted-fg))' }}>Quote Name</p>
            <p className="font-semibold text-sm">{data.quote_name}</p>
          </div>
        )}
        <div className="grid grid-cols-2 gap-3 text-sm">
          {[
            ['Partner', customer?.company_name || '-'],
            tenantDisplay && data.billing_model !== 'DIRECT' ? ['Tenant', tenantDisplay] : null,
            ['Billing', BILLING_LABELS[data.billing_model] || data.billing_model],
            ['Region', region?.name || data.region_name || '-'],
            ['PSM', psm?.name || data.psm_user_name || '-'],
            ['Product', `C4P ${({'STANDARD':'Standard','ADVANCED':'Advanced','MTPRO':'MTPro'}[data.product_code] || data.product_code)}`],
            (!data.is_standalone || (data.extra_db_servers?.length > 0)) ? ['Database', data.is_standalone ? customDbTypesDisplay : dbTypeLabel(data.db_type)] : null,
            ['Users', data.num_users || '-'],
            ['Term', `${data.term_months} months`],
            ['Currency', data.currency],
            ['Billing Frequency', data.billing_frequency || 'Monthly'],
            data.payment_method ? ['Payment Method', data.payment_method] : null,
            data.expiry_date ? ['Expiry', new Date(data.expiry_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })] : null,
            data.use_next_available_slot
              ? ['Required By', 'Next available slot']
              : data.required_by_date ? ['Required By', new Date(data.required_by_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })] : null,
          ].filter(Boolean)
            .concat(r?.user_tier_label ? [['Tier', r.user_tier_label]] : [])
            .map(([k, v]) => (
              <div key={k}><p className="text-xs uppercase tracking-wide" style={{ color: 'hsl(var(--muted-fg))' }}>{k}</p><p className="font-semibold">{v}</p></div>
            ))}
        </div>

        {/* App Server summary with notes */}
        {(includedServer || additionalServers.length > 0) && (
          <div className="rounded-lg border p-3 space-y-2">
            <p className="text-xs font-bold uppercase tracking-wide" style={{ color: 'hsl(var(--muted-fg))' }}>Application Servers</p>
            {includedServer && (
              <div className="flex items-start justify-between gap-2 text-sm">
                <div>
                  <span className="font-medium">{includedServer.server_code}</span>
                  <span className="ml-2 text-xs px-1.5 py-0.5 rounded" style={{ background: 'hsl(196 75% 56% / 0.15)', color: 'hsl(var(--c4p-brand-blue))' }}>Included</span>
                  {includedServer.notes && <p className="text-xs font-semibold mt-0.5" style={{ color: 'hsl(var(--c4p-brand-blue))' }}>Purpose: {includedServer.notes}</p>}
                </div>
              </div>
            )}
            {additionalServers.map((sv, i) => (
              <div key={i} className="flex items-start justify-between gap-2 text-sm border-t pt-2" style={{ borderColor: 'hsl(var(--border))' }}>
                <div className="flex-1 min-w-0">
                  <p className="font-medium">{sv.server_code} <span className="text-xs font-normal" style={{ color: 'hsl(var(--muted-fg))' }}>#{i + 1}</span></p>
                  {sv.additional_root_gb > 0 && <p className="text-xs" style={{ color: 'hsl(var(--muted-fg))' }}>+{sv.additional_root_gb}GB root</p>}
                  {sv.notes && <p className="text-xs font-semibold mt-0.5" style={{ color: 'hsl(var(--c4p-brand-blue))' }}>Purpose: {sv.notes}</p>}
                </div>
              </div>
            ))}
          </div>
        )}

        {data.notes && <div className="rounded-lg p-3 text-sm" style={{ background: 'hsl(var(--muted))' }}>{data.notes}</div>}
        {r && (
          <>
            <Separator />
            <table className="w-full" style={{ borderCollapse: 'collapse', fontSize: '12px' }}>
              <thead>
                <tr style={{ background: 'hsl(var(--muted))', borderBottom: '1px solid hsl(var(--border))' }}>
                  <th className="text-right font-semibold px-2 py-2" style={{ color: 'hsl(var(--muted-fg))', width: '36px' }}>Qty</th>
                  <th className="text-left font-semibold px-2 py-2" style={{ color: 'hsl(var(--muted-fg))' }}>Description</th>
                  <th className="text-right font-semibold px-2 py-2" style={{ color: 'hsl(var(--muted-fg))', whiteSpace: 'nowrap' }}>Unit Price</th>
                  <th className="text-right font-semibold px-2 py-2" style={{ color: 'hsl(var(--muted-fg))', whiteSpace: 'nowrap' }}>Monthly</th>
                </tr>
              </thead>
              <tbody>
                {reviewLineItems.map((l, i) => {
                  const qty = l.qty != null ? (l.qty === Math.floor(l.qty) ? Math.floor(l.qty) : l.qty) : '-';
                  const unitPrice = l.unit_price != null && l.unit_price > 0
                    ? `${s}${Number(l.unit_price).toLocaleString('en-GB', { minimumFractionDigits: 2 })}`
                    : l.is_included ? '' : '-';
                  const monthly = l.is_included ? 'Included'
                    : l.monthly_amount > 0 ? `${s}${Number(l.monthly_amount).toLocaleString('en-GB', { minimumFractionDigits: 2 })}`
                    : l.oneoff_amount > 0 ? `${s}${Number(l.oneoff_amount).toLocaleString('en-GB', { minimumFractionDigits: 2 })} (one-off)`
                    : '-';
                  return (
                    <tr key={i} style={{ borderBottom: '1px solid hsl(var(--border))', opacity: l.is_included ? 0.6 : 1 }}>
                      <td className="text-right tabular-nums px-2 py-2" style={{ color: 'hsl(var(--muted-fg))', verticalAlign: 'top' }}>{qty}</td>
                      <td className="px-2 py-2" style={{ verticalAlign: 'top' }}>
                        <p className={`font-semibold ${l.is_included ? 'text-xs' : ''}`} style={{ color: l.is_included ? 'hsl(var(--muted-fg))' : NAVY }}>{l.label}</p>
                        {l.sub_label && <p className="text-xs mt-0.5" style={{ color: 'hsl(var(--muted-fg))' }}>{l.sub_label}</p>}
                        {l.item_code && <p className="font-mono mt-0.5" style={{ color: 'hsl(var(--muted-fg))', fontSize: '10px', opacity: 0.7 }}>{l.item_code}</p>}
                      </td>
                      <td className="text-right tabular-nums px-2 py-2 text-xs" style={{ color: 'hsl(var(--muted-fg))', verticalAlign: 'top', whiteSpace: 'nowrap' }}>{unitPrice}</td>
                      <td className={`text-right tabular-nums px-2 py-2 font-bold`} style={{ color: l.is_included ? 'hsl(var(--muted-fg))' : NAVY, verticalAlign: 'top', whiteSpace: 'nowrap', fontSize: l.is_included ? '11px' : '12px' }}>{monthly}</td>
                    </tr>
                  );
                })}
                {/* Monthly total + VAT rows */}
                <tr style={{ background: 'hsl(218 52% 11%)', color: 'white' }}>
                  <td colSpan={3} className="px-2 py-2 text-right text-xs font-bold uppercase tracking-wide" style={{ color: 'rgba(255,255,255,0.7)' }}>
                    Monthly Total {data.currency === 'GBP' ? '(ex. VAT)' : '(ex. tax)'} - {data.currency}
                  </td>
                  <td className="px-2 py-2 text-right font-bold tabular-nums" style={{ whiteSpace: 'nowrap' }}>
                    {s}{Number(r.monthly_total).toLocaleString('en-GB', { minimumFractionDigits: 2 })}
                  </td>
                </tr>
                {data.currency === 'GBP' && r.monthly_total > 0 && (() => {
                  const reviewVat = Math.round(r.monthly_total * 0.20 * 100) / 100;
                  const reviewIncVat = r.monthly_total + reviewVat;
                  return (<>
                    <tr style={{ borderTop: '1px solid hsl(var(--border))' }}>
                      <td colSpan={3} className="px-2 py-1.5 text-right text-xs font-semibold" style={{ color: '#d97706' }}>VAT @ 20%</td>
                      <td className="px-2 py-1.5 text-right text-xs font-semibold tabular-nums" style={{ color: '#d97706', whiteSpace: 'nowrap' }}>
                        {s}{reviewVat.toLocaleString('en-GB', { minimumFractionDigits: 2 })}
                      </td>
                    </tr>
                    <tr style={{ background: '#fff7ed' }}>
                      <td colSpan={3} className="px-2 py-2 text-right text-xs font-bold">Monthly Total (inc. VAT)</td>
                      <td className="px-2 py-2 text-right text-xs font-bold tabular-nums" style={{ color: '#c2410c', whiteSpace: 'nowrap' }}>
                        {s}{reviewIncVat.toLocaleString('en-GB', { minimumFractionDigits: 2 })}
                      </td>
                    </tr>
                  </>);
                })()}
              </tbody>
            </table>
          </>
        )}
      </div>
    </Card>
  );
}


// ── Step 8: Manual Contract Price Override ─────────────────────────────────────
function Step8ManualPricing({ data, pricingResult, manualOverrides, setManualOverrides, Card }) {
  const CURRENCY_SYMBOLS_LOCAL = { GBP: '£', EUR: '€', USD: '$', CAD: 'CA$', AUD: 'A$' };
  const currSym = CURRENCY_SYMBOLS_LOCAL[data.currency] || data.currency;
  const isTenantDirect = data.billing_model === 'TENANT_DIRECT';
  const pupmMargin = parseFloat(data.partner_margin_pupm) || 0;
  const nonUserMarginPct = parseFloat(data.partner_margin_percent) || 0;

  // Default tenant price via the standard margin formula - the admin can
  // override any line independently from here.
  const formulaTenantPrice = (li, unitPrice) => {
    const val = li.category === 'base' ? (unitPrice + pupmMargin) : (unitPrice * (1 + nonUserMarginPct / 100));
    return Math.round(val * 100) / 100;
  };

  // Initialise overrides from pricingResult on first render
  React.useEffect(() => {
    if (pricingResult && manualOverrides.length === 0) {
      const lines = (pricingResult.line_items || []).filter(li => !li.is_included && li.monthly_amount > 0);
      setManualOverrides(lines.map(li => {
        const unitPrice = li.unit_price ?? li.monthly_amount;
        const entry = {
          category: li.category,
          label: li.label,
          sub_label: li.sub_label || '',
          qty: li.qty ?? 1,
          unit_price: unitPrice,
          monthly_amount: li.monthly_amount,
        };
        if (isTenantDirect) {
          const tenantUnit = formulaTenantPrice(li, unitPrice);
          entry.tenant_unit_price = tenantUnit;
          entry.tenant_monthly_amount = Math.round((li.qty ?? 1) * tenantUnit * 100) / 100;
        }
        return entry;
      }));
    }
  }, [pricingResult]); // eslint-disable-line react-hooks/exhaustive-deps

  const update = (idx, field, rawVal) => {
    setManualOverrides(prev => prev.map((item, i) => {
      if (i !== idx) return item;
      const val = parseFloat(rawVal) || 0;
      // Only unit_price is editable; qty always comes from the engine
      const updated = { ...item, [field]: val };
      // Recalculate monthly using engine qty (from pricingResult) if available
      const engineLine = (pricingResult?.line_items || []).find(
        l => l.category === item.category && l.label === item.label && !l.is_included
      );
      const currentQty = engineLine?.qty ?? item.qty ?? 1;
      updated.monthly_amount = Math.round(currentQty * updated.unit_price * 100) / 100;
      return updated;
    }));
  };

  // Table is driven by pricingResult (current engine output), not manualOverrides directly.
  // manualOverrides is a price lookup: category::label → unit_price (+ tenant_unit_price).
  const priceMap = {};
  const tenantPriceMap = {};
  manualOverrides.forEach(o => {
    const key = `${o.category}::${o.label}`;
    priceMap[key] = o.unit_price;
    if (o.tenant_unit_price != null) tenantPriceMap[key] = o.tenant_unit_price;
  });

  const updateByKey = (category, label, rawVal) => {
    const val = parseFloat(rawVal) || 0;
    const key = `${category}::${label}`;
    setManualOverrides(prev => {
      const exists = prev.some(o => `${o.category}::${o.label}` === key);
      if (exists) return prev.map(o => `${o.category}::${o.label}` === key ? { ...o, unit_price: val } : o);
      return [...prev, { category, label, unit_price: val, qty: 1, monthly_amount: val }];
    });
  };

  // Tenant price is fully independent from the Partner price - set directly, never derived.
  const updateTenantByKey = (category, label, rawVal) => {
    const val = parseFloat(rawVal) || 0;
    const key = `${category}::${label}`;
    setManualOverrides(prev => {
      const exists = prev.some(o => `${o.category}::${o.label}` === key);
      if (exists) return prev.map(o => `${o.category}::${o.label}` === key ? { ...o, tenant_unit_price: val } : o);
      return [...prev, { category, label, unit_price: 0, qty: 1, monthly_amount: 0, tenant_unit_price: val }];
    });
  };

  // Engine-driven display rows (removed items are automatically excluded)
  const displayRows = (pricingResult?.line_items || []).filter(li => !li.is_included && (li.monthly_amount ?? 0) > 0);

  // Compute the display total using engine qtys × manual unit_prices
  const manualTotal = displayRows.reduce((s, li) => {
    const key = `${li.category}::${li.label}`;
    const unitPrice = key in priceMap ? priceMap[key] : (li.unit_price ?? 0);
    return s + Math.round((li.qty ?? 1) * unitPrice * 100) / 100;
  }, 0);
  const tenantTotal = isTenantDirect ? displayRows.reduce((s, li) => {
    const key = `${li.category}::${li.label}`;
    const unitPrice = key in priceMap ? priceMap[key] : (li.unit_price ?? 0);
    const tenantUnit = key in tenantPriceMap ? tenantPriceMap[key] : formulaTenantPrice(li, unitPrice);
    return s + Math.round((li.qty ?? 1) * tenantUnit * 100) / 100;
  }, 0) : 0;
  const marginTotal = Math.round((tenantTotal - manualTotal) * 100) / 100;
  const engineTotal = (pricingResult?.monthly_total || 0);

  const CAT_LABELS = {
    base: 'Base',
    addon_users: 'Add-on Users',
    db_server: 'DB Server',
    db_storage: 'DB Storage',
    app_server: 'App Server',
    root_storage: 'Root Storage',
    extras: 'Extras',
    file_storage: 'File Storage',
  };

  const CAT_COLOURS = {
    base: { bg: '#eff6ff', color: '#1d4ed8' },
    addon_users: { bg: '#f0fdf4', color: '#15803d' },
    db_server: { bg: '#faf5ff', color: '#7e22ce' },
    db_storage: { bg: '#faf5ff', color: '#9333ea' },
    app_server: { bg: '#fff7ed', color: '#c2410c' },
    root_storage: { bg: '#fefce8', color: '#a16207' },
    extras: { bg: '#f0fdfa', color: '#0f766e' },
    file_storage: { bg: '#f0fdfa', color: '#0891b2' },
  };

  const NAVY = 'hsl(var(--c4p-navy-900))';

  if (!pricingResult) {
    return (
      <Card title="Manual Price Override">
        <p className="text-sm" style={{ color: 'hsl(var(--muted-fg))' }}>
          No pricing calculated yet. Please complete the previous steps and ensure pricing is loaded on the Review step.
        </p>
      </Card>
    );
  }

  return (
    <Card title="Manual Contract — Price Override">
      <div className="space-y-4">
        <div className="rounded-lg p-3 flex items-start gap-2" style={{ background: '#fff7ed', border: '1px solid #fdba74' }}>
          <AlertTriangle size={15} style={{ color: '#ea580c', flexShrink: 0, marginTop: 1 }} />
          <p className="text-xs" style={{ color: '#9a3412' }}>
            <strong>Super Admin only.</strong> Adjust the quantity and unit price for each line item below. The database prices are pre-filled.
            {isTenantDirect
              ? ' Tenant Direct billing: set the Partner and Tenant price independently for each line — they no longer have to follow the margin formula.'
              : ' Any changes here will be stored as the contract\'s bespoke pricing.'}
          </p>
        </div>

        {/* Diff notice */}
        {Math.abs(manualTotal - engineTotal) > 0.01 && (
          <div className="rounded-lg p-3 flex items-center justify-between" style={{ background: '#f0fdf4', border: '1px solid #bbf7d0' }}>
            <span className="text-xs font-medium" style={{ color: '#166534' }}>
              Override total differs from engine total by {currSym}{Math.abs(manualTotal - engineTotal).toFixed(2)}/mo
            </span>
            <button
              className="text-xs underline"
              style={{ color: '#166534' }}
              onClick={() => setManualOverrides([])}
              data-testid="manual-pricing-reset-btn"
            >
              Reset to engine prices
            </button>
          </div>
        )}

        <div className="overflow-x-auto rounded-xl border" style={{ borderColor: 'hsl(var(--border))' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
            <thead>
              <tr style={{ background: 'hsl(var(--muted))', borderBottom: '1px solid hsl(var(--border))' }}>
                <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide" style={{ color: 'hsl(var(--muted-fg))', width: '110px' }}>Category</th>
                <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide" style={{ color: 'hsl(var(--muted-fg))' }}>Line Item</th>
                <th className="px-3 py-2 text-right text-xs font-semibold uppercase tracking-wide" style={{ color: 'hsl(var(--muted-fg))', width: '60px' }}>Qty</th>
                <th className="px-3 py-2 text-right text-xs font-semibold uppercase tracking-wide" style={{ color: 'hsl(var(--muted-fg))', width: '120px' }}>{isTenantDirect ? 'Partner Price' : 'Unit Price'} <span style={{fontWeight:400,textTransform:'none'}}>(editable)</span></th>
                <th className="px-3 py-2 text-right text-xs font-semibold uppercase tracking-wide" style={{ color: 'hsl(var(--muted-fg))', width: '100px' }}>{isTenantDirect ? 'Partner Monthly' : 'Monthly'}</th>
                {isTenantDirect && (
                  <th className="px-3 py-2 text-right text-xs font-semibold uppercase tracking-wide" style={{ color: 'hsl(265 89% 45%)', width: '120px' }}>Tenant Price <span style={{fontWeight:400,textTransform:'none'}}>(editable)</span></th>
                )}
                {isTenantDirect && (
                  <th className="px-3 py-2 text-right text-xs font-semibold uppercase tracking-wide" style={{ color: 'hsl(265 89% 45%)', width: '100px' }}>Tenant Monthly</th>
                )}
                {isTenantDirect && (
                  <th className="px-3 py-2 text-right text-xs font-semibold uppercase tracking-wide" style={{ color: 'hsl(var(--muted-fg))', width: '90px' }}>Margin</th>
                )}
              </tr>
            </thead>
            <tbody>
              {displayRows.map((li, idx) => {
                const colours = CAT_COLOURS[li.category] || { bg: '#f8fafc', color: '#475569' };
                const key = `${li.category}::${li.label}`;
                const displayQty = li.qty ?? 1;
                const unitPrice = key in priceMap ? priceMap[key] : (li.unit_price ?? 0);
                const displayMonthly = Math.round(displayQty * unitPrice * 100) / 100;
                const tenantUnit = isTenantDirect ? (key in tenantPriceMap ? tenantPriceMap[key] : formulaTenantPrice(li, unitPrice)) : null;
                const tenantMonthly = isTenantDirect ? Math.round(displayQty * tenantUnit * 100) / 100 : null;
                const lineMargin = isTenantDirect ? Math.round((tenantMonthly - displayMonthly) * 100) / 100 : null;
                return (
                  <tr key={`${li.category}-${li.label}-${idx}`} style={{ borderBottom: '1px solid hsl(var(--border) / 0.5)' }}>
                    <td className="px-3 py-2">
                      <span className="text-xs font-medium px-1.5 py-0.5 rounded-full whitespace-nowrap"
                        style={{ background: colours.bg, color: colours.color }}>
                        {CAT_LABELS[li.category] || li.category}
                      </span>
                    </td>
                    <td className="px-3 py-2">
                      <p className="font-medium text-xs" style={{ color: NAVY }}>{li.label}</p>
                      {li.sub_label && <p className="text-xs mt-0.5" style={{ color: 'hsl(var(--muted-fg))' }}>{li.sub_label}</p>}
                    </td>
                    <td className="px-3 py-2 text-right">
                      <span className="font-semibold text-sm" style={{ color: NAVY }}
                        data-testid={`manual-override-qty-${idx}`}>
                        {displayQty}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <span className="text-xs font-medium" style={{ color: 'hsl(var(--muted-fg))' }}>{currSym}</span>
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={unitPrice}
                          onChange={e => updateByKey(li.category, li.label, e.target.value)}
                          data-testid={`manual-override-price-${idx}`}
                          style={{
                            width: '90px', textAlign: 'right', fontWeight: 600, fontSize: '13px',
                            background: 'white', border: '1.5px solid hsl(var(--border))',
                            borderRadius: '6px', padding: '3px 8px', outline: 'none',
                          }}
                        />
                      </div>
                    </td>
                    <td className="px-3 py-2 text-right">
                      <span className="font-bold text-sm" style={{ color: NAVY }}>
                        {currSym}{displayMonthly.toFixed(2)}
                      </span>
                    </td>
                    {isTenantDirect && (
                      <td className="px-3 py-2 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <span className="text-xs font-medium" style={{ color: 'hsl(265 89% 45%)' }}>{currSym}</span>
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={tenantUnit}
                            onChange={e => updateTenantByKey(li.category, li.label, e.target.value)}
                            data-testid={`manual-override-tenant-price-${idx}`}
                            style={{
                              width: '90px', textAlign: 'right', fontWeight: 600, fontSize: '13px',
                              background: 'hsl(265 89% 98%)', border: '1.5px solid hsl(265 89% 80%)',
                              borderRadius: '6px', padding: '3px 8px', outline: 'none', color: 'hsl(265 89% 35%)',
                            }}
                          />
                        </div>
                      </td>
                    )}
                    {isTenantDirect && (
                      <td className="px-3 py-2 text-right" data-testid={`manual-override-tenant-monthly-${idx}`}>
                        <span className="font-bold text-sm" style={{ color: 'hsl(265 89% 40%)' }}>
                          {currSym}{tenantMonthly.toFixed(2)}
                        </span>
                      </td>
                    )}
                    {isTenantDirect && (
                      <td className="px-3 py-2 text-right" data-testid={`manual-override-margin-${idx}`}>
                        <span className="font-medium text-xs" style={{ color: lineMargin >= 0 ? '#15803d' : '#dc2626' }}>
                          {lineMargin >= 0 ? '+' : ''}{currSym}{lineMargin.toFixed(2)}
                        </span>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr style={{ borderTop: '2px solid hsl(var(--border))', background: 'hsl(var(--muted) / 0.5)' }}>
                <td colSpan={4} className="px-3 py-3 text-right font-bold text-sm" style={{ color: NAVY }}>
                  Total Monthly {isTenantDirect ? '(Partner)' : ''}
                </td>
                <td className="px-3 py-3 text-right">
                  <span className="font-bold text-base" style={{ color: NAVY }} data-testid="manual-pricing-total">
                    {currSym}{manualTotal.toFixed(2)}
                  </span>
                </td>
                {isTenantDirect && <td className="px-3 py-3" />}
                {isTenantDirect && (
                  <td className="px-3 py-3 text-right">
                    <span className="font-bold text-base" style={{ color: 'hsl(265 89% 35%)' }} data-testid="manual-pricing-tenant-total">
                      {currSym}{tenantTotal.toFixed(2)}
                    </span>
                  </td>
                )}
                {isTenantDirect && (
                  <td className="px-3 py-3 text-right">
                    <span className="font-bold text-sm" style={{ color: marginTotal >= 0 ? '#15803d' : '#dc2626' }} data-testid="manual-pricing-margin-total">
                      {marginTotal >= 0 ? '+' : ''}{currSym}{marginTotal.toFixed(2)}
                    </span>
                  </td>
                )}
              </tr>
            </tfoot>
          </table>
        </div>
        <p className="text-xs" style={{ color: 'hsl(var(--muted-fg))' }}>
          Click <strong>Save Manual Quote</strong> to store these prices. You can convert this quote to a live contract from the Quote Detail page.
        </p>
      </div>
    </Card>
  );
}


// ── Step 8: MTPro Tenant Breakdown (linked to real tenant records) ─────────────
const TENANT_STATUS = [
  { value: 'new',           label: 'New Tenant',            color: '#16a34a' },
  { value: 'existing',      label: 'Existing',              color: '#0ea5e9' },
  { value: 'adding_users',  label: 'Adding Users',          color: '#d97706' },
];

function Step8MtproTenants({ data, set, isReadOnly, Card }) {
  const { tenantsAPI } = require('../../services/api');
  const [partnerTenants, setPartnerTenants] = useState([]);
  const [loadingTenants, setLoadingTenants] = useState(false);
  const [showRowForm, setShowRowForm]     = useState(false);
  const [editId, setEditId]               = useState(null);
  const [newTenantMode, setNewTenantMode] = useState(false); // creating a new tenant inline
  const emptyRow = { tenant_id: '', tenant_name: '', num_users: '', status: 'new', assigned_servers: [], notes: '' };
  const [form, setForm] = useState({ ...emptyRow });
  const [newTenantName, setNewTenantName] = useState('');
  const [saving, setSaving] = useState(false);

  const totalUsers     = parseInt(data.num_users) || 0;
  const allocatedUsers = (data.mtpro_tenants || []).reduce((s, t) => s + (parseInt(t.num_users) || 0), 0);
  const remaining      = totalUsers - allocatedUsers;

  // Load partner's MTPro tenants for the selector
  useEffect(() => {
    if (!data.customer_id) return;
    setLoadingTenants(true);
    tenantsAPI.list({ partner_id: data.customer_id, tenant_type: 'mtpro', page_size: 100 })
      .then(r => setPartnerTenants(r.data?.items || []))
      .catch(() => {})
      .finally(() => setLoadingTenants(false));
  }, [data.customer_id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Build app server options from the quote
  const serverOptions = (data.app_servers || []).map((s, idx) => {
    const label = s.notes
      ? s.notes
      : s.is_included_in_pupm
        ? `Included Server (${s.server_code})`
        : `App Server #${(data.app_servers.filter((x, i) => i <= idx && !x.is_included_in_pupm).length)} (${s.server_code})`;
    return label;
  });

  const openNew = () => { setEditId(null); setForm({ ...emptyRow }); setNewTenantMode(false); setNewTenantName(''); setShowRowForm(true); };
  const openEdit = (t) => {
    setEditId(t.id);
    setForm({ tenant_id: t.tenant_id || '', tenant_name: t.tenant_name, num_users: String(t.num_users), status: t.status || 'new', assigned_servers: t.assigned_servers || [], notes: t.notes || '' });
    setNewTenantMode(false);
    setShowRowForm(true);
  };

  const handleSelectTenant = (tenantId) => {
    if (tenantId === '__new__') {
      setNewTenantMode(true);
      setForm(f => ({ ...f, tenant_id: '', tenant_name: '' }));
    } else {
      const t = partnerTenants.find(t => t.id === tenantId);
      if (t) setForm(f => ({ ...f, tenant_id: t.id, tenant_name: t.company_name, num_users: String(t.num_users || ''), status: 'existing' }));
      setNewTenantMode(false);
    }
  };

  const handleSaveRow = async () => {
    const name = newTenantMode ? newTenantName.trim() : form.tenant_name;
    if (!name) return toast.error('Tenant name is required');
    setSaving(true);
    try {
      let tenantId = form.tenant_id;
      // Create new MTPro tenant in the database
      if (newTenantMode && newTenantName.trim()) {
        const r = await tenantsAPI.create({
          company_name: newTenantName.trim(),
          tenant_type: 'mtpro',
          num_users: parseInt(form.num_users) || 0,
          partner_id: data.customer_id,
          partner_name: '',
        });
        tenantId = r.data?.id || '';
        // Refresh partner tenants list
        tenantsAPI.list({ partner_id: data.customer_id, tenant_type: 'mtpro', page_size: 100 })
          .then(r => setPartnerTenants(r.data?.items || [])).catch(() => {});
      } else if (tenantId) {
        // Update user count on existing tenant
        await tenantsAPI.patch(tenantId, { num_users: parseInt(form.num_users) || 0 });
      }
      const entry = {
        id: editId || crypto.randomUUID(),
        tenant_id: tenantId,
        tenant_name: name,
        num_users: parseInt(form.num_users) || 0,
        status: form.status,
        assigned_servers: form.assigned_servers,
        notes: form.notes.trim(),
      };
      if (editId) {
        set({ mtpro_tenants: (data.mtpro_tenants || []).map(t => t.id === editId ? entry : t) });
      } else {
        set({ mtpro_tenants: [...(data.mtpro_tenants || []), entry] });
      }
      setShowRowForm(false);
    } catch { toast.error('Failed to save tenant'); }
    finally { setSaving(false); }
  };

  const handleDelete = (id) => set({ mtpro_tenants: (data.mtpro_tenants || []).filter(t => t.id !== id) });
  const toggleServer = (label) => {
    const cur = form.assigned_servers || [];
    setForm(f => ({ ...f, assigned_servers: cur.includes(label) ? cur.filter(s => s !== label) : [...cur, label] }));
  };
  const statusMeta = (val) => TENANT_STATUS.find(s => s.value === val) || TENANT_STATUS[0];

  // Already-added tenant IDs (to prevent duplicates in dropdown)
  const addedTenantIds = new Set((data.mtpro_tenants || []).map(t => t.tenant_id).filter(Boolean));
  const availableTenants = partnerTenants.filter(t => !addedTenantIds.has(t.id));

  return (
    <Card title="MTPro Tenant Breakdown">
      <div className="space-y-4">
        {/* Info banner */}
        <div className="rounded-lg p-3 text-sm" style={{ background: 'hsl(196 75% 56% / 0.07)', border: '1px solid hsl(var(--c4p-brand-blue) / 0.2)' }}>
          <p style={{ color: NAVY }}>Optionally record the end customers (tenants) that will use this MTPro system. You can skip this and add tenants later. Select from existing MTPro tenants or create new ones.</p>
        </div>

        {/* Allocation bar */}
        <div className="flex items-center gap-4 flex-wrap">
          <div className="text-sm flex-1">
            <span className="font-bold" style={{ color: NAVY }}>{allocatedUsers}</span>
            <span style={{ color: 'hsl(var(--muted-fg))' }}> / {totalUsers} users allocated across {(data.mtpro_tenants || []).length} tenant{(data.mtpro_tenants || []).length !== 1 ? 's' : ''}</span>
            {remaining > 0 && allocatedUsers > 0 && <span className="ml-2 text-xs font-medium px-2 py-0.5 rounded-full" style={{ background: '#fef3c7', color: '#92400e' }}>{remaining} unallocated</span>}
            {remaining < 0 && <span className="ml-2 text-xs font-medium px-2 py-0.5 rounded-full" style={{ background: '#fee2e2', color: '#991b1b' }}>{Math.abs(remaining)} over</span>}
            {remaining === 0 && allocatedUsers > 0 && <span className="ml-2 text-xs font-medium px-2 py-0.5 rounded-full" style={{ background: '#dcfce7', color: '#166534' }}>Fully allocated</span>}
          </div>
          {!isReadOnly && (
            <button onClick={openNew} className="flex items-center gap-1.5 text-sm font-semibold px-3 py-1.5 rounded-lg"
              style={{ background: NAVY, color: 'white' }} data-testid="mtpro-add-tenant">
              <Plus size={13} /> Add Tenant
            </button>
          )}
        </div>
        {totalUsers > 0 && allocatedUsers > 0 && (
          <div className="h-1.5 rounded-full overflow-hidden" style={{ background: 'hsl(var(--border))' }}>
            <div className="h-full rounded-full transition-all" style={{ width: `${Math.min(100, (allocatedUsers / totalUsers) * 100)}%`, background: remaining < 0 ? '#dc2626' : remaining === 0 ? '#16a34a' : 'hsl(var(--c4p-brand-blue))' }} />
          </div>
        )}

        {/* Tenant rows */}
        {(data.mtpro_tenants || []).length === 0 ? (
          <div className="rounded-xl border border-dashed p-8 text-center" style={{ color: 'hsl(var(--muted-fg))' }}>
            <Users size={24} className="mx-auto mb-2 opacity-30" />
            <p className="text-sm">No tenants added yet - this section is optional.</p>
          </div>
        ) : (
          <div className="overflow-hidden rounded-xl border">
            <table className="w-full" style={{ borderCollapse: 'collapse', fontSize: '13px' }}>
              <thead>
                <tr style={{ background: 'hsl(var(--muted))', borderBottom: '1px solid hsl(var(--border))' }}>
                  <th className="text-left px-3 py-2 font-semibold text-xs" style={{ color: 'hsl(var(--muted-fg))' }}>Tenant</th>
                  <th className="text-center px-3 py-2 font-semibold text-xs w-20" style={{ color: 'hsl(var(--muted-fg))' }}>Users</th>
                  <th className="text-center px-3 py-2 font-semibold text-xs w-28" style={{ color: 'hsl(var(--muted-fg))' }}>Status</th>
                  <th className="text-left px-3 py-2 font-semibold text-xs" style={{ color: 'hsl(var(--muted-fg))' }}>App Servers</th>
                  {!isReadOnly && <th className="w-16" />}
                </tr>
              </thead>
              <tbody>
                {(data.mtpro_tenants || []).map((t, idx) => {
                  const sm = statusMeta(t.status);
                  return (
                    <tr key={t.id} style={{ borderBottom: '1px solid hsl(var(--border))' }} data-testid={`mtpro-tenant-${idx}`}>
                      <td className="px-3 py-2.5">
                        <p className="font-semibold" style={{ color: NAVY }}>{t.tenant_name}</p>
                        {t.notes && <p className="text-xs italic" style={{ color: 'hsl(var(--muted-fg))' }}>{t.notes}</p>}
                      </td>
                      <td className="px-3 py-2.5 text-center font-bold tabular-nums" style={{ color: 'hsl(var(--c4p-orange-600))' }}>{t.num_users}</td>
                      <td className="px-3 py-2.5 text-center">
                        <span className="text-xs font-medium px-2 py-0.5 rounded-full" style={{ background: sm.color + '1a', color: sm.color }}>{sm.label}</span>
                      </td>
                      <td className="px-3 py-2.5 text-xs" style={{ color: 'hsl(var(--muted-fg))' }}>
                        {t.assigned_servers?.length > 0 ? t.assigned_servers.join(' · ') : <span style={{ opacity: 0.5 }}>None</span>}
                      </td>
                      {!isReadOnly && (
                        <td className="px-2 py-2">
                          <div className="flex gap-0.5">
                            <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => openEdit(t)} style={{ color: 'hsl(var(--muted-fg))' }}><svg xmlns="http://www.w3.org/2000/svg" width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg></Button>
                            <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => handleDelete(t.id)} style={{ color: 'hsl(var(--danger))' }}><Trash2 size={11} /></Button>
                          </div>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Add / Edit modal */}
      {showRowForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.5)' }} onClick={() => setShowRowForm(false)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 space-y-4 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <h3 className="font-bold text-lg" style={{ color: NAVY }}>{editId ? 'Edit Tenant Entry' : 'Add Tenant'}</h3>

            {/* Tenant selector - only on new entries */}
            {!editId && (
              <div className="space-y-1.5">
                <label className="text-sm font-semibold">Select or Create Tenant *</label>
                {loadingTenants ? (
                  <p className="text-xs" style={{ color: 'hsl(var(--muted-fg))' }}>Loading tenants…</p>
                ) : (
                  <select className="w-full border rounded-lg px-3 py-2 text-sm" value={newTenantMode ? '__new__' : (form.tenant_id || '')}
                    onChange={e => handleSelectTenant(e.target.value)}>
                    <option value="">- Select an MTPro tenant -</option>
                    {availableTenants.map(t => (
                      <option key={t.id} value={t.id}>{t.company_name}{t.num_users ? ` (${t.num_users} users)` : ''}</option>
                    ))}
                    <option value="__new__">+ Create new MTPro tenant…</option>
                  </select>
                )}
              </div>
            )}

            {/* New tenant name input */}
            {(newTenantMode || editId) && (
              <div className="space-y-1.5">
                <label className="text-sm font-semibold">{newTenantMode ? 'New Tenant Name *' : 'Tenant Name'}</label>
                {newTenantMode ? (
                  <input className="w-full border rounded-lg px-3 py-2 text-sm" placeholder="e.g. Acme Corporation"
                    value={newTenantName} onChange={e => setNewTenantName(e.target.value)} autoFocus />
                ) : (
                  <p className="text-sm font-semibold px-3 py-2 rounded-lg" style={{ background: 'hsl(var(--muted))' }}>{form.tenant_name}</p>
                )}
              </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <label className="text-sm font-semibold">User Count</label>
                <input type="number" min="0" className="w-full border rounded-lg px-3 py-2 text-sm" placeholder="0"
                  value={form.num_users} onChange={e => setForm(f => ({ ...f, num_users: e.target.value }))} />
                <p className="text-xs" style={{ color: 'hsl(var(--muted-fg))' }}>Updates the tenant record</p>
              </div>
              <div className="space-y-1.5">
                <label className="text-sm font-semibold">Status</label>
                <select className="w-full border rounded-lg px-3 py-2 text-sm" value={form.status}
                  onChange={e => setForm(f => ({ ...f, status: e.target.value }))}>
                  {TENANT_STATUS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                </select>
              </div>
            </div>

            {serverOptions.length > 0 && (
              <div className="space-y-1.5">
                <label className="text-sm font-semibold">App Servers assigned to this tenant</label>
                <div className="space-y-1.5 rounded-lg border p-2">
                  {serverOptions.map(label => (
                    <label key={label} className="flex items-center gap-2 cursor-pointer text-sm py-0.5">
                      <input type="checkbox" checked={(form.assigned_servers || []).includes(label)}
                        onChange={() => toggleServer(label)} style={{ accentColor: NAVY }} />
                      {label}
                    </label>
                  ))}
                </div>
              </div>
            )}
            <div className="space-y-1.5">
              <label className="text-sm font-semibold">Notes</label>
              <input className="w-full border rounded-lg px-3 py-2 text-sm" placeholder="Any context…"
                value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} />
            </div>
            <div className="flex gap-2 justify-end pt-1">
              <button onClick={() => setShowRowForm(false)} className="px-4 py-2 rounded-lg border text-sm font-semibold">Cancel</button>
              <button onClick={handleSaveRow} disabled={saving} className="px-4 py-2 rounded-lg text-sm font-semibold text-white flex items-center gap-1.5"
                style={{ background: NAVY }}>
                {saving ? <Loader2 size={13} className="animate-spin" /> : null}
                {editId ? 'Save Changes' : 'Add Tenant'}
              </button>
            </div>
          </div>
        </div>
      )}
    </Card>
  );
}


// ── Mini Summary Panel ─────────────────────────────────────────────────────────
function MiniSummary({ r, err, calculating, data, customer, existingQuote }) {
  const s = sym(data.currency);
  const fmt = (n) => n != null ? n.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '-';
  const isGBP = data.currency === 'GBP';
  const isTenantDirect = data.billing_model === 'TENANT_DIRECT';
  const dbTypeLabel = (t) => ({ HANA: 'SAP HANA', SQL: 'SQL Server', PRO4GL: 'Progress 4GL' }[t] || t);
  // C4P Custom: the per-server db_type lives on each extra_db_servers entry, not the
  // top-level data.db_type field - show every distinct type in use, not just HANA.
  const customDbTypesDisplay = data.is_standalone
    ? [...new Set((data.extra_db_servers || []).map(sv => sv.db_type))].map(dbTypeLabel).join(' and ')
    : '';

  // For Tenant Direct: apply partner margin to live pricing result so the
  // wizard summary matches what the tenant invoice will be after save.
  // If a live tenant_invoice_monthly is already attached (Manual Contract
  // Price Override step, computed per-line with bespoke overrides), use it
  // directly instead of recomputing via the blanket margin formula.
  const estimatedBase = r?.monthly_total ?? 0;
  const estimatedTotal = (() => {
    if (!isTenantDirect || !r) return estimatedBase;
    if (r.tenant_invoice_monthly != null) return r.tenant_invoice_monthly;
    const pupmMargin = (parseFloat(data.partner_margin_pupm) || 0) * (parseInt(data.num_users) || 0);
    const nonUserMonthly = estimatedBase - (r.monthly_base ?? 0);
    const nonUserMargin  = nonUserMonthly * ((parseFloat(data.partner_margin_percent) || 0) / 100);
    return Math.round((estimatedBase + pupmMargin + nonUserMargin) * 100) / 100;
  })();

  const vat    = isGBP && estimatedTotal > 0 ? Math.round(estimatedTotal * 0.20 * 100) / 100 : 0;
  const incVat = estimatedTotal + vat;
  const numUsersInt = parseInt(data.num_users) || 0;
  const hideUserLines = data.is_standalone && numUsersInt === 0;
  const allLines = (r?.line_items || []).filter(l =>
    !hideUserLines || (l.category !== 'base' && l.category !== 'inclusion')
  );

  return (
    <div className="rounded-xl border bg-white overflow-hidden"
         style={{ boxShadow: '0 4px 16px rgba(0,0,0,0.08)' }}
         data-testid="quote-summary-panel">

      {/* Monthly total header */}
      <div className="px-4 py-4"
           style={{ background: 'linear-gradient(150deg, hsl(218 52% 11%) 0%, hsl(210 48% 17%) 100%)' }}>
        <p className="text-xs font-bold uppercase tracking-widest mb-2"
           style={{ color: 'hsl(var(--c4p-brand-blue))' }}>Monthly Total</p>
        <div className="text-[2rem] font-bold tabular-nums leading-none"
             style={{ fontFamily: 'var(--font-display)', color: 'white' }}
             data-testid="quote-summary-total-amount">
          {calculating
            ? <span className="flex items-center gap-2 text-xl opacity-60"><RefreshCw size={14} className="animate-spin" /> Calculating</span>
            : r ? `${s}${fmt(estimatedTotal)}` : <span className="opacity-25">{s}-</span>}
        </div>
        {isGBP && r && estimatedTotal > 0 && (
          <p className="text-xs mt-1.5" style={{ color: 'rgba(255,255,255,0.45)' }}>
            {s}{fmt(incVat)} per month including VAT
          </p>
        )}
      </div>

      {/* Idle state */}
      {!r && !err && !calculating && (
        <div className="px-4 py-8 text-center">
          <p className="text-sm" style={{ color: 'hsl(var(--muted-fg))' }}>
            Select a product and enter user count to see live pricing
          </p>
        </div>
      )}

      {/* Error state */}
      {err && (
        <div className="px-4 py-3">
          <Alert variant="destructive" className="py-2">
            <AlertCircle className="h-3 w-3" />
            <AlertDescription className="text-xs">{typeof err === 'string' ? err : 'Pricing error'}</AlertDescription>
          </Alert>
        </div>
      )}

      {/* Quote breakdown table */}
      {r && allLines.length > 0 && (
        <>
          {/* Customer context bar */}
          {customer && (
            <div className="px-4 py-2.5" style={{ background: 'hsl(var(--muted) / 0.5)', borderBottom: '1px solid hsl(var(--border))' }}>
              <p className="text-xs font-semibold">{customer.company_name}</p>
              <p className="text-xs" style={{ color: 'hsl(var(--muted-fg))' }}>
                C4P {data.is_standalone ? 'Custom' : ({'STANDARD':'Standard','ADVANCED':'Advanced','MTPRO':'MTPro'}[data.product_code] || data.product_code)}{(!data.is_standalone || (data.extra_db_servers?.length > 0)) ? ` · ${data.is_standalone ? customDbTypesDisplay : dbTypeLabel(data.db_type)}` : ''}{(!data.is_standalone || numUsersInt > 0) ? ` · ${numUsersInt} users` : ''} · {data.term_months} months · {data.currency}{data.region_name ? ` · ${data.region_name}` : ''}{isTenantDirect ? ' · Tenant Direct' : ''}
              </p>
              {isTenantDirect && data.tenant_name && (
                <p className="text-xs font-semibold mt-0.5" style={{ color: 'hsl(265 89% 45%)' }}>Tenant: {data.tenant_name}</p>
              )}
            </div>
          )}

          {/* Line items */}
          <table className="w-full" style={{ borderCollapse: 'collapse', fontSize: '11px' }}>
            <thead>
              <tr style={{ background: 'hsl(var(--muted))', borderBottom: '1px solid hsl(var(--border))' }}>
                <th className="text-right font-semibold px-3 py-2" style={{ color: 'hsl(var(--muted-fg))', width: '36px' }}>Qty</th>
                <th className="text-left font-semibold px-2 py-2" style={{ color: 'hsl(var(--muted-fg))' }}>Description</th>
                <th className="text-right font-semibold px-2 py-2" style={{ color: 'hsl(var(--muted-fg))', whiteSpace: 'nowrap' }}>Unit Price</th>
                <th className="text-right font-semibold px-3 py-2" style={{ color: 'hsl(var(--muted-fg))', whiteSpace: 'nowrap' }}>Monthly</th>
              </tr>
            </thead>
            <tbody>
              {allLines.map((l, i) => {
                // Live bespoke tenant price (attached by effectivePricingResult once the
                // admin has reached the Price Override step) takes priority over the
                // blanket margin formula used for non-Manual Tenant Direct quotes.
                const hasLiveTenant = isTenantDirect && l.tenant_unit_price != null;
                // For Tenant Direct: gross up ALL chargeable line items to show tenant-facing prices
                const nonUserFactor = isTenantDirect ? 1 + ((parseFloat(data.partner_margin_percent) || 0) / 100) : 1;
                const isTdBase = isTenantDirect && !hasLiveTenant && l.category === 'base';
                // PUPM line: add flat PUPM margin per user
                const tenantPupm = isTdBase ? (l.unit_price || 0) + (parseFloat(data.partner_margin_pupm) || 0) : null;
                // Non-user chargeable lines: multiply by non-user margin factor
                const isChargeableNonBase = isTenantDirect && !hasLiveTenant && !l.is_included && !isTdBase;

                const displayUnitPrice = hasLiveTenant
                  ? l.tenant_unit_price
                  : tenantPupm !== null
                    ? tenantPupm
                    : isChargeableNonBase && l.unit_price > 0
                      ? Math.round(l.unit_price * nonUserFactor * 100) / 100
                      : l.unit_price;
                const displayMonthly = hasLiveTenant
                  ? l.tenant_monthly_amount
                  : tenantPupm !== null
                    ? tenantPupm * (parseInt(data.num_users) || 0)
                    : isChargeableNonBase && l.monthly_amount > 0
                      ? Math.round(l.monthly_amount * nonUserFactor * 100) / 100
                      : l.monthly_amount;
                const displayOneoff = isChargeableNonBase && l.oneoff_amount > 0
                  ? Math.round(l.oneoff_amount * nonUserFactor * 100) / 100
                  : l.oneoff_amount;

                const qty = l.qty != null ? (l.qty === Math.floor(l.qty) ? Math.floor(l.qty) : l.qty) : '-';
                const unitPrice = displayUnitPrice != null && displayUnitPrice > 0
                  ? `${s}${fmt(displayUnitPrice)}`
                  : l.is_included ? '' : '-';
                const monthly = l.is_included
                  ? 'Included'
                  : displayMonthly > 0 ? `${s}${fmt(displayMonthly)}`
                  : displayOneoff > 0 ? `${s}${fmt(displayOneoff)}`
                  : '-';
                return (
                  <tr key={i} style={{ borderBottom: '1px solid hsl(var(--border))', opacity: l.is_included ? 0.65 : 1 }}>
                    <td className="text-right tabular-nums px-3 py-2 font-medium" style={{ color: 'hsl(var(--muted-fg))', verticalAlign: 'top' }}>{qty}</td>
                    <td className="px-2 py-2" style={{ verticalAlign: 'top' }}>
                      <p className={`font-semibold ${l.is_included ? 'text-xs' : ''}`} style={{ color: l.is_included ? 'hsl(var(--muted-fg))' : NAVY }}>{l.label}</p>
                      {l.sub_label && <p className="mt-0.5" style={{ color: 'hsl(var(--muted-fg))' }}>{l.sub_label}</p>}
                      {l.item_code && <p className="font-mono mt-0.5" style={{ color: 'hsl(var(--muted-fg))', fontSize: '10px', opacity: 0.7 }}>{l.item_code}</p>}
                    </td>
                    <td className="text-right tabular-nums px-2 py-2 text-xs" style={{ color: 'hsl(var(--muted-fg))', verticalAlign: 'top', whiteSpace: 'nowrap' }}>{unitPrice}</td>
                    <td className="text-right tabular-nums font-bold px-3 py-2" style={{ color: l.is_included ? 'hsl(var(--muted-fg))' : NAVY, verticalAlign: 'top', whiteSpace: 'nowrap', fontSize: l.is_included ? '11px' : '12px' }}>{monthly}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          {/* VAT section - GBP only */}
          {isGBP && estimatedTotal > 0 && (
            <table className="w-full" style={{ borderCollapse: 'collapse', fontSize: '11px', borderTop: '2px solid hsl(var(--border))' }}>
              <tbody>
                <tr style={{ borderBottom: '1px solid hsl(var(--border))' }}>
                  <td className="px-3 py-1.5" style={{ width: '36px' }} />
                  <td className="px-2 py-1.5" style={{ color: '#d97706' }}>VAT @ 20%</td>
                  <td className="px-2 py-1.5" />
                  <td className="text-right tabular-nums px-3 py-1.5" style={{ color: '#d97706', whiteSpace: 'nowrap' }}>{s}{fmt(vat)}</td>
                </tr>
                <tr style={{ background: 'hsl(218 52% 11%)', color: 'white' }}>
                  <td className="px-3 py-2" />
                  <td className="px-2 py-2 font-bold text-xs">Monthly inc. VAT</td>
                  <td className="px-2 py-2" />
                  <td className="text-right tabular-nums font-bold px-3 py-2" style={{ whiteSpace: 'nowrap' }}>{s}{fmt(incVat)}</td>
                </tr>
              </tbody>
            </table>
          )}

          {r.warnings?.length > 0 && (
            <div className="px-4 py-2 text-xs" style={{ background: 'hsl(var(--warning) / 0.08)', color: 'hsl(var(--warning))' }}>
              {r.warnings.map((w, i) => <p key={i}>{w}</p>)}
            </div>
          )}
        </>
      )}

    </div>
  );
}
